import { format } from 'date-fns';


const API_URL = process.env.NODE_ENV === 'development'
  ? '/api'  // En dev, proxy via React
  : (process.env.REACT_APP_API_URL
      ? `${process.env.REACT_APP_API_URL}/api`
      : '/api');
const normalizeDateParam = (value) => {
  if (!value) return '';
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === 'object') {
    const candidate = value.date_caisse || value.dateCaisse || value.date || value.value || value.target?.value || value.currentTarget?.value;
    return candidate && candidate !== value ? normalizeDateParam(candidate) : '';
  }

  const stringValue = String(value).trim();
  const isoMatch = stringValue.match(/^\d{4}-\d{2}-\d{2}/);
  return isoMatch ? isoMatch[0] : '';
};

const extractApiErrorMessage = (data, response) => {
  // Un refus de validation porte un message générique (« Erreur de validation
  // des données ») et le vrai motif dans `errors`. Afficher le générique
  // laissait l'utilisateur deviner quel champ corriger.
  const detailValidation = Array.isArray(data?.errors) && data.errors.length > 0
    && (!data?.message || /validation/i.test(data.message));
  if (detailValidation) {
    return data.errors
      .map((entry) => entry?.msg || entry?.message || entry?.error || String(entry))
      .filter(Boolean)
      .join(' ');
  }
  if (data?.message && data.message !== 'Une erreur est survenue') return data.message;
  if (typeof data?.error === 'string' && data.error) return data.error;
  if (typeof data?.details === 'string' && data.details) return data.details;
  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    return data.errors
      .map((entry) => entry?.msg || entry?.message || entry?.error || String(entry))
      .filter(Boolean)
      .join(' ');
  }
  if (data?.error?.message) return data.error.message;
  return response?.statusText || 'Une erreur est survenue';
};

const normalizePaymentModeName = (value = '') => {
  return String(value || '')
    .replace(/Esp(?:\u00e8|�|è|Ã¨|ÃÂ¨)ces/gi, 'Esp\u00e8ces')
    .replace(/Ch(?:\u00e8|�|è|Ã¨|ÃÂ¨)que/gi, 'Ch\u00e8que')
    .replace(/Non sp(?:\u00e9|�|é|Ã©)cifi(?:\u00e9|�|é|Ã©)/gi, 'Non sp\u00e9cifi\u00e9')
    .replace(/scolaris(?:\u00e9|�|é|Ã©)/gi, 'scolaris\u00e9');
};

const normalizePaymentModesPayload = (payload) => {
  if (Array.isArray(payload)) {
    return payload.map((mode) => ({
      ...mode,
      nom: normalizePaymentModeName(mode?.nom)
    }));
  }
  if (payload?.success && Array.isArray(payload.data)) {
    return {
      ...payload,
      data: normalizePaymentModesPayload(payload.data)
    };
  }
  return payload;
};

/**
 * Accès suspendu : on quitte l'application proprement.
 *
 * Un compte suspendu pendant qu'il travaille recevait des refus en série — des
 * verrous, des messages d'erreur, un écran à moitié vide — sans jamais être
 * renvoyé à la connexion. Le motif est gardé pour la page de connexion, qui
 * l'affiche.
 *
 * Exportée pour `SessionManager`, dont la vérification périodique passe par
 * `fetch` sans ce client.
 */
let sortieEnCours = false;
export const sortirPourAccesSuspendu = (message) => {
  if (sortieEnCours) return;
  sortieEnCours = true;
  try {
    sessionStorage.setItem('klasysMotifDeconnexion', message || 'Votre accès a été suspendu.');
    ['token', 'user', 'isLoggedInNew', 'userPermissions', 'redirectTo'].forEach((cle) => localStorage.removeItem(cle));
  } catch {
    /* stockage indisponible : la redirection suffit */
  }
  if (!window.location.pathname.startsWith('/connexion')) {
    window.location.href = '/connexion';
  }
};

const handleResponse = async (response) => {
  const data = await response.json();

  if (response.status === 401 && data?.code === 'ACCESS_SUSPENDED') {
    sortirPourAccesSuspendu(data.message);
  }

  if (!response.ok) {
    const message = extractApiErrorMessage(data, response);
    // Retourner l'objet pour les erreurs metier (400, 403, 409)
    if (response.status === 400 || response.status === 403 || response.status === 409) {
      return {
        success: false,
        message,
        error: message,
        code: data.code,
        status: response.status,
        errors: data.errors,
        receivedData: data.receivedData,
        data
      };
    }
    // Lancer une exception uniquement pour les vraies erreurs serveur (500+)
    const error = new Error(message);
    error.code = data.code;
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
};




// Dans api.js, modifiez getAuthHeaders
// ✅ Extraire le tenant depuis l'URL du navigateur
const getTenantFromHost = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'www';
  }
  const parts = hostname.split('.');
  if (parts.length >= 2 && parts[parts.length - 1] === 'localhost') {
    return parts[0];
  }
  if (parts.length >= 3) {
    return parts[0];
  }
  return null;
};

export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  const anneeScolaireId = localStorage.getItem('annee_scolaire_id');
  const headers = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const tenant = getTenantFromHost();
  if (tenant) {
    headers['X-Tenant'] = tenant;
  }
  if (anneeScolaireId) {
    headers['X-Annee-Scolaire-Id'] = anneeScolaireId;
  }
  return headers;
};

/**
 * Les en-têtes d'une requête de connexion.
 *
 * `getAuthHeaders()` joint à chaque appel l'année retenue dans
 * `localStorage` — celle de la session précédente. Sur une requête de
 * connexion, cette année-là ne veut plus rien dire : elle n'est réécrite
 * qu'une fois la connexion réussie. Une année clôturée entre-temps restait
 * donc collée aux tentatives suivantes, et le verrou de lecture seule
 * refusait le second facteur en la lisant (csap1, 03/10/2026).
 *
 * L'année choisie, elle, voyage dans le corps de `/api/auth/login` ; le
 * serveur la retient dans le défi et la retrouve seul au temps du code.
 * On n'envoie donc que l'école, et le jeton seulement là où il est exigé.
 */
export const getEntetesConnexion = ({ avecJeton = false } = {}) => {
  const headers = {
    'Content-Type': 'application/json',
  };

  const tenant = getTenantFromHost();
  if (tenant) {
    headers['X-Tenant'] = tenant;
  }

  if (avecJeton) {
    const token = localStorage.getItem('token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  return headers;
};

const getFormDataHeaders = () => {
  const token = localStorage.getItem('token');
  const anneeScolaireId = localStorage.getItem('annee_scolaire_id');
  const headers = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const tenant = getTenantFromHost();
  if (tenant) {
    headers['X-Tenant'] = tenant;
  }
  if (anneeScolaireId) {
    headers['X-Annee-Scolaire-Id'] = anneeScolaireId;
  }

  return headers;
};



const checkApiConnection = async () => {
  try {
    console.log("Testing API connection");
    const response = await fetch(`${API_URL}/test`);
    console.log("API Response status:", response.status);
    console.log("API Response ok:", response.ok);
    return response.ok;
  } catch (error) {
    console.error("API Connection error:", error);
    return false;
  }
};





export const api = {
  checkConnection: checkApiConnection,

  licence: {
    // Instantane de licence diffuse par le CIK. Lecture seule : la licence se
    // pilote depuis le CIK, jamais depuis KLASYS.
    getMonInstantane: async () => {
      try {
        const response = await fetch(`${API_URL}/licence/mon-instantane`, {
          method: 'GET',
          headers: getAuthHeaders(),
          credentials: 'include',
          cache: 'no-store'
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur chargement de la licence:', error);
        return { success: false, error: error.message };
      }
    },

    // Separee de getMonInstantane : celle-ci compte les eleves, les employes et
    // les comptes, et seul l'ecran de licence en a besoin.
    getPlafonds: async () => {
      try {
        const response = await fetch(`${API_URL}/licence/plafonds`, {
          method: 'GET',
          headers: getAuthHeaders(),
          credentials: 'include',
          cache: 'no-store'
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur chargement des plafonds:', error);
        return { success: false, error: error.message };
      }
    },

    // La version de KLASYS, publiee par le CIK. Elle n'appartient pas a la
    // licence de l'ecole : c'est celle du code deploye, la meme pour tous.
    getVersion: async () => {
      try {
        const response = await fetch(`${API_URL}/licence/version`, {
          method: 'GET',
          headers: getAuthHeaders(),
          credentials: 'include',
          cache: 'no-store'
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur chargement de la version:', error);
        return { success: false, error: error.message };
      }
    }
  },

  userPreferences: {
    getMe: async () => {
      try {
        const response = await fetch(`${API_URL}/user-preferences/me`, {
          method: 'GET',
          headers: getAuthHeaders(),
          credentials: 'include',
          cache: 'no-store'
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur chargement preferences utilisateur:', error);
        return { success: false, error: error.message };
      }
    },

    updateMe: async (preferences) => {
      try {
        const response = await fetch(`${API_URL}/user-preferences/me`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          credentials: 'include',
          cache: 'no-store',
          body: JSON.stringify({ preferences })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur sauvegarde preferences utilisateur:', error);
        return { success: false, error: error.message };
      }
    }
  },

  reinscription: {
    // RÃÂ©cupÃÂ©ration des ÃÂ©lÃÂ¨ves ÃÂ©ligibles
    getEligibleStudents: async () => {
      try {
        const response = await fetch(`${API_URL}/reinscription/eligible-students`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des ÃÂ©lÃÂ¨ves ÃÂ©ligibles:', error);
        return {
          success: false,
          error: 'Erreur lors du chargement des ÃÂ©lÃÂ¨ves ÃÂ©ligibles'
        };
      }
    },

    // RÃÂ©inscription d'un ÃÂ©lÃÂ¨ve
    reinscrire: async (data) => {
      try {
        const response = await fetch(`${API_URL}/reinscription/${data.eleveId}`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            annee_scolaire_id: data.annee_scolaire_id,
            nouvelle_classe_id: data.nouvelle_classe_id
          })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur lors de la rÃÂ©inscription:', error);
        return {
          success: false,
          error: 'Erreur lors de la rÃÂ©inscription'
        };
      }
    },

    // Obtention des statistiques
    getStats: async () => {
      try {
        const response = await fetch(`${API_URL}/reinscription/stats`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        return {
          success: false,
          error: 'Erreur lors du chargement des statistiques'
        };
      }
    },

    // VÃÂ©rification du statut
    getStatus: async (eleveId) => {
      try {
        const response = await fetch(`${API_URL}/reinscription/status/${eleveId}`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        return {
          success: false,
          error: 'Erreur lors du chargement du statut'
        };
      }
    },

    // RÃÂ©cupÃÂ©ration des approbations en attente
    getPendingApprovals: async (annee_scolaire_id) => {
      try {
        const response = await fetch(`${API_URL}/reinscription/pending?annee_scolaire_id=${annee_scolaire_id}`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des approbations en attente:', error);
        return {
          success: false,
          error: 'Erreur lors du chargement des approbations'
        };
      }
    },

    // Approbation d'une rÃÂ©inscription
    approve: async (id, data) => {
      try {
        const response = await fetch(`${API_URL}/reinscription/approve/${id}`, {
          method: 'PUT',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur lors de l\'approbation:', error);
        return {
          success: false,
          error: 'Erreur lors de l\'approbation'
        };
      }
    },

    getYearStats: async (annee_scolaire_id) => {
      try {
        const response = await fetch(`${API_URL}/reinscription/stats?annee_scolaire_id=${annee_scolaire_id}`, {
          headers: getAuthHeaders()
        });
        const data = await handleResponse(response);

        // Adapter les noms des champs pour correspondre au frontend
        return {
          success: true,
          data: {
            total: data.data.total,
            pending: data.data.pending,
            approved: data.data.approved,
            rejected: data.data.rejected// Ajoutez cette propriÃÂ©tÃÂ© si elle n'existe pas dans la rÃÂ©ponse
          }
        };
      } catch (error) {
        console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des stats:', error);
        return {
          success: false,
          error: 'Erreur lors du chargement des statistiques'
        };
      }
    },

    // RÃÂ©cupÃÂ©ration de l'historique
    getHistorique: async (annee_scolaire_id) => {
      try {
        const response = await fetch(`${API_URL}/reinscription/historique?annee_scolaire_id=${annee_scolaire_id}`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur lors de la rÃÂ©cupÃÂ©ration de l\'historique:', error);
        return {
          success: false,
          error: 'Erreur lors du chargement de l\'historique'
        };
      }
    }
},


//affichage du role et nom sur la barre superieur
auth: {
  login: async (credentials) => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials)
      });

      const data = await handleResponse(response);

      if (data.success && data.user && data.token) { // VÃÂ©rifier que le token est prÃÂ©sent
        // Stocker le token et l'utilisateur sÃÂ©parÃÂ©ment
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('isLoggedInNew', 'true');
      }

      return data;
    } catch (error) {
      console.error('Erreur de connexion:', error);
      return {
        success: false,
        error: error.message || 'Erreur de connexion'
      };
    }
  },

  changeFirstLoginPassword: async (newPassword) => {
    try {
      const response = await fetch(`${API_URL}/auth/first-login-change-password`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ nouveau_mot_de_passe: newPassword })
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors du changement du mot de passe:', error);
      return {
        success: false,
        error: error.message || 'Erreur lors du changement du mot de passe'
      };
    }
  },


  getMyPermissions: async () => {
    try {
      console.log('Ã°ÂÂÂ [API] Appel getMyPermissions');
      console.log('Ã°ÂÂÂ [API] URL:', `${API_URL}/auth/my-permissions`);

      const headers = getAuthHeaders();
      console.log('Ã°ÂÂÂ¬ [API] Headers:', headers);

      const response = await fetch(`${API_URL}/auth/my-permissions`, {
        headers: headers,
      });

      console.log('Ã°ÂÂÂ¡ [API] RÃÂ©ponse brute:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      const result = await handleResponse(response);
      console.log('Ã°ÂÂÂ¦ [API] RÃÂ©sultat final:', result);

      return result;
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur dans getMyPermissions:', error);
      return { success: false, error: error.message };
    }
  }

},

  // Services pour les annÃÂ©es scolaires

  anneesScolaires: {
    getAll: async () => {
        try {
            const response = await fetch(`${API_URL}/annees-scolaires`, {
                headers: getAuthHeaders()
            });
            return handleResponse(response);
        } catch (error) {
            console.error('Erreur getAll:', error);
            return {
                success: false,
                data: [],
                error: error.message
            };
        }
    },

    /**
     * L'annee active AVEC ses dates, lue en base.
     *
     * `getActive()` court-circuite l'API des qu'un `annee_scolaire_id` se
     * trouve dans le localStorage — c'est-a-dire toujours apres connexion — et
     * rend un objet fabrique de toutes pieces : id, nom, deux drapeaux. Ni
     * `date_debut`, ni `date_fin`.
     *
     * Ce raccourci sert le contexte « annee consultee » et trente et un ecrans
     * s'en accommodent : ils n'ont besoin que de l'identifiant. Mais la
     * generation annuelle des emplois du temps, elle, a besoin des DATES, et
     * elle echouait sur « Les dates de l'annee scolaire ne sont pas definies »
     * alors qu'elles sont NOT NULL en base et parfaitement renseignees.
     *
     * On ne touche donc pas a `getActive`. On ajoute la porte qui manquait.
     */
    getActiveAvecDates: async () => {
        try {
            const response = await fetch(`${API_URL}/annees-scolaires/actives`, {
                headers: getAuthHeaders()
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Erreur getActiveAvecDates:', error);
            return { success: false, data: null, message: error.message };
        }
    },

    /**
     * L'ouverture exceptionnelle en cours sur une annee cloturee, posee depuis
     * le CIK : `{ perimetre, libelle, motif, expire_le }`, ou `null`. Lue par le
     * bandeau de l'administration, qui ne dit plus « lecture seule » pendant
     * qu'elle court.
     */
    getOuvertureExceptionnelle: async (anneeId) => {
        try {
            const response = await fetch(`${API_URL}/annees-scolaires/${encodeURIComponent(anneeId)}/ouverture-exceptionnelle`, {
                headers: getAuthHeaders()
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Erreur getOuvertureExceptionnelle:', error);
            return { success: false, ouverture: null, message: error.message };
        }
    },

    getActive: async () => {
        try {
            const selectedYearId = localStorage.getItem('annee_scolaire_id');
            if (selectedYearId) {
                const isReadOnly = localStorage.getItem('annee_scolaire_read_only') === 'true';
                return {
                    success: true,
                    data: {
                        id: Number(selectedYearId),
                        nom: localStorage.getItem('annee_scolaire_nom') || '',
                        est_active: isReadOnly ? 0 : 1,
                        est_cloturee: isReadOnly ? 1 : 0,
                        is_selected_context: true
                    }
                };
            }

            const response = await fetch(`${API_URL}/annees-scolaires/actives`, {
                headers: getAuthHeaders()  // Ajout ici
            });
            const result = await handleResponse(response);
            return {
                success: result.success,
                data: result.data
            };
        } catch (error) {
            console.error('Erreur getActive:', error);
            return {
                success: false,
                data: null,
                error: error.message
            };
        }
    },

    getLoginOptions: async () => {
        try {
            const response = await fetch(`${API_URL}/annees-scolaires/login-options`, {
                headers: getAuthHeaders()
            });
            return handleResponse(response);
        } catch (error) {
            console.error('Erreur getLoginOptions:', error);
            return {
                success: false,
                data: [],
                error: error.message,
                // Conserver le code et le statut : sans eux, l'écran de
                // connexion ne peut pas distinguer une école qui n'existe pas
                // d'une panne, et affiche la même erreur générique pour les
                // deux — ce qui fait passer une adresse erronée pour un bug.
                code: error.code,
                status: error.status
            };
        }
    },


    getVerifications: async (anneeId) => {
        try {
            const response = await fetch(`${API_URL}/annees-scolaires/${anneeId}/verifications`, {
                headers: getAuthHeaders()  // Ajout ici
            });
            const data = await handleResponse(response);
            return data;
        } catch (error) {
            console.error('Erreur getVerifications:', error);
            return { success: false, message: error.message };
        }
    },

    getDiagnosticCloture: async () => {
        try {
            const response = await fetch(`${API_URL}/annees-scolaires/diagnostic-cloture`, {
                headers: getAuthHeaders()
            });
            return handleResponse(response);
        } catch (error) {
            console.error('Erreur getDiagnosticCloture:', error);
            return { success: false, message: error.message, data: null };
        }
    },

    getDiagnosticClotureGroupe: async () => {
        try {
            const response = await fetch(`${API_URL}/annees-scolaires/diagnostic-cloture/groupe`, {
                headers: getAuthHeaders()
            });
            return handleResponse(response);
        } catch (error) {
            console.error('Erreur getDiagnosticClotureGroupe:', error);
            return { success: false, message: error.message, data: null };
        }
    },

    creerAutomatiquementSuivante: async () => {
        try {
            const response = await fetch(`${API_URL}/annees-scolaires/creer-auto`, {
                method: 'POST',
                headers: getAuthHeaders()
            });
            return handleResponse(response);
        } catch (error) {
            console.error('Erreur creerAutomatiquementSuivante:', error);
            return { success: false, message: error.message, data: null };
        }
    },

    preparerStructurePedagogique: async () => {
        try {
            const response = await fetch(`${API_URL}/annees-scolaires/preparer-structure-pedagogique`, {
                method: 'POST',
                headers: getAuthHeaders()
            });
            return handleResponse(response);
        } catch (error) {
            console.error('Erreur preparerStructurePedagogique:', error);
            return { success: false, message: error.message, data: null };
        }
    },

    preparerPersonnel: async () => {
        try {
            const response = await fetch(`${API_URL}/annees-scolaires/preparer-personnel`, {
                method: 'POST',
                headers: getAuthHeaders()
            });
            return handleResponse(response);
        } catch (error) {
            console.error('Erreur preparerPersonnel:', error);
            return { success: false, message: error.message, data: null };
        }
    },

    getPersonnelReconduction: async () => {
        try {
            const response = await fetch(`${API_URL}/annees-scolaires/cloture/personnel`, {
                headers: getAuthHeaders()
            });
            return handleResponse(response);
        } catch (error) {
            console.error('Erreur getPersonnelReconduction:', error);
            return { success: false, message: error.message, data: null };
        }
    },

    modifierDecisionsPersonnel: async (decisions) => {
        try {
            const response = await fetch(`${API_URL}/annees-scolaires/cloture/personnel`, {
                method: 'PUT',
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ decisions })
            });
            return handleResponse(response);
        } catch (error) {
            console.error('Erreur modifierDecisionsPersonnel:', error);
            return { success: false, message: error.message, data: null };
        }
    },

    preparerCalendrierFinancier: async () => {
        try {
            const response = await fetch(`${API_URL}/annees-scolaires/preparer-calendrier-financier`, {
                method: 'POST',
                headers: getAuthHeaders()
            });
            return handleResponse(response);
        } catch (error) {
            console.error('Erreur preparerCalendrierFinancier:', error);
            return { success: false, message: error.message, data: null };
        }
    },

    getCalendrierCloture: async () => {
        try {
            const response = await fetch(`${API_URL}/annees-scolaires/cloture/calendrier`, {
                headers: getAuthHeaders()
            });
            return handleResponse(response);
        } catch (error) {
            console.error('Erreur getCalendrierCloture:', error);
            return { success: false, message: error.message, data: null };
        }
    },

    getRecouvrementCloture: async (anneeId = null) => {
        try {
            const params = anneeId ? `?annee_id=${encodeURIComponent(anneeId)}` : '';
            const response = await fetch(`${API_URL}/annees-scolaires/cloture/recouvrement${params}`, {
                headers: getAuthHeaders()
            });
            return handleResponse(response);
        } catch (error) {
            console.error('Erreur getRecouvrementCloture:', error);
            return { success: false, message: error.message, data: null };
        }
    },

    // L'onglet Recouvrement n'existe que s'il y a une année clôturée.
    getRecouvrementDisponible: async () => {
        try {
            const response = await fetch(`${API_URL}/annees-scolaires/recouvrement/disponible`, {
                headers: getAuthHeaders()
            });
            return handleResponse(response);
        } catch (error) {
            console.error('Erreur getRecouvrementDisponible:', error);
            return { success: false, message: error.message, data: null };
        }
    },

    getRecouvrementDerniereAnneeCloturee: async () => {
        try {
            const response = await fetch(`${API_URL}/annees-scolaires/recouvrement/derniere-annee-cloturee`, {
                headers: getAuthHeaders()
            });
            return handleResponse(response);
        } catch (error) {
            console.error('Erreur getRecouvrementDerniereAnneeCloturee:', error);
            return { success: false, message: error.message, data: null };
        }
    },

    sauvegarderCalendrierCloture: async (mois) => {
        try {
            const response = await fetch(`${API_URL}/annees-scolaires/cloture/calendrier`, {
                method: 'PUT',
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ mois })
            });
            return handleResponse(response);
        } catch (error) {
            console.error('Erreur sauvegarderCalendrierCloture:', error);
            return { success: false, message: error.message, data: null };
        }
    },

    finaliserCloture: async () => {
        try {
            const response = await fetch(`${API_URL}/annees-scolaires/cloture/finaliser`, {
                method: 'POST',
                headers: getAuthHeaders()
            });
            return handleResponse(response);
        } catch (error) {
            console.error('Erreur finaliserCloture:', error);
            return { success: false, message: error.message, data: null };
        }
    },

    getLogs: async (anneeId) => {
        try {
            const response = await fetch(`${API_URL}/annees-scolaires/${anneeId}/logs`, {
                headers: getAuthHeaders()  // Ajout ici
            });
            return handleResponse(response);
        } catch (error) {
            return { success: false, message: error.message };
        }
    },


    update: async (id, data) => {
        try {
            const response = await fetch(`${API_URL}/annees-scolaires/${id}`, {
                method: 'PUT',
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });
            return handleResponse(response);
        } catch (error) {
            console.error('Erreur update annÃÂ©e scolaire:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

},





  // Les services « trainings » (certifications comprises) et « documents » ont
  // été retirés le 28/09/2026. Leurs écrans n'étaient plus montés, les routes
  // /trainings et /certifications n'existaient plus côté serveur, et leurs
  // tables sont vides dans toutes les écoles. Les tables, elles, restent en
  // place : les vider demanderait une migration pour ne rien gagner.
  // À ne pas confondre avec les documents des employés et des élèves, qui
  // vivent dans `employes.documents` et `eleves.documents` et sont bien utilisés.

    // Endpoints EmployÃÂ©s
employes: {
  // 🟢 Créer un employé
  create: async (employeData) => {
    try {
      console.log('📤 Données reçues dans l’API:', employeData);

      // Convertir en FormData si besoin
      if (!(employeData instanceof FormData)) {
        const formData = new FormData();
        const sanitizedData = JSON.parse(
          JSON.stringify(employeData, (key, value) => (value === undefined ? null : value))
        );
        formData.append('data', JSON.stringify(sanitizedData));
        employeData = formData;
      }

      // Ne pas fixer Content-Type pour FormData
      const headers = getAuthHeaders();
      delete headers['Content-Type'];

      const response = await fetch(`${API_URL}/employes`, {
        method: 'POST',
        headers,
        body: employeData
      });

      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur création employé:', error);
      throw error;
    }
  },

  // 🟢 Récupérer tous les employés
  getAll: async () => {
    const response = await fetch(`${API_URL}/employes`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },





  getRhStats: async () => {
    const response = await fetch(`${API_URL}/employes/rh/statistiques`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  // 🟢 Récupérer un employé spécifique
  getOne: async (id) => {
    const response = await fetch(`${API_URL}/employes/${id}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },
  getMyProfileChangeContext: async () => {
    try {
      const response = await fetch(`${API_URL}/employes/me/profile-change/context`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur contexte modification profil:', error);
      return { success: false, error: error.message };
    }
  },

  createMyProfileChangeRequest: async (profileData) => {
    try {
      let body = profileData;
      let headers = getAuthHeaders();

      if (profileData instanceof FormData) {
        headers = getFormDataHeaders();
      } else {
        headers = {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        };
        body = JSON.stringify(profileData);
      }

      const response = await fetch(`${API_URL}/employes/me/profile-change/request`, {
        method: 'POST',
        headers,
        body
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur demande modification profil:', error);
      return { success: false, error: error.message, message: error.message };
    }
  },

  // Suspendre ou retablir l'acces d'un employe. Distinct du statut d'emploi :
  // `statut` decrit l'emploi et sert aux affectations, a la paie et aux
  // listings ; l'acces a sa propre colonne.
  setAccesSuspendu: async (employeeId, suspendu, motif = null) => {
    try {
      const response = await fetch(`${API_URL}/employes/${employeeId}/acces`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ suspendu, motif })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur suspension acces:', error);
      return { success: false, message: error.message };
    }
  },

  getProfileChangeHistory: async (employeeId, limit = 50) => {
    try {
      const response = await fetch(`${API_URL}/employes/${employeeId}/profile-change-history?limit=${limit}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur historique modification profil:', error);
      return { success: false, error: error.message, data: [] };
    }
  },

  // 🟢 Mettre à jour un employé
  update: async (id, employeData) => {
    try {
      console.log('📤 Données reçues dans update API:', employeData);

      const sanitizeDeep = (obj) => {
        if (obj === null || obj === undefined) return null;
        if (typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(sanitizeDeep);
        return Object.fromEntries(
          Object.entries(obj).map(([key, value]) => [key, sanitizeDeep(value)])
        );
      };

      const sanitizedData = sanitizeDeep(employeData);
      const dataToSend = JSON.stringify(sanitizedData);

      const response = await fetch(`${API_URL}/employes/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders() // ✅ token inclus ici
        },
        body: dataToSend
      });

      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur update employé:', error);
      throw error;
    }
  },

  // 🟢 Supprimer un employé
  delete: async (id) => {
    try {
      const response = await fetch(`${API_URL}/employes/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur suppression employé:', error);
      return { success: false, error: error.message };
    }
  },

  // 🟢 Générer un contrat d’employé
  generateEmployeeContract: async (data) => {
    try {
      const response = await fetch(`${API_URL}/employes/generate-contract`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur génération contrat:', error);
      return { success: false, message: error.message };
    }
  },




 // 🟢 Upload photo employé
uploadPhoto: async (employeId, formData) => {
  try {
    // ✅ CRITIQUE : Supprimer Content-Type pour laisser le navigateur le définir automatiquement
    const headers = getAuthHeaders();
    delete headers['Content-Type'];
    delete headers['Accept'];

    const response = await fetch(`${API_URL}/employes/${employeId}/photo`, {
      method: 'POST',
      headers: headers, // Seulement Authorization
      body: formData
    });
    return handleResponse(response);
  } catch (error) {
    console.error('❌ Erreur upload photo:', error);
    return { success: false, error: error.message };
  }
},



  // 🟢 Supprimer photo employé
  deletePhoto: async (employeId) => {
    try {
      const response = await fetch(`${API_URL}/employes/${employeId}/photo`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur suppression photo:', error);
      return { success: false, error: error.message };
    }
  },



  // 🟢 Upload document employé
uploadDocument: async (employeId, formData, type) => {
  try {
    // ✅ Supprimer Content-Type pour multipart
    const headers = getAuthHeaders();
    delete headers['Content-Type'];
    delete headers['Accept'];

    const response = await fetch(`${API_URL}/employes/${employeId}/documents/${type}`, {
      method: 'POST',
      headers: headers,
      body: formData
    });
    return handleResponse(response);
  } catch (error) {
    console.error('❌ Erreur upload document:', error);
    return { success: false, error: error.message };
  }
},




  // 🟢 Supprimer document employé
  deleteDocument: async (employeId, documentId) => {
    try {
      const response = await fetch(`${API_URL}/employes/${employeId}/documents/${documentId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur suppression document:', error);
      return { success: false, error: error.message };
    }
  },

  // 🟢 Récupérer rôles, enseignants, etc.
  getByRole: async (role) => {
    const response = await fetch(`${API_URL}/employes/role/${role}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getRoles: async () => {
    const response = await fetch(`${API_URL}/employes/roles`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // ⚠️ LES DEUX LECTURES DONT LE FORMULAIRE « NOUVEL EMPLOYE » A BESOIN,
  // prises COTE EMPLOYES et non cote configuration.
  //
  // Il appelait `groupesSecurite.getAll()` et `roles.getByGroupeId()`, dont les
  // routes sont gardees par la porte du module de CONFIGURATION. Un employe du
  // groupe « Administration Generale » — directeur, secretaire, comptable —
  // recevait donc un 403 des l'ouverture du formulaire, et il aurait fallu lui
  // ouvrir tout le module de configuration pour creer un employe.
  //
  // Ces deux routes-ci ne rendent que des NOMS, et sont gardees par la seule
  // authentification. Les methodes `groupesSecurite.getAll()` et
  // `roles.getByGroupeId()` restent en place, inchangees, pour l'ecran
  // « Roles et permissions » qui a legitimement besoin de la porte.
  getGroupesSecurite: async () => {
    const response = await fetch(`${API_URL}/employes/groupes-securite`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getRolesParGroupe: async (groupeId) => {
    const response = await fetch(`${API_URL}/employes/roles-par-groupe/${groupeId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // Les enseignants seuls : groupe « Enseignant » et statut actif. La route
  // existait, aucune methode ne l'appelait — d'ou les ecrans qui proposaient
  // tous les employes.
  getEnseignants: async () => {
    try {
      const response = await fetch(`${API_URL}/employes/enseignants`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur getEnseignants:', error);
      return { success: false, error: error.message };
    }
  },

  getEnseignantsWithAffectations: async (annee_scolaire_id) => {
    const url = `${API_URL}/employes/enseignants-with-affectations${annee_scolaire_id ? `?annee_scolaire_id=${annee_scolaire_id}` : ''}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    return handleResponse(response);
  },

  // 🟢 Changer le rôle d’un enseignant
  changerRoleEnseignant: async (enseignant_id, nouveau_role_id) => {
    const response = await fetch(`${API_URL}/employes/${enseignant_id}/changer-role`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({ nouveau_role_id })
    });
    return handleResponse(response);
  },

  // 🟢 Récupérer tableau de bord enseignant
  getTableauDeBordEnseignant: async (enseignant_id, annee_scolaire_id) => {
    const response = await fetch(`${API_URL}/employes/${enseignant_id}/tableau-de-bord?annee_scolaire_id=${annee_scolaire_id}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },


  // 🟢 Récupérer tous les enseignants avec leurs affectations détaillées
getEnseignantsAvecAffectationsDetaillees: async (annee_scolaire_id) => {
  try {
    const response = await fetch(
      `${API_URL}/employes/enseignants-avec-affectations-detaillees?annee_scolaire_id=${annee_scolaire_id}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  } catch (error) {
    console.error('❌ Erreur getEnseignantsAvecAffectationsDetaillees:', error);
    return { success: false, error: error.message };
  }
},



// ============================================================================
// GESTION DES ACCÈS ET DEMANDES
// ============================================================================

getAccessData: async (employeeId, connectionHistoryPage = 1) => {
  try {
    let url = `${API_URL}/employes/${employeeId}/access-data`;
    if (connectionHistoryPage > 1) {
      url += `?connectionHistoryPage=${connectionHistoryPage}`;
    }

    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    return handleResponse(response);
  } catch (error) {
    console.error('❌ Erreur getAccessData:', error);
    return {
      success: false,
      error: error.message
    };
  }
},

createAccessRequest: async (requestData) => {
  try {
    const response = await fetch(`${API_URL}/employes/access-request`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    return handleResponse(response);
  } catch (error) {
    console.error('❌ Erreur createAccessRequest:', error);
    return {
      success: false,
      error: error.message
    };
  }
},

getConnectionHistory: async (employeeId) => {
  try {
    const response = await fetch(`${API_URL}/employes/${employeeId}/connection-history`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    return handleResponse(response);
  } catch (error) {
    console.error('❌ Erreur getConnectionHistory:', error);
    return {
      success: false,
      error: error.message
    };
  }
},

markRejectionAsRead: async (employeeId) => {
  try {
    const response = await fetch(`${API_URL}/support/mark-rejection-read/${employeeId}`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });

    return handleResponse(response);
  } catch (error) {
    console.error('❌ Erreur markRejectionAsRead:', error);
    return {
      success: false,
      error: error.message
    };
  }
},

downloadDocument: async (employeId, documentId) => {
  try {
    const response = await fetch(`${API_URL}/employes/${employeId}/documents/${documentId}/download`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors du téléchargement');
    }

    const disposition = response.headers.get('Content-Disposition');
    let filename = 'document.pdf';
    if (disposition && disposition.includes('filename=')) {
      filename = disposition.split('filename=')[1].replace(/"/g, '');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('❌ Erreur downloadDocument:', error);
    return { success: false, error: error.message };
  }
},

// ============================================================================
// GESTION DES ENSEIGNANTS - COMPLÉMENTS
// ============================================================================

getEnseignantsPrincipaux: async (annee_scolaire_id = null) => {
  try {
    let url = `${API_URL}/employes/enseignants-principaux`;
    if (annee_scolaire_id) {
      url += `?annee_scolaire_id=${annee_scolaire_id}`;
    }

    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  } catch (error) {
    console.error('❌ Erreur getEnseignantsPrincipaux:', error);
    return {
      success: false,
      error: error.message
    };
  }
},

getEnseignantsMatieres: async (annee_scolaire_id = null) => {
  try {
    let url = `${API_URL}/employes/enseignants-matieres`;
    if (annee_scolaire_id) {
      url += `?annee_scolaire_id=${annee_scolaire_id}`;
    }

    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  } catch (error) {
    console.error('❌ Erreur getEnseignantsMatieres:', error);
    return {
      success: false,
      error: error.message
    };
  }
},

// ============================================================================
// CONFIGURATION TYPE DE CONTRAT ET BADGES
// ============================================================================

setContractType: async (employeId, contractData) => {
  try {
    const response = await fetch(`${API_URL}/employes/${employeId}/contract-type`, {
      method: 'PUT',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(contractData)
    });
    return handleResponse(response);
  } catch (error) {
    console.error('❌ Erreur setContractType:', error);
    return { success: false, error: error.message };
  }
},

getEmployeeHourlyRates: async (employeId) => {
  try {
    const response = await fetch(`${API_URL}/employes/${employeId}/hourly-rates`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  } catch (error) {
    console.error('❌ Erreur getEmployeeHourlyRates:', error);
    return { success: false, error: error.message };
  }
},

setEmployeeHourlyRates: async (employeId, ratesData) => {
  try {
    const response = await fetch(`${API_URL}/employes/${employeId}/hourly-rates`, {
      method: 'PUT',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(ratesData)
    });
    return handleResponse(response);
  } catch (error) {
    console.error('❌ Erreur setEmployeeHourlyRates:', error);
    return { success: false, error: error.message };
  }
},

calculateAttendanceSalary: async (employeId, monthId) => {
  try {
    const response = await fetch(`${API_URL}/employes/${employeId}/attendance-salary/${monthId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  } catch (error) {
    console.error('❌ Erreur calculateAttendanceSalary:', error);
    return { success: false, error: error.message };
  }
},

getClassesAndSubjects: async () => {
  try {
    const response = await fetch(`${API_URL}/employes/classes-and-subjects`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  } catch (error) {
    console.error('❌ Erreur getClassesAndSubjects:', error);
    return { success: false, error: error.message };
  }
},





 // 🆕 ENFANTS À CHARGE
  getEnfantsACharge: async (employeId) => {
    try {
      const response = await fetch(`${API_URL}/employes/${employeId}/enfants-charge`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur getEnfantsACharge:', error);
      return { success: false, error: error.message };
    }
  },

  searchElevesForLiaison: async (searchTerm, anneeScolaireId) => {
    try {
      const response = await fetch(
        `${API_URL}/employes/search-eleves?q=${encodeURIComponent(searchTerm)}&annee_scolaire_id=${anneeScolaireId}`,
        { headers: getAuthHeaders() }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur searchElevesForLiaison:', error);
      return { success: false, error: error.message };
    }
  },

  lierEnfantsACharge: async (employeId, eleveIds) => {
    try {
      const response = await fetch(`${API_URL}/employes/${employeId}/enfants-charge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ eleve_ids: eleveIds })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur lierEnfantsACharge:', error);
      return { success: false, error: error.message };
    }
  },

  toggleDeductionEnfant: async (employeId, liaisonId) => {
    try {
      const response = await fetch(
        `${API_URL}/employes/${employeId}/enfants-charge/${liaisonId}/toggle`,
        {
          method: 'PUT',
          headers: getAuthHeaders()
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur toggleDeductionEnfant:', error);
      return { success: false, error: error.message };
    }
  },

  delierEnfantACharge: async (employeId, liaisonId) => {
    try {
      const response = await fetch(
        `${API_URL}/employes/${employeId}/enfants-charge/${liaisonId}`,
        {
          method: 'DELETE',
          headers: getAuthHeaders()
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur delierEnfantACharge:', error);
      return { success: false, error: error.message };
    }
  },

  getTotalRetenuesEnfants: async (employeId) => {
    try {
      const response = await fetch(
        `${API_URL}/employes/${employeId}/enfants-charge/total-retenues`,
        { headers: getAuthHeaders() }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur getTotalRetenuesEnfants:', error);
      return { success: false, error: error.message };
    }
  },


   exportPDF: async (params) => {
    const headers = getAuthHeaders();
    const response = await fetch(`${API_URL}/employes/export/pdf?${params.toString()}`, {
      method: 'GET',
      headers: headers
    });
    return response;
  },


  // 🆕 VÉRIFICATION BADGE RFID
verifyBadge: async (badge_number) => {
  try {
    const response = await fetch(`${API_URL}/employes/verify-badge`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({ badge_number })
    });
    return handleResponse(response);
  } catch (error) {
    console.error('❌ Erreur verifyBadge:', error);
    return { success: false, error: error.message };
  }
},

// ============================================================================
// LISTE DES BADGES RFID ATTRIBUÉS (lecture seule)
// ============================================================================
//
// Qui porte un badge, sans ouvrir trente fiches. Deux colonnes font le badge —
// `rfid_uid` ET `numero_badge` — et le serveur les renvoie SÉPARÉMENT : ne
// jamais les fusionner côté client, un employé qui n'a que l'une des deux
// passerait pour « sans badge » alors qu'il pointe tous les matins.
//
// Les deux appels sont gardés par la licence `cap.rfid` côté serveur. Un 403
// remonte donc ici si l'école n'a pas les badges : l'appelant l'affiche, il ne
// le confond pas avec « aucun badge attribué ».

getBadgesRfid: async () => {
  try {
    const response = await fetch(`${API_URL}/employes/badges`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  } catch (error) {
    console.error('❌ Erreur getBadgesRfid:', error);
    return { success: false, error: error.message };
  }
},

downloadBadgesRfidPDF: async () => {
  try {
    const response = await fetch(`${API_URL}/employes/badges/pdf`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Erreur génération PDF');

    // Le nom de fichier est celui que le serveur a daté : il le calcule en SQL,
    // là où le fuseau de l'école fait foi. Un nom recalculé ici porterait la
    // date du navigateur, et la veille une partie de la soirée.
    const disposition = response.headers.get('Content-Disposition');
    let filename = 'Badges_RFID.pdf';
    if (disposition && disposition.includes('filename=')) {
      filename = disposition.split('filename=')[1].replace(/"/g, '').trim() || filename;
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('❌ Erreur downloadBadgesRfidPDF:', error);
    return { success: false, error: error.message };
  }
}




},


parametresSalaires: {
  getByAnnee: async (anneeId) => {
    try {
      const response = await fetch(`${API_URL}/parametres-salaires/${anneeId}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur get parametres salaires:', error);
      return { success: false, error: error.message };
    }
  },

  updateByAnnee: async (anneeId, data) => {
    try {
      const response = await fetch(`${API_URL}/parametres-salaires/${anneeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur update parametres salaires:', error);
      return { success: false, error: error.message };
    }
  }
},

etatSalaires: {
  getEtat: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      const suffix = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await fetch(`${API_URL}/etat-salaires${suffix}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur etat salaires:', error);
      return { success: false, error: error.message };
    }
  },

  validerEtat: async (params = {}) => {
    try {
      const response = await fetch(`${API_URL}/etat-salaires/valider`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(params)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur validation etat salaires:', error);
      return { success: false, error: error.message };
    }
  },

  getMoisFiges: async (annee_scolaire_id) => {
    try {
      const response = await fetch(
        `${API_URL}/etat-salaires/mois-figes?annee_scolaire_id=${annee_scolaire_id}`,
        { headers: getAuthHeaders() }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lecture mois figes:', error);
      return { success: false, error: error.message };
    }
  },

  defigerMois: async (params = {}) => {
    try {
      const response = await fetch(`${API_URL}/etat-salaires/defiger`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(params)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur defigeage mois:', error);
      return { success: false, error: error.message };
    }
  }
},

cantine: {
  _query: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    return queryParams.toString() ? `?${queryParams.toString()}` : '';
  },

  _send: async (path, method, body = {}) => {
    try {
      const response = await fetch(`${API_URL}/cantine${path}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(body)
      });
      return handleResponse(response);
    } catch (error) {
      console.error(`Erreur cantine ${method} ${path}:`, error);
      return { success: false, error: error.message };
    }
  },

  _get: async (path, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      const suffix = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await fetch(`${API_URL}/cantine${path}${suffix}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error(`Erreur cantine GET ${path}:`, error);
      return { success: false, error: error.message };
    }
  },

  // Paramètres
  getParametres: (params = {}) => api.cantine._get('/parametres', params),
  saveParametres: (body = {}) => api.cantine._send('/parametres', 'PUT', body),

  // Inscriptions
  getInscriptions: (params = {}) => api.cantine._get('/inscriptions', params),
  saveInscription: (body = {}) => api.cantine._send('/inscriptions', 'POST', body),
  deleteInscription: (id, body = {}) => api.cantine._send(`/inscriptions/${id}`, 'DELETE', body),

  // Restrictions alimentaires
  getRestrictions: (params = {}) => api.cantine._get('/restrictions', params),
  saveRestriction: (body = {}) => api.cantine._send('/restrictions', 'POST', body),
  deleteRestriction: (id, body = {}) => api.cantine._send(`/restrictions/${id}`, 'DELETE', body),

  // Menus
  getMenus: (params = {}) => api.cantine._get('/menus', params),
  saveMenu: (body = {}) => api.cantine._send('/menus', 'POST', body),
  deleteMenu: (id, body = {}) => api.cantine._send(`/menus/${id}`, 'DELETE', body),

  // Tableau de bord
  getTableauDeBord: (params = {}) => api.cantine._get('/tableau-de-bord', params),

  // Cas limites : creances et synchronisation
  getRepasNonCouverts: (params = {}) => api.cantine._get('/repas-non-couverts', params),
  synchroniserInscriptions: (body = {}) => api.cantine._send('/synchroniser-inscriptions', 'POST', body),

  // Pointage du jour
  getFeuilleDuJour: (params = {}) => api.cantine._get('/pointage', params),
  enregistrerPointage: (body = {}) => api.cantine._send('/pointage', 'POST', body),
  annulerPointage: (body = {}) => api.cantine._send('/pointage', 'DELETE', body)
},

parametresPointage: {
  // GET - Récupérer les paramètres pour une année scolaire
  getByAnnee: async (anneeId) => {
    try {
      const response = await fetch(`${API_URL}/parametres-pointage/${anneeId}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur get parametres:', error);
      return { success: false, error: error.message };
    }
  },

  // PUT - Mettre à jour les paramètres pour une année scolaire
  updateByAnnee: async (anneeId, data) => {
    try {
      const response = await fetch(`${API_URL}/parametres-pointage/${anneeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur update parametres:', error);
      return { success: false, error: error.message };
    }
  },

  // GET - Récupérer le mode de pointage pour une année
  getModeByAnnee: async (anneeId) => {
    try {
      const response = await fetch(`${API_URL}/parametres-pointage/mode/${anneeId}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur get mode:', error);
      return { success: false, error: error.message };
    }
  },

  // GET - Récupérer les valeurs par défaut
  getDefaut: async () => {
    try {
      const response = await fetch(`${API_URL}/parametres-pointage/defaut`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur get defaut:', error);
      return { success: false, error: error.message };
    }
  },

  // ⚠️ ANCIENNES MÉTHODES (pour compatibilité temporaire)
  get: async () => {
    console.warn('⚠️ Méthode obsolète : utilisez getByAnnee() à la place');
    const anneeId = localStorage.getItem('annee_scolaire_id') || '1';
    return api.parametresPointage.getByAnnee(anneeId);
  },

  update: async (data) => {
    console.warn('⚠️ Méthode obsolète : utilisez updateByAnnee() à la place');
    const anneeId = localStorage.getItem('annee_scolaire_id') || '1';
    return api.parametresPointage.updateByAnnee(anneeId, data);
  }
},



// ============================================
// GROUPES HORAIRES
// ============================================
groupesHoraires: {
  // GET - Liste de tous les groupes
  getAll: async () => {
    try {
      const response = await fetch(`${API_URL}/groupes-horaires`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur getAll groupes:', error);
      return { success: false, error: error.message };
    }
  },

  getById: async (id) => {
    try {
      const response = await fetch(`${API_URL}/groupes-horaires/${id}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur getById groupe:', error);
      return { success: false, error: error.message };
    }
  },

  // POST - Créer un groupe
  create: async (data) => {
    try {
      const response = await fetch(`${API_URL}/groupes-horaires`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur create groupe:', error);
      return { success: false, error: error.message };
    }
  },

  // PUT - Modifier un groupe
  update: async (id, data) => {
    try {
      const response = await fetch(`${API_URL}/groupes-horaires/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur update groupe:', error);
      return { success: false, error: error.message };
    }
  },

  // DELETE - Supprimer un groupe
  delete: async (id) => {
    try {
      const response = await fetch(`${API_URL}/groupes-horaires/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur delete groupe:', error);
      return { success: false, error: error.message };
    }
  },

  // GET - Employés d'un groupe
  getEmployes: async (id) => {
    try {
      const response = await fetch(`${API_URL}/groupes-horaires/${id}/employes`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur getEmployes:', error);
      return { success: false, error: error.message };
    }
  },

  // PUT - Affecter des employés à un groupe
  affecterEmployes: async (id, employe_ids) => {
    try {
      const response = await fetch(`${API_URL}/groupes-horaires/${id}/affecter-employes`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ employe_ids })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur affecterEmployes:', error);
      return { success: false, error: error.message };
    }
  },

  // PUT - Retirer un employé d'un groupe
  retirerEmploye: async (employe_id) => {
    try {
      const response = await fetch(`${API_URL}/groupes-horaires/employes/${employe_id}/retirer`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur retirerEmploye:', error);
      return { success: false, error: error.message };
    }
  },

  // GET - Employés non affectés
  getEmployesNonAffectes: async () => {
    try {
      const response = await fetch(`${API_URL}/groupes-horaires/employes/non-affectes/liste`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur getEmployesNonAffectes:', error);
      return { success: false, error: error.message };
    }
  }
},



support: {
  // Ã¢ÂÂ URLs CORRECTES (vos URLs originales qui marchent)
  getPendingRequests: async () => {
    try {
      const response = await fetch(`${API_URL}/support/pending-requests`, { // Ã¢ÂÂ VOS URLs
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur getPendingRequests:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  processRequest: async (requestId, action, options = {}) => {
    try {
      const response = await fetch(`${API_URL}/support/process-request/${requestId}`, { // Ã¢ÂÂ VOS URLs
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action,
          notes: options.notes || null
        }),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur processRequest:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  markAsProcessed: async (requestId) => {
    try {
      const response = await fetch(`${API_URL}/support/mark-processed/${requestId}`, { // Ã¢ÂÂ VOS URLs
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur markAsProcessed:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  getHistorique: async (page = 1) => {
    try {
      const response = await fetch(`${API_URL}/support/historique?page=${page}`, {
        headers: getAuthHeaders()
      });

      const result = await handleResponse(response);
      return result;
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration de l\'historique:', error);
      throw error;
    }
  },

  // Ã¢ÂÂ NOUVELLES mÃÂ©thodes pour les tickets de support (avec VOS patterns d'URLs)
  getSupportRequests: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          queryParams.append(key, filters[key]);
        }
      });

      const response = await fetch(`${API_URL}/support/requests?${queryParams}`, { // Ã¢ÂÂ Pattern cohÃÂ©rent
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur getSupportRequests:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  getSupportRequestById: async (requestId) => {
    try {
      const response = await fetch(`${API_URL}/support/requests/${requestId}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur getSupportRequestById:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  processSupportRequest: async (requestId, updateData) => {
    try {
      const response = await fetch(`${API_URL}/support/requests/${requestId}/process`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur processSupportRequest:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  createSupportRequest: async (requestData) => {
    try {
      const response = await fetch(`${API_URL}/support/requests`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur createSupportRequest:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  getMyRequests: async (limit = 10) => {
    try {
      const response = await fetch(`${API_URL}/support/my-requests?limit=${limit}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur getMyRequests:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  getSupportStats: async () => {
    try {
      const response = await fetch(`${API_URL}/support/stats`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur getSupportStats:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  getSpecialRequestsConfig: async () => {
    try {
      const response = await fetch(`${API_URL}/support/special-requests/config`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur getSpecialRequestsConfig:', error);
      return { success: false, error: error.message };
    }
  },

  updateSpecialRequestsConfig: async (data) => {
    try {
      const response = await fetch(`${API_URL}/support/special-requests/config`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur updateSpecialRequestsConfig:', error);
      return { success: false, error: error.message };
    }
  },

  getSpecialRequests: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });

      const suffix = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await fetch(`${API_URL}/support/special-requests${suffix}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur getSpecialRequests:', error);
      return { success: false, error: error.message };
    }
  },

  createSpecialRequest: async (data) => {
    try {
      const response = await fetch(`${API_URL}/support/special-requests`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur createSpecialRequest:', error);
      return { success: false, error: error.message };
    }
  },

  processSpecialRequest: async (requestId, data) => {
    try {
      const response = await fetch(`${API_URL}/support/special-requests/${requestId}/decision`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur processSpecialRequest:', error);
      return { success: false, error: error.message };
    }
  }
},




  // Nouvel endpoint RÃÂ´les


// Section pour les rÃÂ´les
roles: {
  create: async (donnees) => {
    try {
      const response = await fetch(`${API_URL}/roles`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(donnees)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la creation du role:', error);
      return { success: false, error: error.message };
    }
  },

  update: async (id, donnees) => {
    try {
      const response = await fetch(`${API_URL}/roles/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(donnees)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la modification du role:', error);
      return { success: false, error: error.message };
    }
  },

  remove: async (id) => {
    try {
      const response = await fetch(`${API_URL}/roles/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la suppression du role:', error);
      return { success: false, error: error.message };
    }
  },

  getAll: async () => {
    try {
      const response = await fetch(`${API_URL}/roles`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des rÃÂ´les:', error);
      return { success: false, error: error.message };
    }
  },

  getByGroupeId: async (groupeId) => {
    try {
      const response = await fetch(`${API_URL}/roles/groupe/${groupeId}`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des rÃÂ´les:', error);
      return { success: false, error: error.message };
    }
  },

  getPermissions: async (id) => {
    try {
      const response = await fetch(`${API_URL}/roles/${id}/permissions`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des permissions:', error);
      return { success: false, error: error.message };
    }
  },

  // Dans la partie securityGroups.updatePermissions de api.js
updatePermissions: async (groupId, dataToSend) => {
  try {
    // Notez que dataToSend contient dÃÂ©jÃÂ  { permissions: {...} }
    const response = await fetch(`${API_URL}/security-groups/${groupId}/permissions`, {
      method: 'PUT',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dataToSend) // On envoie directement dataToSend qui a le bon format
    });

    // Si l'API n'existe pas encore, utiliser celle des rÃÂ´les comme fallback
    if (!response.ok && response.status === 404) {
      console.log("API /security-groups/permissions non disponible, fallback sur /roles");

      // RÃÂ©cupÃÂ©rer les rÃÂ´les associÃÂ©s au groupe
      const rolesResponse = await fetch(`${API_URL}/roles/groupe/${groupId}`, {
        headers: getAuthHeaders()
      });

      if (!rolesResponse.ok) {
        throw new Error(`Erreur lors de la rÃÂ©cupÃÂ©ration des rÃÂ´les: ${rolesResponse.status}`);
      }

      // Utiliser await pour s'assurer d'avoir les donnÃÂ©es
      const rolesData = await rolesResponse.json();

      // VÃÂ©rifier que rolesData est bien un tableau
      if (!Array.isArray(rolesData)) {
        console.error("Format de rÃÂ©ponse inattendu pour les rÃÂ´les:", rolesData);
        return {
          success: false,
          error: "Format de donnÃÂ©es inattendu pour les rÃÂ´les"
        };
      }

      // Pour chaque rÃÂ´le du groupe, mettre ÃÂ  jour ses permissions
      for (const role of rolesData) {
        await fetch(`${API_URL}/roles/${role.id}/permissions`, {
          method: 'PUT',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(dataToSend) // Utiliser le mÃÂªme format de donnÃÂ©es
        });
      }

      return {
        success: true,
        message: "Permissions mises ÃÂ  jour via le systÃÂ¨me de rÃÂ´les existant"
      };
    }

    return handleResponse(response);
  } catch (error) {
    console.error('Erreur lors de la mise ÃÂ  jour des permissions:', error);
    return {
      success: false,
      error: error.message
    };
  }
},

},

// Section pour les permissions
permissions: {
  getAll: async () => {
    try {
      const response = await fetch(`${API_URL}/permissions`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des permissions:', error);
      return { success: false, error: error.message };
    }
  },

  getByModule: async () => {
    try {
      const response = await fetch(`${API_URL}/permissions/by-module`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des permissions par module:', error);
      return { success: false, error: error.message };
    }
  }
},

  // Nouvel endpoint DÃÂ©partements
 departements: {
  getAll: async () => {
    const response = await fetch(`${API_URL}/departements`, {
      headers: getAuthHeaders() // ✅ Ajout du token ici
    });
    return handleResponse(response);
  }
},



 // Dans api.js, service timetable
timetable: {


    getAll: async (params) => {
      try {
        const queryParams = new URLSearchParams({
          ...(params.year_id && { annee_scolaire_id: params.year_id }),
          ...(params.class_id && { classe_id: params.class_id }),
          ...(params.teacher_id && { enseignant_id: params.teacher_id })
        });

        const url = `${API_URL}/timetable?${queryParams}`;
        console.log('URL de requÃÂªte:', url); // Pour dÃÂ©boguer

        const response = await fetch(url, {
          headers: getAuthHeaders()
        });

        return handleResponse(response);
      } catch (error) {
        console.error('Erreur getAll:', error);
        throw error;
      }
    },


  getByClass: async ({ classId, annee_scolaire_id }) => {
  try {
    // VÃÂ©rifier que les paramÃÂ¨tres requis sont prÃÂ©sents
    if (!classId || !annee_scolaire_id) {
      throw new Error('classId et annee_scolaire_id sont requis');
    }

    const url = new URL(`${API_URL}/timetable/classe/${classId}`);

    const response = await fetch(url, {
      headers: getAuthHeaders() // Ajoutez les headers d'authentification si nÃÂ©cessaire
    });

    return handleResponse(response);
  } catch (error) {
    console.error('Erreur getByClass:', error);
    throw error;
  }
},



  update: async (id, data) => {
  try {
    const formattedData = {
      classe_id: data.classe_id,
      enseignant_id: data.enseignant_id || data.teacher_id,
      matiere_id: data.matiere_id || data.subject_id,
      affectation_id: data.affectation_id || null,
      jour_semaine: data.jour_semaine?.toString(),
      heure_debut: data.heure_debut,
      heure_fin: data.heure_fin,
      salle: data.salle || '101',
      annee_scolaire_id: data.annee_scolaire_id,
      date: data.date,
      scope: data.scope,  // 🔥 AJOUTÉ
      ...(data.scope === 'period' && {
        date_debut: data.date_debut,
        date_fin: data.date_fin
      })
    };

    console.log('Données formatées pour envoi API:', formattedData);

    const response = await fetch(`${API_URL}/timetable/${id}`, {
      method: 'PUT',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formattedData)
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur update:', error);
    throw error;
  }
},




  create: async (data) => {
    try {
      const formattedData = {
        classe_id: data.classe_id,
        enseignant_id: data.enseignant_id || data.teacher_id,
        matiere_id: data.matiere_id || data.subject_id,
        jour_semaine: data.jour_semaine?.toString(),
        heure_debut: data.heure_debut,
        heure_fin: data.heure_fin,
        salle: data.salle || '101',
        annee_scolaire_id: data.annee_scolaire_id,
        affectation_id: data.affectation_id,
        date: data.date
      };

      console.log('DonnÃÂ©es formatÃÂ©es pour crÃÂ©ation:', formattedData);

      const response = await fetch(`${API_URL}/timetable`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formattedData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur create:', error);
      throw error;
    }
  },

  // Un second `delete:` existait ici, en double avec celui d'en dessous : dans
  // un objet JavaScript, c'est la dernière clé qui gagne, donc celui-ci ne
  // servait à rien et entretenait la confusion. Retiré le 28/09/2026.

  createSeries: async (serieData) => {
  try {
    if (!serieData || !serieData.slots || !Array.isArray(serieData.slots)) {
      throw new Error('Format de données invalide');
    }

    const formattedData = {
      type_recurrence: serieData.type_recurrence,
      date_debut: serieData.date_debut,
      date_fin: serieData.date_fin,
      jours_semaine: serieData.jours_semaine,
      heure_debut: serieData.heure_debut,
      heure_fin: serieData.heure_fin,
      salle: serieData.salle || '101',
      annee_scolaire_id: Number(serieData.annee_scolaire_id),

      slots: serieData.slots.map(slot => ({
        classe_id: Number(slot.classe_id),
        enseignant_id: Number(slot.enseignant_id),
        matiere_id: Number(slot.matiere_id),
        affectation_id: slot.affectation_id ? Number(slot.affectation_id) : null,
        jour_semaine: Number(slot.jour_semaine),
        heure_debut: slot.heure_debut,
        heure_fin: slot.heure_fin,
        salle: slot.salle || '101',
        annee_scolaire_id: Number(serieData.annee_scolaire_id)
      })),
      mutualisations: Array.isArray(serieData.mutualisations)
        ? serieData.mutualisations.map(item => ({
            classe_principale_id: Number(item.classe_principale_id),
            classes_ids: Array.isArray(item.classes_ids) ? item.classes_ids.map(Number) : [],
            enseignant_id: Number(item.enseignant_id),
            matiere_id: item.matiere_id === null || item.matiere_id === undefined ? null : Number(item.matiere_id),
            jour_semaine: Number(item.jour_semaine),
            heure_debut: item.heure_debut,
            heure_fin: item.heure_fin,
            annee_scolaire_id: Number(item.annee_scolaire_id || serieData.annee_scolaire_id)
          }))
        : []
    };

    const response = await fetch(`${API_URL}/timetable/series`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()  // ✅ AJOUTÉ
      },
      body: JSON.stringify(formattedData)
    });

    return handleResponse(response);
  } catch (error) {
    console.error('Erreur lors de la création de la série:', error);
    throw error;
  }
},




  // RÃÂ©cupÃÂ©ration de toutes les sÃÂ©ries
getSeries: async (annee_scolaire_id) => {
  try {
    const response = await fetch(`${API_URL}/timetable/series?annee_scolaire_id=${annee_scolaire_id}`, {
      headers: getAuthHeaders()  // ✅ AJOUTÉ
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur lors de la récupération des séries:', error);
    throw error;
  }
},

  // RÃÂ©cupÃÂ©ration d'une sÃÂ©rie spÃÂ©cifique
  getSeriesById: async (serieId) => {
  try {
    const response = await fetch(`${API_URL}/timetable/series/${serieId}`, {
      headers: getAuthHeaders()  // ✅ AJOUTÉ
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur lors de la récupération de la série:', error);
    throw error;
  }
},

  // Mise ÃÂ  jour d'une sÃÂ©rie
  updateSeries: async (serieId, serieData) => {
  try {
    const response = await fetch(`${API_URL}/timetable/series/${serieId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()  // ✅ AJOUTÉ
      },
      body: JSON.stringify(serieData)
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur lors de la mise à jour de la série:', error);
    throw error;
  }
},

  // Suppression d'une sÃÂ©rie
  deleteSeries: async (serieId) => {
  try {
    const response = await fetch(`${API_URL}/timetable/series/${serieId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()  // ✅ AJOUTÉ
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur lors de la suppression de la série:', error);
    throw error;
  }
},

  createBatchSlots: async (slots) => {
  try {
    const formattedData = Array.isArray(slots)
      ? slots.reduce((acc, slot, index) => {
          acc[index] = slot;
          return acc;
        }, {})
      : slots;

    const response = await fetch(`${API_URL}/timetable/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()  // ✅ AJOUTÉ
      },
      body: JSON.stringify(formattedData)
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur lors de la création en batch:', error);
    throw error;
  }
},

  checkSlotConflicts: async (slots) => {
  try {
    const response = await fetch(`${API_URL}/timetable/check-conflicts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()  // ✅ AJOUTÉ
      },
      body: JSON.stringify({ slots })
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur lors de la vérification des conflits:', error);
    throw error;
  }
},

  updateBatchSlots: async (slots) => {
  try {
    const response = await fetch(`${API_URL}/timetable/batch`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()  // ✅ AJOUTÉ
      },
      body: JSON.stringify({ slots })
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur lors de la mise à jour en batch:', error);
    throw error;
  }
},

  deleteRecurringSeries: async (seriesId) => {
  try {
    const response = await fetch(`${API_URL}/timetable/series/${seriesId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()  // ✅ AJOUTÉ
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur lors de la suppression de la série:', error);
    throw error;
  }
},


  logModification: async (modificationData) => {
    const response = await fetch(`${API_URL}/timetable/${modificationData.emploi_temps_id}/modifications`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()  // ✅ AJOUTÉ
      },
      body: JSON.stringify({
        date_modification: modificationData.date_modification,
        motif: modificationData.motif,
        nouvelle_salle: modificationData.nouvelle_salle,
        enseignant_remplacant_id: modificationData.enseignant_remplacant_id
      })
    });
    return handleResponse(response);
  },

  // `getAuthHeaders()` porte le jeton ET l'en-tête X-Tenant : sans lui, toutes
  // les routes `/api` répondent 401, l'authentification étant posée en amont
  // dans server.js (`app.use('/api', authenticateRequest, routes)`).
  delete: async (id) => {
    const response = await fetch(`${API_URL}/timetable/${id}`, {
      method: 'DELETE',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      }
    });
    return handleResponse(response);
  },


  cancel: async (id, motif = null) => {
    try {
      const response = await fetch(`${API_URL}/timetable/${id}/cancel`, {
        method: 'PATCH',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ motif })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur annulation crÃÂ©neau:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },



// 🆕 MÉTHODES POUR COURS MUTUALISÉS

/**
 * Récupérer les classes disponibles pour mutualisation
 */
getClassesDisponiblesMutualisation: async (params) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/timetable/classes-disponibles-mutualisation?${queryString}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur récupération classes disponibles:', error);
    return {
      success: false,
      error: error.message
    };
  }
},

/**
 * Créer une mutualisation de cours
 */
creerMutualisation: async (data) => {
  try {
    const response = await fetch(`${API_URL}/timetable/mutualiser`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur création mutualisation:', error);
    return {
      success: false,
      error: error.message
    };
  }
},

/**
 * Récupérer les informations d'un groupe mutualisé
 */
getGroupeMutualise: async (groupeId) => {
  try {
    const response = await fetch(
      `${API_URL}/timetable/groupe-mutualise/${groupeId}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur récupération groupe mutualisé:', error);
    return {
      success: false,
      error: error.message
    };
  }
},

/**
 * Supprimer une mutualisation
 */
supprimerMutualisation: async (groupeId) => {
  try {
    const response = await fetch(
      `${API_URL}/timetable/mutualiser/${groupeId}`,
      {
        method: 'DELETE',
        headers: getAuthHeaders()
      }
    );
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur suppression mutualisation:', error);
    return {
      success: false,
      error: error.message
    };
  }
},


/**
 * Récupérer le volume horaire détaillé d'un enseignant par classe
 * Retourne pour chaque classe : le détail des créneaux + le total
 */
getVolumeHoraireDetaille: async (enseignantId, annee_scolaire_id) => {
  try {
    const response = await fetch(
      `${API_URL}/timetable/enseignant/${enseignantId}/volume-horaire?annee_scolaire_id=${annee_scolaire_id}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur récupération volume horaire:', error);
    return {
      success: false,
      error: error.message
    };
  }
},


// 🆕 MÉTHODES POUR SUPPRESSION EN MASSE

/**
 * Prévisualiser le nombre de créneaux qui seront supprimés
 */
// Les enseignants qui ont des creneaux dans les classes cochees : l'ecran de
// suppression ciblee ne propose que des gens qui ont quelque chose a supprimer.
getEnseignantsPourSuppression: async (params) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/timetable/bulk-delete/enseignants?${queryString}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur liste des enseignants a supprimer:', error);
    return { success: false, error: error.message };
  }
},

previewBulkDelete: async (params) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_URL}/timetable/bulk-delete/preview?${queryString}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur preview suppression:', error);
    return {
      success: false,
      error: error.message
    };
  }
},

/**
 * Supprimer en masse des créneaux
 */
bulkDelete: async (data) => {
  try {
    const response = await fetch(`${API_URL}/timetable/bulk-delete`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur suppression en masse:', error);
    return {
      success: false,
      error: error.message
    };
  }
},




 /**
   * Export PDF emploi du temps (single)
   * @param {Object} params - { type: 'classe|enseignant', id, annee_scolaire_id, dateDebut?, dateFin? }
   * @returns {Blob} - Blob du PDF
   */
  exportPDF: async (params) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          queryParams.append(key, params[key]);
        }
      });

      const response = await fetch(`${API_URL}/timetable/export-pdf?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          ...getAuthHeaders()
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors de l\'export PDF');
      }

      // Retourner le blob directement
      return await response.blob();

    } catch (error) {
      console.error('Erreur export PDF:', error);
      throw error;
    }
  },

  /**
   * Export multiple PDF emplois du temps (ZIP)
   * @param {Object} data - { type: 'classe|enseignant', ids: [], annee_scolaire_id, dateDebut?, dateFin? }
   * @returns {Blob} - Blob du ZIP
   */
  exportMultiplePDF: async (data) => {
    try {
      const response = await fetch(`${API_URL}/timetable/export-multiple-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors de l\'export multiple');
      }

      // Retourner le blob directement
      return await response.blob();

    } catch (error) {
      console.error('Erreur export multiple PDF:', error);
      throw error;
    }
  },



  toggleCancel: async (id, data) => {
  try {
    const response = await fetch(`${API_URL}/timetable/${id}/toggle-cancel`, {
      method: 'PATCH',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur toggle annulation:', error);
    throw error;
  }
},



},





discipline: {
    // RÃÂ©cupÃÂ©rer les suivis d'un ÃÂ©lÃÂ¨ve
    getSuivisByEleve: async (eleveId) => {
      const response = await fetch(`${API_URL}/eleves/${eleveId}/suivis-disciplinaires`);
      return handleResponse(response);
    },

    // CrÃÂ©er un suivi
    createSuivi: async (eleveId, suiviData) => {
      const response = await fetch(`${API_URL}/eleves/${eleveId}/suivis-disciplinaires`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...suiviData,
          annee_scolaire: '2023-2024'
        })
      });
      return handleResponse(response);
    },

    // Mettre ÃÂ  jour un suivi
    updateSuivi: async (eleveId, suiviId, suiviData) => {
      const response = await fetch(`${API_URL}/eleves/${eleveId}/suivis-disciplinaires/${suiviId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...suiviData,
          annee_scolaire: '2023-2024'
        })
      });
      return handleResponse(response);
    },

    // Supprimer un suivi
    deleteSuivi: async (eleveId, suiviId) => {
      const response = await fetch(`${API_URL}/eleves/${eleveId}/suivis-disciplinaires/${suiviId}`, {
        method: 'DELETE'
      });
      return handleResponse(response);
    },
  },


  // Modèles d'identifiant et de matricule de l'école.
  numerotation: {
    lire: async () => {
      try {
        const response = await fetch(`${API_URL}/parametres/numerotation`, { headers: getAuthHeaders() });
        return await handleResponse(response);
      } catch (error) {
        return { success: false, message: error.message };
      }
    },

    apercu: async (modeles) => {
      try {
        const response = await fetch(`${API_URL}/parametres/numerotation/apercu`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify(modeles)
        });
        return await handleResponse(response);
      } catch (error) {
        return { success: false, message: error.message };
      }
    },

    // Chaque champ est facultatif : { modele_identifiant } ou
    // { modele_matricule, prochain_numero }
    enregistrer: async (reglages) => {
      try {
        const response = await fetch(`${API_URL}/parametres/numerotation`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify(reglages)
        });
        return await handleResponse(response);
      } catch (error) {
        return { success: false, message: error.message };
      }
    }
  },

  // Double authentification des comptes : ce que l'école en décide.
  // `handleResponse` lève sur 401/404/5xx : chaque appel renvoie donc un
  // `{ success: false, message }` plutôt que de laisser l'écran croire à une
  // liste vide.
  doubleAuth: {
    lister: async () => {
      try {
        const response = await fetch(`${API_URL}/double-auth`, { headers: getAuthHeaders() });
        return await handleResponse(response);
      } catch (error) {
        return { success: false, message: error.message, code: error.code };
      }
    },

    journal: async () => {
      try {
        const response = await fetch(`${API_URL}/double-auth/journal`, { headers: getAuthHeaders() });
        return await handleResponse(response);
      } catch (error) {
        return { success: false, message: error.message, code: error.code };
      }
    },

    // { employeIds: [..] } ou { groupeId }
    exiger: async (cible) => {
      try {
        const response = await fetch(`${API_URL}/double-auth/exiger`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify(cible)
        });
        return await handleResponse(response);
      } catch (error) {
        return { success: false, message: error.message, code: error.code };
      }
    },

    reinitialiser: async (employeId, motif) => {
      try {
        const response = await fetch(`${API_URL}/double-auth/${employeId}/reinitialiser`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ motif })
        });
        return await handleResponse(response);
      } catch (error) {
        return { success: false, message: error.message, code: error.code };
      }
    },

    desactiver: async (employeId, motif) => {
      try {
        const response = await fetch(`${API_URL}/double-auth/${employeId}/desactiver`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ motif })
        });
        return await handleResponse(response);
      } catch (error) {
        return { success: false, message: error.message, code: error.code };
      }
    }
  },

  finance: {
  // Récupérer les paiements d'un élève
  getPaiements: async (eleveId, anneeId, categorie = null) => {
    try {
      const params = new URLSearchParams();
      if (anneeId) params.append('annee_scolaire_id', anneeId);
      if (categorie) params.append('categorie', categorie);

      const response = await fetch(`${API_URL}/finance/paiements/${eleveId}?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la recuperation des paiements:', error);
      return { success: false, error: error.message };
    }
  },


  getPaiementsAnnexesPonctuels: async (eleveId, anneeId, categorie = null) => {
    try {
      const params = new URLSearchParams();
      if (anneeId) params.append('annee_scolaire_id', anneeId);
      if (categorie) params.append('categorie', categorie);

      const response = await fetch(`${API_URL}/finance/paiements-annexes-ponctuels/${eleveId}?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur recuperation paiements annexes ponctuels:', error);
      return { success: false, error: error.message, message: error.message };
    }
  },

  creerPaiementAnnexePonctuel: async (eleveId, paiementData) => {
    try {
      const response = await fetch(`${API_URL}/finance/paiements-annexes-ponctuels/${eleveId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(paiementData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur paiement annexe ponctuel:', error);
      return { success: false, error: error.message, message: error.message };
    }
  },

  genererRecuAnnexePonctuel: async (paiementId) => {
    try {
      const response = await fetch(`${API_URL}/recus/annexe-ponctuel/${paiementId}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la generation du recu annexe');
      }

      return await response.blob();
    } catch (error) {
      console.error('Erreur generation recu annexe ponctuel:', error);
      throw error;
    }
  },
  // Récupérer l'historique d'un élève
  getHistorique: async (eleveId) => {
    const response = await fetch(`${API_URL}/finance/historique/${eleveId}`, {
      headers: getAuthHeaders()  // ✅ CORRIGÉ
    });
    return handleResponse(response);
  },

  // Récupérer le récapitulatif d'une classe
  getRecapClasse: async (classeId) => {
    const response = await fetch(`${API_URL}/finance/recap-classe/${classeId}`, {
      headers: getAuthHeaders()  // ✅ CORRIGÉ
    });
    return handleResponse(response);
  },

  // Générer un reçu de paiement
  generateReceipt: async (receiptData) => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

      if (!receiptData.user && currentUser.id) {
        receiptData.user = { id: currentUser.id };
      }

      const response = await fetch(`${API_URL}/finance/receipts/generate`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),  // ✅ OK
          'Accept': 'application/pdf'
        },
        body: JSON.stringify(receiptData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors de la génération du reçu');
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/pdf')) {
        const pdfBlob = await response.blob();
        return {
          success: true,
          data: pdfBlob,
          filename: `recu-paiement-${new Date().toISOString().split('T')[0]}.pdf`
        };
      } else {
        const data = await response.json().catch(() => ({}));
        return { success: true, data, isPdf: false };
      }
    } catch (error) {
      console.error('Erreur lors de la génération du reçu:', error);
      return { success: false, error: error.message || 'Erreur lors de la génération du reçu' };
    }
  },

  // Télécharger un reçu existant
  downloadReceipt: async (receiptId) => {
    try {
      const response = await fetch(`${API_URL}/finance/receipts/${receiptId}/download`, {
        headers: {
          ...getAuthHeaders(),  // ✅ OK
          'Accept': 'application/pdf'
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors du téléchargement du reçu');
      }

      const pdfBlob = await response.blob();
      return {
        success: true,
        data: pdfBlob,
        filename: `recu-${receiptId}.pdf`
      };
    } catch (error) {
      console.error('Erreur lors du téléchargement du reçu:', error);
      return { success: false, error: error.message || 'Erreur lors du téléchargement du reçu' };
    }
  },

  // Récupérer les modes de paiement
  getModesPaiement: async () => {
    try {
      const response = await fetch(`${API_URL}/finance/modes-paiement`, {
        headers: getAuthHeaders()  // ✅ OK
      });

      if (!response.ok) {
        console.warn('API des modes de paiement non disponible, utilisation des valeurs par défaut');
        return {
          success: true,
          data: [
            { id: 1, nom: "Esp\u00e8ces" },
            { id: 2, nom: "Virement" },
            { id: 3, nom: "Transfert Mobile" },
            { id: 4, nom: "Ch\u00e8que" }
          ]
        };
      }

      return normalizePaymentModesPayload(await handleResponse(response));
    } catch (error) {
      console.error('Erreur lors de la récupération des modes de paiement:', error);
      return {
        success: true,
        data: [
          { id: 1, nom: "Esp\u00e8ces" },
          { id: 2, nom: "Virement" },
          { id: 3, nom: "Transfert Mobile" },
          { id: 4, nom: "Ch\u00e8que" }
        ]
      };
    }
  },

  // Récupérer les détails d'un paiement
  getPaymentDetails: async (eleveId, moisId, anneeId) => {
    try {
      const response = await fetch(`${API_URL}/finance/paiements/${eleveId}/${moisId}/details?annee_scolaire_id=${anneeId}`, {
        headers: getAuthHeaders()  // ✅ OK
      });

      if (!response.ok) {
        console.warn('API des détails de paiement non disponible');
        return {
          success: true,
          data: {
            agent_nom: "Information non disponible",
            agent_prenom: "",
            mode_paiement: "Non sp\u00e9cifi\u00e9",
            numero_transaction: null
          }
        };
      }

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la récupération des détails du paiement:', error);
      return {
        success: true,
        data: {
          agent_nom: "Information non disponible",
          agent_prenom: "",
          mode_paiement: "Non sp\u00e9cifi\u00e9",
          numero_transaction: null
        }
      };
    }
  },

  // ✅ GARDER SEULEMENT CETTE VERSION (avec try/catch)
  updatePayments: async (eleveId, paiementData) => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

      const response = await fetch(`${API_URL}/finance/paiements`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()  // ✅ CORRIGÉ (ordre correct)
        },
        body: JSON.stringify({
          eleve_id: eleveId,
          mois_id: paiementData.mois_id,
          montant_paye: parseFloat(paiementData.montant_paye),
          date_paiement: new Date().toISOString().split('T')[0],
          annee_scolaire_id: paiementData.annee_scolaire_id || '2023-2024',
          agent_id: paiementData.agent_id || currentUser.id,
          mode_paiement: paiementData.mode_paiement || 'Esp\u00e8ces',
          numero_transaction: paiementData.numero_transaction || null
        })
      });
      return handleResponse(response);
    } catch (error) {
      console.error("Erreur lors de l'enregistrement du paiement:", error);
      return {
        success: false,
        message: error.message,
        error: error.message,
        code: error.code
      };
    }
  },

  // Récupérer les frais par catégorie
  getFraisCategories: async (eleveId, classeId, anneeScolaireId) => {
    try {
      const response = await fetch(`${API_URL}/finance/frais-categories?eleve_id=${eleveId}&classe_id=${classeId}&annee_scolaire_id=${anneeScolaireId}`, {
        headers: getAuthHeaders()  // ✅ OK
      });

      if (!response.ok) {
        console.log("API des frais par catégorie non disponible, récupération des frais de classe");

        const classeResponse = await fetch(`${API_URL}/classes/${classeId}`, {
          headers: getAuthHeaders()  // ✅ OK
        });

        if (!classeResponse.ok) {
          throw new Error("Impossible de récupérer les détails de la classe");
        }

        const classeData = await classeResponse.json();
        const fraisMensuels = classeData.frais_mensuel || 50000;

        return {
          success: true,
          data: {
            scolarite: fraisMensuels,
            transport: 15000,
            cantine: 20000,
            activites: 10000
          }
        };
      }

      return handleResponse(response);
    } catch (error) {
      console.error("Erreur lors de la récupération des frais par catégorie:", error);
      return {
        success: true,
        data: {
          scolarite: 50000,
          transport: 15000,
          cantine: 20000,
          activites: 10000
        }
      };
    }
  },

  // Enregistrer un paiement multi-catégories
  enregistrerPaiementMulti: async (donneesPaiement) => {
  try {
    if (donneesPaiement.mode_paiement && typeof donneesPaiement.mode_paiement === 'string') {
      donneesPaiement.mode_paiement = normalizePaymentModeName(donneesPaiement.mode_paiement);
    }

    // 🆕 Gérer le cas "Pas de mode de paiement car non scolarisé"
    if (donneesPaiement.mode_paiement === 'Pas de mode de paiement car non scolaris\u00e9') {
      // Ne pas envoyer de mode_paiement au backend
      donneesPaiement.mode_paiement = null;
      donneesPaiement.mode_paiement_id = null;
    } else if (donneesPaiement.mode_paiement && typeof donneesPaiement.mode_paiement === 'string') {
      // Convertir le nom du mode de paiement en ID (logique existante)
      try {
        const modesPaiementResponse = await api.finance.getModesPaiement();

        if (modesPaiementResponse.success && Array.isArray(modesPaiementResponse.data)) {
          const modePaiement = modesPaiementResponse.data.find(
            mode => normalizePaymentModeName(mode.nom).toLowerCase() === donneesPaiement.mode_paiement.toLowerCase()
          );

          if (modePaiement) {
            donneesPaiement.mode_paiement_id = modePaiement.id;
          }
        }
      } catch (modeError) {
        console.warn("Erreur lors de la récupération des modes de paiement:", modeError);
      }
    }

    const response = await fetch(`${API_URL}/finance/paiements/multi`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(donneesPaiement)
    });

    // ⚠️ UN 404 N'EST JAMAIS UN ENCAISSEMENT.
    //
    // Ce bloc rendait `success: true` avec un identifiant tiré au hasard
    // (`Math.random()`) et le message « Paiement enregistré avec succès
    // (simulation) ». Le caissier lisait un bandeau vert, le formulaire se
    // vidait, le pied de modal basculait sur « Nouveau paiement » — et rien
    // n'avait été écrit. L'argent était dans le tiroir sans trace.
    //
    // Un 404 ici veut dire : la route est absente du backend déployé (le cas
    // le plus crédible, un frontend plus récent que le serveur pendant une
    // MEP), l'école est introuvable dans `klasys_tenants`, ou un proxy a
    // répondu à la place du serveur. Dans les trois cas on refuse.
    //
    // ⚠️ Le message du serveur passe AVANT le nôtre quand il en envoie un :
    // figer la phrase ici poserait un masque permanent sur tout 404 métier
    // que le backend pourrait vouloir expliquer un jour.
    if (response.status === 404) {
      let code = null;
      let messageServeur = null;
      try {
        const corps = await response.json();
        code = corps?.code || null;
        messageServeur = corps?.message || null;
      } catch (erreurCorps) {
        // Corps non JSON — page 404 d'un proxy, corps vide. Le message
        // générique ci-dessous suffit, et surtout : on ne laisse pas
        // `response.json()` faire échouer le refus lui-même.
      }

      console.error("POST /finance/paiements/multi a répondu 404 — paiement NON enregistré", code);

      const messageParDefaut = code === 'TENANT_NOT_FOUND'
        ? "École introuvable : le paiement n'a PAS été enregistré. Reconnectez-vous, puis recommencez."
        : "Le service de paiement est introuvable : le paiement n'a PAS été enregistré. Ne remettez pas de reçu et prévenez l'administrateur.";

      return {
        success: false,
        status: 404,
        code: code || 'PAIEMENT_SERVICE_INTROUVABLE',
        message: messageServeur || messageParDefaut,
        error: "Service de paiement introuvable (404)"
      };
    }

    return handleResponse(response);
  } catch (error) {
    console.error("Erreur lors de l'enregistrement du paiement multi-catégories:", error);
    return {
      success: false,
      message: error.message,
      error: error.message,
      code: error.code
    };
  }
},


  // Récupérer l'historique détaillé des paiements
  getHistoriquePaiementDetail: async (eleveId, moisId, annee_scolaire_id, categorie = null) => {
    try {
      let url = `${API_URL}/finance/paiements/${eleveId}/${moisId}/historique?annee_scolaire_id=${annee_scolaire_id}`;

      if (categorie) {
        url += `&categorie=${categorie}`;
      }

      const response = await fetch(url, {
        headers: getAuthHeaders()  // ✅ OK
      });

      return handleResponse(response);
    } catch (error) {
      console.error("Erreur lors de la récupération de l'historique détaillé:", error);
      return { success: false, error: error.message };
    }
  },



// Récupérer les frais d'inscription/réinscription d'un élève
  getFraisInscription: async (eleveId, anneeId) => {
    try {
      const response = await fetch(`${API_URL}/finance/frais-fixes/${eleveId}?annee_scolaire_id=${anneeId}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        // Si l'API retourne une erreur, on retourne null plutôt que de planter
        console.warn('API frais d\'inscription non disponible ou aucun frais trouvé');
        return {
          success: true,
          data: null,
          type: null
        };
      }

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la récupération des frais d\'inscription:', error);
      return {
        success: true,
        data: null,
        type: null,
        error: error.message
      };
    }
  },






  // Ajuster les frais d'inscription/réinscription
ajusterFraisInscription: async (fraisId, ajustementData) => {
  try {
    // ✅ CORRECTION : Validation côté client - accepter 0
    if (ajustementData.nouveau_montant === null ||
        ajustementData.nouveau_montant === undefined ||
        ajustementData.nouveau_montant === '') {
      return {
        success: false,
        message: 'Veuillez saisir un montant'
      };
    }

    const montantNum = parseFloat(ajustementData.nouveau_montant);

    if (isNaN(montantNum) || montantNum < 0) {
      return {
        success: false,
        message: 'Le montant doit être supérieur ou égal à 0'
      };
    }

    if (!ajustementData.justification || ajustementData.justification.trim().length < 10) {
      return {
        success: false,
        message: 'La justification doit contenir au moins 10 caractères'
      };
    }

    const response = await fetch(`${API_URL}/finance/frais-fixes/${fraisId}/ajuster`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({
        nouveau_montant: montantNum,  // ✅ Utiliser montantNum
        justification: ajustementData.justification.trim()
      })
    });

    return handleResponse(response);
  } catch (error) {
    console.error('Erreur lors de l\'ajustement des frais:', error);
    return {
      success: false,
      message: error.message || 'Erreur lors de l\'ajustement des frais'
    };
  }
},



  // Récupérer l'historique des ajustements
  getHistoriqueAjustements: async (fraisId) => {
    try {
      const response = await fetch(`${API_URL}/finance/frais-fixes/${fraisId}/historique`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        console.warn('Impossible de récupérer l\'historique des ajustements');
        return {
          success: true,
          data: []
        };
      }

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'historique:', error);
      return {
        success: true,
        data: [],
        error: error.message
      };
    }
  },


  // Créer des frais d'inscription par défaut
creerFraisParDefaut: async (fraisData) => {
  try {
    // Validation des données côté client
    if (!fraisData.eleve_id || !fraisData.annee_scolaire_id) {
      return {
        success: false,
        message: 'Données incomplètes : eleve_id et annee_scolaire_id requis'
      };
    }

    if (fraisData.montant === null || fraisData.montant === undefined) {
      return {
        success: false,
        message: 'Le montant est requis'
      };
    }

    const montantNum = parseFloat(fraisData.montant);
    if (isNaN(montantNum) || montantNum < 0) {
      return {
        success: false,
        message: 'Le montant doit être un nombre supérieur ou égal à 0'
      };
    }

    if (!fraisData.type || !['inscription', 'reinscription'].includes(fraisData.type)) {
      return {
        success: false,
        message: 'Le type doit être "inscription" ou "reinscription"'
      };
    }

    // ✅ AJOUT : Récupérer l'utilisateur connecté
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

    // Appel à l'API
    const response = await fetch(`${API_URL}/finance/frais-inscription/creer-par-defaut`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({
        eleve_id: fraisData.eleve_id,
        annee_scolaire_id: fraisData.annee_scolaire_id,
        montant: montantNum,
        type: fraisData.type,
        justification: fraisData.justification || `Création des frais de ${fraisData.type}`,
        agent_id: currentUser.id || null // ✅ Passer l'ID de l'utilisateur connecté
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        message: errorData.message || 'Erreur lors de la création des frais par défaut'
      };
    }

    const result = await handleResponse(response);

    // Log de succès
    console.log('✅ Frais par défaut créés avec succès:', result.data);

    return result;
  } catch (error) {
    console.error('❌ Erreur lors de la création des frais par défaut:', error);
    return {
      success: false,
      message: error.message || 'Erreur lors de la création des frais par défaut'
    };
  }
},



// 🆕 NOUVELLE MÉTHODE AJOUTÉE ICI
  /**
   * 🚀 Récupère toutes les données de suivi de paiement en UNE SEULE requête
   * @param {number} anneeId - ID de l'année scolaire
   * @param {number} trimestre - Numéro du trimestre (optionnel)
   * @returns {Promise} Promise avec les données complètes
   */
  // --------------------------------------------------------------------
  // Relances de scolarite par SMS
  // --------------------------------------------------------------------
  // `apercu` et `envoyer` appellent le meme calcul cote serveur : le nombre
  // annonce dans la confirmation est celui qui partira.
  apercuRelances: async (donnees) => {
    try {
      const response = await fetch(`${API_URL}/finance/relances/apercu`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(donnees)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur apercu des relances:', error);
      return { success: false, error: error.message };
    }
  },

  envoyerRelances: async (donnees) => {
    try {
      const response = await fetch(`${API_URL}/finance/relances`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(donnees)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur envoi des relances:', error);
      return { success: false, error: error.message };
    }
  },

  historiqueRelances: async (eleveId) => {
    try {
      const response = await fetch(`${API_URL}/finance/relances/eleve/${eleveId}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur historique des relances:', error);
      return { success: false, error: error.message };
    }
  },

  getSuiviPaiementComplet: async (anneeId, trimestre = null) => {
    try {
      let url = `${API_URL}/finance/suivi-paiement/complet?annee_id=${anneeId}`;
      if (trimestre) {
        url += `&trimestre=${trimestre}`;
      }

      console.log(`🚀 Appel API suivi paiement complet - URL: ${url}`);

      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();

      console.log(`✅ Données reçues - ${data.data?.length || 0} élèves`);

      return data;
    } catch (error) {
      console.error('❌ Erreur getSuiviPaiementComplet:', error);
      throw error;
    }
  },





  /**
   * Génère un PDF du suivi des paiements
   */

genererSuiviPaiementPDF: async (annee_scolaire_id, trimestre, mois_id, filtres) => {
  try {
    const response = await fetch(`${API_URL}/finance/suivi-paiement/pdf`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({
        annee_scolaire_id,
        trimestre,
        mois_id,
        filtres
        // ❌ PAS DE stats
        // ❌ PAS DE eleves
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
    }

    const blob = await response.blob();

    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'Suivi_Paiements.pdf';
    if (contentDisposition) {
      const matches = /filename="([^"]+)"/.exec(contentDisposition);
      if (matches && matches[1]) {
        filename = matches[1];
      }
    }

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    return { success: true, filename };
  } catch (error) {
    console.error('Erreur génération PDF:', error);
    throw error;
  }
},




/**
 * 🚀 Récupère les statistiques globales de paiements (toutes les classes)
 */
getStatsPaiementsGlobales: async (annee_id, mois_id) => {
  try {
    const response = await fetch(
      `${API_URL}/finance/stats-paiements-globales?annee_id=${annee_id}&mois_id=${mois_id}`,
      {
        headers: getAuthHeaders()
      }
    );

    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Erreur getStatsPaiementsGlobales:', error);
    throw error;
  }
},


/**
 * 🚀 Récupère les élèves d'une classe avec TOUS leurs paiements
 */
getElevesClasseAvecPaiements: async (classe_id, annee_id) => {
  try {
    const response = await fetch(
      `${API_URL}/finance/eleves-classe-avec-paiements?classe_id=${classe_id}&annee_id=${annee_id}`,
      {
        headers: getAuthHeaders()
      }
    );

    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Erreur getElevesClasseAvecPaiements:', error);
    throw error;
  }
},


 genererRecuIndividuel: async (eleveId, moisId, annee_scolaire_id, categorie) => {
    try {
      const response = await fetch(
        `${API_URL}/recus/individuel/${eleveId}/mois/${moisId}`,
        {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ annee_scolaire_id, categorie })
        }
      );

      if (!response.ok) {
        throw new Error('Erreur lors de la génération du reçu');
      }

      const blob = await response.blob();
      return blob;
    } catch (error) {
      console.error('Erreur génération reçu individuel:', error);
      throw error;
    }
  },



   /**
   * 📊 Récupère le chiffre d'affaires annuel avec détails par section
   * @param {number} anneeId - ID de l'année scolaire
   * @returns {Promise} Promise avec les données complètes du CA
   */
  getChiffreAffairesAnnuel: async (anneeId) => {
    try {
      console.log(`📊 Appel API chiffre d'affaires - Année: ${anneeId}`);

      const response = await fetch(
        `${API_URL}/finance/chiffre-affaires-annuel?annee_id=${anneeId}`,
        {
          method: 'GET',
          headers: getAuthHeaders()
        }
      );

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();

      console.log(`✅ Chiffre d'affaires reçu - ${data.data?.sections?.length || 0} sections`);

      return data;
    } catch (error) {
      console.error('❌ Erreur getChiffreAffairesAnnuel:', error);
      throw error;
    }
  },


  getDashboardPilotage: async (anneeId) => {
    const response = await fetch(`${API_URL}/finance/dashboard-pilotage?annee_id=${anneeId}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return response.json();
  }



},



  // Services pour les classes

classes: {


  getAll: async () => {
    try {
      const response = await fetch(`${API_URL}/classes`, {
        headers: getAuthHeaders()
      });
      console.log("RÃÂ©ponse brute de l'API classes:", response);
      const result = await handleResponse(response);
      console.log("DonnÃÂ©es aprÃÂ¨s handleResponse:", result);

      // On vÃÂ©rifie si les donnÃÂ©es sont dans result.data
      if (result && result.success && Array.isArray(result.data)) {
        return result.data;
      } else if (Array.isArray(result)) {
        return result;
      }

      console.warn('Format de donnÃÂ©es inattendu pour les classes:', result);
      return [];
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des classes:', error);
      return [];
    }
  },

  create: async (classeData) => {
    try {
      const response = await fetch(`${API_URL}/classes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
       body: JSON.stringify({
  nom: classeData.nom,
  section_id: classeData.section_id || null,
  frais_inscription: parseFloat(classeData.frais_inscription) || 0,
  frais_reinscription: parseFloat(classeData.frais_reinscription) || 0,
  frais_mensuel: parseFloat(classeData.frais_mensuel) || 0,
  frais_transport: parseFloat(classeData.frais_transport) || 0,
  frais_cantine: parseFloat(classeData.frais_cantine) || 0,
  taux_horaire: parseFloat(classeData.taux_horaire) || 0,
  seuil_eleves: classeData.seuil_eleves ? parseInt(classeData.seuil_eleves) : null, // ? Ajout propre
  sans_matiere: Boolean(classeData.sans_matiere),
  ordre: classeData.ordre || null // facultatif, selon ton API
})
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur crÃÂ©ation classe:', error);
      return {
        success: false,
        message: 'Erreur lors de la crÃÂ©ation de la classe',
        error: error.message
      };
    }
  },

  update: async (id, classeData) => {
    try {
      const response = await fetch(`${API_URL}/classes/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify((() => {
          const payload = {
            nom: classeData.nom,
            frais_inscription: parseFloat(classeData.frais_inscription) || 0,
            frais_reinscription: parseFloat(classeData.frais_reinscription) || 0,
            frais_mensuel: parseFloat(classeData.frais_mensuel) || 0,
            frais_transport: parseFloat(classeData.frais_transport) || 0,
            frais_cantine: parseFloat(classeData.frais_cantine) || 0,
            taux_horaire: parseFloat(classeData.taux_horaire) || 0,
            seuil_eleves: classeData.seuil_eleves ? parseInt(classeData.seuil_eleves, 10) : null,
            sans_matiere: Boolean(classeData.sans_matiere)
          };

          if (classeData.section_id !== undefined) {
            payload.section_id = classeData.section_id || null;
          }

          if (classeData.ordre !== undefined && classeData.ordre !== null && classeData.ordre !== '') {
            payload.ordre = parseInt(classeData.ordre, 10);
          }

          return payload;
        })())
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur mise ? jour classe:', error);
      return {
        success: false,
        message: 'Erreur lors de la mise ? jour de la classe',
        error: error.message
      };
    }
  },

  delete: async (id) => {
    try {
      const response = await fetch(`${API_URL}/classes/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur suppression classe:', error);
      return {
        success: false,
        message: 'Erreur lors de la suppression de la classe',
        error: error.message
      };
    }
  },

  getById: async (id) => {
    try {
      const response = await fetch(`${API_URL}/classes/${id}`, {
        headers: getAuthHeaders()
      });

      const data = await handleResponse(response);

      // VÃÂ©rifier le format de la rÃÂ©ponse
      if (data.success && data.data) {
        return data.data;
      }

      return data;
    } catch (error) {
      console.error("Erreur lors de la rÃÂ©cupÃÂ©ration des dÃÂ©tails de la classe:", error);
      return null;
    }
  },

  getEleves: async (classeId) => {
  try {
    const response = await fetch(`${API_URL}/classes/${classeId}/eleves`, {
      headers: getAuthHeaders()
    });
    const result = await handleResponse(response);

    // CORRECTION: Retourner directement le tableau des ÃÂ©lÃÂ¨ves
    if (result && result.success && Array.isArray(result.data)) {
      return result.data; // Retourner directement le tableau
    } else if (Array.isArray(result)) {
      return result; // Si c'est dÃÂ©jÃÂ  un tableau
    }

    console.warn('Format inattendu pour les ÃÂ©lÃÂ¨ves:', result);
    return []; // Retourner tableau vide en cas de problÃÂ¨me
  } catch (error) {
    console.error('Erreur rÃÂ©cupÃÂ©ration ÃÂ©lÃÂ¨ves classe:', error);
    return []; // Retourner tableau vide en cas d'erreur
  }
},


  getStats: async (classeId) => {
    try {
      const response = await fetch(`${API_URL}/classes/${classeId}/statistiques`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur rÃÂ©cupÃÂ©ration stats classe:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  getCapacity: async (classeId, anneeScolaireId = null) => {
    try {
      const params = new URLSearchParams();
      if (anneeScolaireId) params.append('annee_scolaire_id', anneeScolaireId);
      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(`${API_URL}/classes/${classeId}/capacite${query}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur recuperation capacite classe:', error);
      return {
        success: false,
        message: error.message,
        error: error.message
      };
    }
  },

  getAllByYear: async (annee_scolaire_id) => {
    try {
      const response = await fetch(`${API_URL}/classes/by-year?annee_scolaire_id=${annee_scolaire_id}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des classes par annÃÂ©e:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  getWithStats: async (annee_scolaire_id) => {
    try {
      let url = `${API_URL}/classes/with-stats`;
      if (annee_scolaire_id) {
        url += `?annee_scolaire_id=${annee_scolaire_id}`;
      }

      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des classes avec statistiques:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  changeClasse: async (eleveId, nouvelleClasseId) => {
    try {
      const response = await fetch(`${API_URL}/classes/${nouvelleClasseId}/change-classe`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          eleve_id: eleveId,
          nouvelle_classe_id: nouvelleClasseId
        })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur changement classe ÃÂ©lÃÂ¨ve:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // ============================================================================
  // Ã°ÂÂÂ NOUVELLES MÃÂTHODES MANQUANTES POUR ClassesModernes
  // ============================================================================

  /**
   * RÃÂ©cupÃÂ¨re les classes d'une section triÃÂ©es par ordre
   * @param {number|string} sectionId - ID de la section (ou 'null' pour sans section)
   * @returns {Promise<Object>} Classes de la section ordonnÃÂ©es
   */
  getBySection: async (sectionId, includeAnnexes = true) => {
    try {
      const params = new URLSearchParams({ include_annexes: includeAnnexes ? 'true' : 'false' });
      const response = await fetch(`${API_URL}/classes/sections/${sectionId}/ordered?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur recuperation classes par section:', error);
      return {
        success: false,
        message: 'Erreur lors de la recuperation des classes de la section',
        error: error.message
      };
    }
  },

  /**
   * DÃÂ©termine la classe suivante selon la logique scolaire
   * @param {number} classeId - ID de la classe actuelle
   * @returns {Promise<Object>} Informations sur la classe suivante
   */
  getClasseSuivante: async (classeId) => {
    try {
      const response = await fetch(`${API_URL}/classes/${classeId}/suivante`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur recherche classe suivante:', error);
      return {
        success: false,
        message: 'Erreur lors de la recherche de la classe suivante',
        error: error.message
      };
    }
  },

  /**
   * Calcule le prochain ordre disponible dans une section
   * @param {number|string} sectionId - ID de la section (ou 'null' pour sans section)
   * @returns {Promise<Object>} Prochain ordre disponible
   */
  getProchainOrdre: async (sectionId) => {
    try {
      const response = await fetch(`${API_URL}/classes/sections/${sectionId}/prochain-ordre`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur calcul prochain ordre:', error);
      return {
        success: false,
        message: 'Erreur lors du calcul du prochain ordre',
        error: error.message
      };
    }
  },

  /**
   * RÃÂ©organise les ordres des classes d'une section (drag & drop)
   * @param {number|string} sectionId - ID de la section
   * @param {Array} nouveauxOrdres - Tableau des nouveaux ordres [{id, ordre}]
   * @returns {Promise<Object>} RÃÂ©sultat de la rÃÂ©organisation
   */
  reorderSection: async (sectionId, nouveauxOrdres) => {
    try {
      const response = await fetch(`${API_URL}/classes/sections/${sectionId}/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          nouveaux_ordres: nouveauxOrdres
        })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur rÃÂ©organisation section:', error);
      return {
        success: false,
        message: 'Erreur lors de la rÃÂ©organisation de la section',
        error: error.message
      };
    }
  },

  /**
   * RÃÂ©organise automatiquement une section (nettoie les ordres)
   * @param {number|string} sectionId - ID de la section
   * @returns {Promise<Object>} RÃÂ©sultat de la rÃÂ©organisation automatique
   */
  reorganiserAuto: async (sectionId) => {
    try {
      const response = await fetch(`${API_URL}/classes/sections/${sectionId}/reorganiser-auto`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        }
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur rÃÂ©organisation automatique:', error);
      return {
        success: false,
        message: 'Erreur lors de la rÃÂ©organisation automatique',
        error: error.message
      };
    }
  },

  /**
   * RÃÂ©cupÃÂ¨re les dÃÂ©tails complets d'une classe
   * @param {number} classeId - ID de la classe
   * @returns {Promise<Object>} DÃÂ©tails complets de la classe
   */
  getDetailsComplets: async (classeId) => {
    try {
      const response = await fetch(`${API_URL}/classes/${classeId}/details-complets`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur rÃÂ©cupÃÂ©ration dÃÂ©tails complets:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * CrÃÂ©er une classe annexe
   * @param {number} classeId - ID de la classe de base
   * @returns {Promise<Object>} RÃÂ©sultat de la crÃÂ©ation d'annexe
   */
  createAnnexe: async (classeId) => {
    try {
      const response = await fetch(`${API_URL}/classes/${classeId}/create-annexe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        }
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur crÃÂ©ation classe annexe:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Mettre ÃÂ  jour le seuil d'une classe
   * @param {number} classeId - ID de la classe
   * @param {number} seuil - Nouveau seuil
   * @returns {Promise<Object>} RÃÂ©sultat de la mise ÃÂ  jour
   */
  updateSeuil: async (classeId, seuil) => {
    try {
      const response = await fetch(`${API_URL}/classes/${classeId}/seuil`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ seuil })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur mise a jour seuil:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * RÃÂ©cupÃÂ¨re le seuil global
   * @returns {Promise<Object>} Seuil global actuel
   */
  getSeuilGlobal: async () => {
    try {
      const response = await fetch(`${API_URL}/classes/parametres/seuil-global`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur rÃÂ©cupÃÂ©ration seuil global:', error);
      // Retourner une valeur par dÃÂ©faut si l'API n'existe pas encore
      return {
        success: true,
        data: { seuil_global: 25, auto_creation_annexe_seuil: false }
      };
    }
  },

  /**
   * Met ÃÂ  jour le seuil global
   * @param {number} seuil - Nouveau seuil global
   * @returns {Promise<Object>} RÃÂ©sultat de la mise ÃÂ  jour
   */
  updateSeuilGlobal: async (seuil, options = {}) => {
    try {
      const response = await fetch(`${API_URL}/classes/parametres/seuil-global`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          seuil,
          auto_creation_annexe_seuil: options.auto_creation_annexe_seuil
        })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur mise a jour seuil global:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * PrÃÂ©pare les donnÃÂ©es pour interface de crÃÂ©ation de classe
   * @param {number|string} sectionId - ID de la section
   * @returns {Promise<Object>} DonnÃÂ©es prÃÂ©parÃÂ©es pour le formulaire
   */
  preparerCreation: async (sectionId) => {
    try {
      // RÃÂ©cupÃÂ©rer classes existantes de la section
      const classesSection = await api.classes.getBySection(sectionId, false);

      // Calculer prochain ordre
      const prochainOrdre = await api.classes.getProchainOrdre(sectionId);

      return {
        success: true,
        data: {
          section_id: sectionId === 'null' ? null : parseInt(sectionId),
          classes_existantes: classesSection.success ? classesSection.data : [],
          ordre_suggere: prochainOrdre.success ? prochainOrdre.data.prochain_ordre : 1,
          total_classes: classesSection.success ? classesSection.data.length : 0
        }
      };
    } catch (error) {
      console.error('Erreur prÃÂ©paration crÃÂ©ation:', error);
      return {
        success: false,
        message: 'Erreur lors de la prÃÂ©paration de crÃÂ©ation',
        error: error.message
      };
    }
  },

  // ============================================================================
  // MÃÂTHODES UTILITAIRES POUR INTERFACE
  // ============================================================================

  /**
   * Analyse la progression possible d'une classe
   * @param {number} classeId - ID de la classe
   * @returns {Promise<Object>} Analyse complÃÂ¨te de progression
   */
  analyserProgression: async (classeId) => {
    try {
      // RÃÂ©cupÃÂ©rer infos classe actuelle
      const classeActuelle = await api.classes.getById(classeId);
      if (!classeActuelle) {
        return {
          success: false,
          message: 'Classe non trouvÃÂ©e'
        };
      }

      // RÃÂ©cupÃÂ©rer classe suivante
      const progressionResult = await api.classes.getClasseSuivante(classeId);

      // RÃÂ©cupÃÂ©rer classes de la mÃÂªme section
      const classesSection = await api.classes.getBySection(classeActuelle.section_id || 'null');

      return {
        success: true,
        data: {
          classe_actuelle: classeActuelle,
          progression: progressionResult.success ? progressionResult.data : null,
          classes_section: classesSection.success ? classesSection.data : [],
          analyse: {
            peut_progresser: progressionResult.success && progressionResult.data.classe_suivante !== null,
            type_progression: progressionResult.success ? progressionResult.data.type_progression : 'inconnue',
            est_derniere_section: progressionResult.success && progressionResult.data.type_progression === 'fin_parcours'
          }
        }
      };
    } catch (error) {
      console.error('Erreur analyse progression:', error);
      return {
        success: false,
        message: 'Erreur lors de l\'analyse de progression',
        error: error.message
      };
    }
  },

  /**
   * Valide la cohÃÂ©rence d'une progression de classes
   * @param {Array} classes - Liste des classes ÃÂ  valider
   * @returns {Object} RÃÂ©sultat de validation avec suggestions
   */
  validerProgression: (classes) => {
    if (!Array.isArray(classes) || classes.length === 0) {
      return {
        valide: true,
        problemes: [],
        suggestions: []
      };
    }

    const problemes = [];
    const suggestions = [];

    // Trier par ordre
    const classesTriees = [...classes].sort((a, b) => a.ordre - b.ordre);

    // VÃÂ©rifier continuitÃÂ© des ordres
    for (let i = 0; i < classesTriees.length; i++) {
      const ordreAttendu = i + 1;
      const ordreActuel = classesTriees[i].ordre;

      if (ordreActuel !== ordreAttendu) {
        problemes.push({
          type: 'ordre_discontinu',
          classe: classesTriees[i].nom,
          ordre_actuel: ordreActuel,
          ordre_attendu: ordreAttendu
        });
      }
    }

    // VÃÂ©rifier doublons d'ordre
    const ordres = classes.map(c => c.ordre);
    const ordresUniques = [...new Set(ordres)];
    if (ordres.length !== ordresUniques.length) {
      problemes.push({
        type: 'ordres_dupliques',
        message: 'Certaines classes ont le mÃÂªme ordre'
      });
    }

    // GÃÂ©nÃÂ©rer suggestions
    if (problemes.length > 0) {
      suggestions.push({
        action: 'reorganiser_auto',
        description: 'RÃÂ©organiser automatiquement les ordres de cette section'
      });
    }

    return {
      valide: problemes.length === 0,
      problemes,
      suggestions,
      total_classes: classes.length,
      ordre_max: Math.max(...ordres, 0)
    };
  },


// ============================================================================
// Ã°ÂÂÂ MÃÂTHODES POUR TAUX HORAIRES
// ============================================================================

/**
 * RÃÂ©cupÃÂ¨re toutes les classes avec leurs taux horaires pour employÃÂ©s
 * @returns {Promise<Object>} Classes avec taux horaires
 */
getClassesWithTauxHoraires: async () => {
  try {
    const response = await fetch(`${API_URL}/classes/taux-horaires`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur rÃÂ©cupÃÂ©ration classes avec taux horaires:', error);
    return {
      success: false,
      error: error.message
    };
  }
},

/**
 * Met ÃÂ  jour le taux horaire d'une classe
 * @param {number} classeId - ID de la classe
 * @param {number} tauxHoraire - Nouveau taux horaire
 * @returns {Promise<Object>} RÃÂ©sultat de la mise ÃÂ  jour
 */
updateTauxHoraire: async (classeId, tauxHoraire) => {
  try {
    const response = await fetch(`${API_URL}/classes/${classeId}/taux-horaire`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({ taux_horaire: tauxHoraire })
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur mise ÃÂ  jour taux horaire:', error);
    return {
      success: false,
      error: error.message
    };
  }
},

/**
 * Configuration en masse des taux horaires
 * @param {Array} tauxParClasses - [{classe_id, taux_horaire}]
 * @returns {Promise<Object>} RÃÂ©sultat de la mise ÃÂ  jour en masse
 */
updateTauxHorairesEnMasse: async (tauxParClasses) => {
  try {
    const response = await fetch(`${API_URL}/classes/taux-horaires/bulk-update`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({ taux_horaires: tauxParClasses })
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur mise ÃÂ  jour taux horaires en masse:', error);
    return {
      success: false,
      error: error.message
    };
  }
},





},


// Services pour les matiÃÂ¨res

matieres: {
  // Ã¢ÂÂ MÃÂTHODES EXISTANTES (CONSERVÃÂES)

  // RÃÂ©cupÃÂ©rer toutes les matiÃÂ¨res
  getAll: async (anneeId) => {  // Ã¢ÂÂ Ajouter le paramÃÂ¨tre anneeId
    try {
      // Ã¢ÂÂ Construire l'URL avec l'annÃÂ©e scolaire si fournie
      let url = `${API_URL}/matieres`;
      if (anneeId) {
        url += `?annee_scolaire_id=${anneeId}`;
      }

      console.log('[DEBUG API] URL matiÃÂ¨res:', url);

      const response = await fetch(url, {
        headers: getAuthHeaders()
      });

      const result = await handleResponse(response);
      console.log('[DEBUG API] MatiÃÂ¨res rÃÂ©cupÃÂ©rÃÂ©es:', result);

      // Ã¢ÂÂ IMPORTANT : Retourner toujours un format cohÃÂ©rent
      if (result && result.success !== undefined) {
        // Format: {success, data, message}
        return Array.isArray(result.data) ? result.data : [];
      } else if (Array.isArray(result)) {
        // Format: [...] directement
        return result;
      } else {
        // Fallback sÃÂ©curisÃÂ©
        console.warn('[API] Format inattendu pour matiÃÂ¨res:', result);
        return [];
      }
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des matiÃÂ¨res:', error);
      return []; // Ã¢ÂÂ TOUJOURS retourner un tableau
    }
  },

  // RÃÂ©cupÃÂ©rer les matiÃÂ¨res d'une classe spÃÂ©cifique
  getByClasse: async (classe_id, annee_scolaire_id) => {
    try {
      const response = await fetch(`${API_URL}/matieres/classe/${classe_id}?annee_scolaire_id=${annee_scolaire_id}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des matiÃÂ¨res de la classe:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // RÃÂ©cupÃÂ©rer les matiÃÂ¨res disponibles (non encore assignÃÂ©es ÃÂ  une classe)
  getDisponibles: async (classe_id, annee_scolaire_id) => {
    try {
      const response = await fetch(`${API_URL}/matieres/disponibles?classe_id=${classe_id}&annee_scolaire_id=${annee_scolaire_id}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des matiÃÂ¨res disponibles:', error);
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  },

  // Ã¢ÂÂ NOUVELLE MÃÂTHODE AJOUTÃÂE pour les affectations
  getByEnseignant: async (enseignant_id, annee_scolaire_id) => {
    try {
      const response = await fetch(`${API_URL}/matieres/enseignant/${enseignant_id}?annee_scolaire_id=${annee_scolaire_id}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des matiÃÂ¨res de l\'enseignant:', error);
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  },

  // CrÃÂ©er une nouvelle matiÃÂ¨re
  // Ã¢ÂÂ CORRIGÃÂ - MÃÂ©thode create complÃÂ¨te
  create: async (matiereData) => {
    try {
      console.log('Ã°ÂÂÂ¡ [API] DonnÃÂ©es reÃÂ§ues pour crÃÂ©ation:', matiereData);

      const payload = {
        nom: matiereData.nom,
        code: matiereData.code,
        coefficient: Number(matiereData.coefficient),
        classes_selectionnees: matiereData.classes_selectionnees || [], // Ã¢ÂÂ AJOUTÃÂ
        annee_scolaire_id: matiereData.annee_scolaire_id || 1 // Ã¢ÂÂ AJOUTÃÂ
      };

      console.log('Ã°ÂÂÂ¤ [API] Payload final envoyÃÂ©:', payload);

      const response = await fetch(`${API_URL}/matieres`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload) // Ã¢ÂÂ Envoyer le payload complet
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur crÃÂ©ation matiÃÂ¨re:', error);
      return {
        success: false,
        message: 'Erreur lors de la crÃÂ©ation de la matiÃÂ¨re',
        error: error.message
      };
    }
  },

  // Mettre ÃÂ  jour une matiÃÂ¨re
  // Ã¢ÂÂ CORRIGÃÂ - MÃÂ©thode update complÃÂ¨te
  update: async (id, matiereData) => {
    try {
      console.log('Ã°ÂÂÂ¡ [API] DonnÃÂ©es reÃÂ§ues pour mise ÃÂ  jour:', matiereData);

      const payload = {
        nom: matiereData.nom,
        code: matiereData.code,
        coefficient: Number(matiereData.coefficient),
        classes_selectionnees: matiereData.classes_selectionnees || [], // Ã¢ÂÂ AJOUTÃÂ
        annee_scolaire_id: matiereData.annee_scolaire_id || 1 // Ã¢ÂÂ AJOUTÃÂ
      };

      console.log('Ã°ÂÂÂ¤ [API] Payload update envoyÃÂ©:', payload);

      const response = await fetch(`${API_URL}/matieres/${id}`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload) // Ã¢ÂÂ Envoyer le payload complet
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur mise ÃÂ  jour matiÃÂ¨re:', error);
      return {
        success: false,
        message: 'Erreur lors de la mise ÃÂ  jour de la matiÃÂ¨re',
        error: error.message
      };
    }
  },

  // Supprimer une matiÃÂ¨re
  delete: async (id) => {
    const response = await fetch(`${API_URL}/matieres/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // ============================================================================
  // NOUVELLES MÃÂTHODES D'ASSOCIATION MATIÃÂRE-CLASSE
  // ============================================================================

  associations: {
    // Ajouter une association matière-classe avec coefficient optionnel
    add: async (matiereId, classeId, anneeId, coefficient = null) => {  // ⬅️ AJOUTER coefficient
      try {
        console.log(`🔗 [API] Ajout association: matière ${matiereId} → classe ${classeId} (coef: ${coefficient || 'défaut'})`);

        const response = await fetch(`${API_URL}/matieres/${matiereId}/associations`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            classe_id: classeId,
            annee_scolaire_id: anneeId,
            coefficient: coefficient  // ⬅️ AJOUTER
          })
        });

        return handleResponse(response);
      } catch (error) {
        console.error('❌ [API] Erreur ajout association:', error);
        return {
          success: false,
          message: 'Erreur lors de l\'ajout de l\'association',
          error: error.message
        };
      }
    },

    // Supprimer une association matière-classe
    remove: async (matiereId, classeId, anneeId) => {
      try {
        console.log(`🗑️ [API] Suppression association: matière ${matiereId} → classe ${classeId}`);
        const query = anneeId ? `?annee_scolaire_id=${encodeURIComponent(anneeId)}` : '';
        const response = await fetch(`${API_URL}/matieres/${matiereId}/associations/${classeId}${query}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });

        return handleResponse(response);
      } catch (error) {
        console.error('❌ [API] Erreur suppression association:', error);
        return {
          success: false,
          message: 'Erreur lors de la suppression de l\'association',
          error: error.message
        };
      }
    },

    // Récupérer les associations d'une matière
    getByMatiere: async (matiereId, anneeId) => {
      try {
        const url = anneeId
          ? `${API_URL}/matieres/${matiereId}/associations?annee_scolaire_id=${anneeId}`
          : `${API_URL}/matieres/${matiereId}/associations`;

        const response = await fetch(url, {
          headers: getAuthHeaders()
        });

        const result = await handleResponse(response);
        return result.success ? result.data : [];
      } catch (error) {
        console.error('❌ [API] Erreur récupération associations:', error);
        return [];
      }
    },

    // ⬅️ NOUVELLE MÉTHODE : Modifier uniquement le coefficient
    updateCoefficient: async (matiereId, classeId, anneeId, coefficient) => {
      try {
        console.log(`🔧 [API] Modification coefficient: matière ${matiereId} → classe ${classeId}: ${coefficient}`);

        const response = await fetch(`${API_URL}/matieres/${matiereId}/associations/${classeId}/coefficient`, {
          method: 'PUT',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            annee_scolaire_id: anneeId,
            coefficient: coefficient
          })
        });

        return handleResponse(response);
      } catch (error) {
        console.error('❌ [API] Erreur modification coefficient:', error);
        return {
          success: false,
          message: 'Erreur lors de la modification du coefficient',
          error: error.message
        };
      }
    }
  },


  // ============================================================================
  // MÃÂTHODES EXISTANTES (CONSERVÃÂES POUR COMPATIBILITÃÂ)
  // ============================================================================

  // Assigner une matiÃÂ¨re ÃÂ  des classes
  assignerAuxClasses: async (matiere_id, classes_ids, annee_scolaire_id) => {
    try {
      const response = await fetch(`${API_URL}/matieres/${matiere_id}/assigner-classes`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          classes_ids: classes_ids,
          annee_scolaire_id: annee_scolaire_id
        })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de l\'assignation aux classes:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Retirer une matiÃÂ¨re d'une classe
  retirerDeClasse: async (matiere_id, classe_id, annee_scolaire_id) => {
    try {
      const response = await fetch(`${API_URL}/matieres/${matiere_id}/retirer-classe/${classe_id}?annee_scolaire_id=${annee_scolaire_id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors du retrait de la classe:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Obtenir les statistiques d'une matiÃÂ¨re (combien de classes, d'enseignants, etc.)
  getStatistiques: async (matiere_id, annee_scolaire_id) => {
    try {
      const response = await fetch(`${API_URL}/matieres/${matiere_id}/statistiques?annee_scolaire_id=${annee_scolaire_id}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des statistiques:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Dupliquer les affectations matiÃÂ¨re-classe d'une annÃÂ©e vers une autre
  dupliquerAffectations: async (annee_source_id, annee_cible_id) => {
    try {
      const response = await fetch(`${API_URL}/matieres/dupliquer-affectations`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          annee_source_id: annee_source_id,
          annee_cible_id: annee_cible_id
        })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la duplication des affectations:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
},


//Services pour les eleves
  eleves: {
  // Situation de réduction d'un élève : ce qu'il paie aujourd'hui et pourquoi.
  getSituationReduction: async (eleveId, anneeScolaireId) => {
    try {
      const query = anneeScolaireId ? `?annee_scolaire_id=${anneeScolaireId}` : '';
      const response = await fetch(`${API_URL}/eleves/${eleveId}/situation-reduction${query}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur situation réduction élève:', error);
      throw error;
    }
  },

  // Mois d'effet d'un élève, avec la raison : les mois déjà engagés qui le
  // repoussent. Sans cette raison, la date affichée reste une décision opaque.
  getMoisEffet: async (eleveId, anneeScolaireId) => {
    try {
      const query = anneeScolaireId ? `?annee_scolaire_id=${anneeScolaireId}` : '';
      const response = await fetch(`${API_URL}/eleves/${eleveId}/mois-effet${query}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error("Erreur mois d'effet élève:", error);
      throw error;
    }
  },

  // Journal des modifications financières : lu depuis les demandes spéciales,
  // qui conservent déjà l'avant, l'après, les auteurs et les horodatages.
  getJournalModifications: async (eleveId) => {
    try {
      const response = await fetch(`${API_URL}/eleves/${eleveId}/journal-modifications`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur journal modifications élève:', error);
      throw error;
    }
  },

  // Changement de classe : passe par le Centre de Support, comme les autres
  // gestes à effet financier.
  demanderChangementClasse: async (eleveId, payload) => {
    try {
      const response = await fetch(`${API_URL}/eleves/${eleveId}/demande-changement-classe`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur demande changement de classe:', error);
      throw error;
    }
  },

  // Frais de début d'année : lus depuis frais_fixes, modifiés via le Centre de
  // Support et non plus par la route d'ajustement directe.
  getFraisDebutAnnee: async (eleveId, anneeScolaireId) => {
    try {
      const query = anneeScolaireId ? `?annee_scolaire_id=${anneeScolaireId}` : '';
      const response = await fetch(`${API_URL}/eleves/${eleveId}/frais-debut-annee${query}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error("Erreur frais de début d'année:", error);
      throw error;
    }
  },

  demanderModificationFraisDebutAnnee: async (eleveId, payload) => {
    try {
      const response = await fetch(`${API_URL}/eleves/${eleveId}/demande-modification-frais-debut-annee`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur demande modification frais:', error);
      throw error;
    }
  },

  // Demande de modification de réduction : jamais d'écriture directe, le Centre
  // de Support tranche.
  demanderModificationReduction: async (eleveId, payload) => {
    try {
      const response = await fetch(`${API_URL}/eleves/${eleveId}/demande-modification-reduction`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur demande modification réduction:', error);
      throw error;
    }
  },

  // ✅ Créer un élève
  create: async (eleveData) => {
    try {
      const response = await fetch(`${API_URL}/eleves`, {
        method: 'POST',
        headers: getAuthHeaders(),  // ✅ SIMPLIFIÉ
        body: JSON.stringify(eleveData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur création élève:', error);
      throw error;
    }
  },

  // ✅ Récupérer tous les élèves
  getAll: async (annee_scolaire_id) => {
    try {
      const response = await fetch(`${API_URL}/eleves?annee_scolaire_id=${annee_scolaire_id}`, {
        headers: getAuthHeaders()  // ✅ CORRIGÉ
      });
      return handleResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Effectif de l'année qui précède `annee_scolaire_id` : le « sur combien »
  // des réinscrits, et le nombre de réinscrits partis depuis.
  // `data` vaut null pour la première année de l'école.
  getEffectifAnneePrecedente: async (annee_scolaire_id) => {
    const response = await fetch(
      `${API_URL}/eleves/effectif-annee-precedente?annee_scolaire_id=${encodeURIComponent(annee_scolaire_id)}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  // ✅ Exporter la liste des élèves en PDF
  exportPDF: async ({ annee_scolaire_id, section_id = null, classe_ids = [] } = {}) => {
    const params = new URLSearchParams();
    if (annee_scolaire_id) params.append('annee_scolaire_id', annee_scolaire_id);
    if (section_id) params.append('section_id', section_id);
    if (classe_ids.length > 0) params.append('classe_ids', classe_ids.join(','));

    return fetch(`${API_URL}/eleves/export/pdf?${params.toString()}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
  },

  // ✅ Récupérer un élève par ID
  getById: async (id) => {
    const response = await fetch(`${API_URL}/eleves/${id}`, {
      headers: getAuthHeaders()  // ✅ CORRIGÉ
    });
    return handleResponse(response);
  },

  // ✅ Mettre à jour un élève
  getInterSiteTransferTrace: async (id) => {
    const response = await fetch(`${API_URL}/eleves/${id}/intersite-transfer`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  update: async (id, eleveData) => {
    try {
      console.log("API: Type de dateNaissance:", typeof eleveData.dateNaissance);
      console.log("API: Valeur dateNaissance:", eleveData.dateNaissance);

      const response = await fetch(`${API_URL}/eleves/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),  // ✅ CORRIGÉ
        body: JSON.stringify(eleveData)
      });

      const result = await handleResponse(response);
      console.log("API: Date dans la réponse:", result.dateNaissance || result.date_naissance);

      return result;
    } catch (error) {
      console.error('API: Erreur lors de la mise à jour:', error);
      throw error;
    }
  },

  // ✅ Supprimer un élève
  delete: async (id) => {
    const response = await fetch(`${API_URL}/eleves/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()  // ✅ CORRIGÉ
    });
    return handleResponse(response);
  },

  // ✅ Récupérer les raisons de radiation
  getRadiationReasons: async () => {
    const response = await fetch(`${API_URL}/radiation/reasons`, {
      headers: getAuthHeaders()  // ✅ CORRIGÉ
    });
    return handleResponse(response);
  },

  // Les élèves dont une demande de radiation OU de restauration court déjà, rendus
  // en deux ensembles indexés par identifiant d'élève : { radiation, restauration }.
  //
  // Sert à MONTRER l'attente dans les listes : sans repère, l'élève reste affiché
  // comme si rien ne s'était passé, et on reclique — c'est ainsi qu'un même élève a
  // porté quatre demandes de radiation identiques le 05/10/2026. La restauration a
  // le même défaut en miroir, dans la liste des radiés.
  getDemandesEnAttente: async () => {
    const response = await fetch(`${API_URL}/radiation/demandes-en-attente`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  previewRadiation: async (id, radiationData) => {
    const response = await fetch(`${API_URL}/radiation/preview/${id}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(radiationData || {})
    });
    return handleResponse(response);
  },

  // ✅ Radier un élève (déjà correct)
  radiate: async (id, radiationData) => {
    const callId = Math.random().toString(36).substr(2, 6);
    console.log(`🔥 [API-RADIATE-${callId}] ===== DÉBUT RADIATION ÉLÈVE ${id} =====`);

    try {
      if (!id) throw new Error('ID élève manquant');
      if (!radiationData) throw new Error('Données de radiation manquantes');
      if (!radiationData.reason_id) throw new Error('ID de raison de radiation manquant');
      if (!radiationData.date_radiation) throw new Error('Date de radiation manquante');
      if (!radiationData.annee_scolaire_id) throw new Error('ID année scolaire manquant');

      const headers = getAuthHeaders();

      if (!headers.Authorization) {
        throw new Error('Token d\'authentification manquant');
      }

      const url = `${API_URL}/radiation/radiate/${id}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: headers,  // ✅ DÉJÀ CORRECT
        body: JSON.stringify(radiationData)
      });

      if (!response.ok) {
        let errorDetail = '';
        try {
          errorDetail = await response.text();
        } catch (parseError) {}

        switch (response.status) {
          case 401:
            throw new Error('Non authentifié - Token invalide ou expiré');
          case 403:
            throw new Error('Accès interdit - Droits insuffisants');
          case 404:
            throw new Error(`Élève avec l'ID ${id} non trouvé`);
          case 409: {
              // ⚠️ LE REFUS DE DOUBLON DOIT SE LIRE, PAS S'AFFICHER « Erreur HTTP 409 ».
              // Depuis le 05/10/2026, le serveur refuse une seconde demande tant que
              // la première est en attente, et son message porte le NUMÉRO et la DATE
              // de celle qui court déjà. Le laisser tomber dans le cas par défaut
              // aurait rendu ce refus aussi opaque que le silence qu'il remplace.
              let messageDoublon = 'Une demande est déjà en attente pour cet élève.';
              try {
                const corps = JSON.parse(errorDetail);
                if (corps && corps.message) messageDoublon = corps.message;
              } catch (parseError) {
                // Corps non lisible : on garde le message générique, qui dit déjà
                // l'essentiel — une demande est en cours.
              }
              const erreurDoublon = new Error(messageDoublon);
              erreurDoublon.statusCode = 409;
              throw erreurDoublon;
            }
            case 422:
            throw new Error(`Données invalides: ${errorDetail || 'Vérifiez les données'}`);
          case 500:
            throw new Error(`Erreur serveur: ${errorDetail || 'Contactez l\'admin'}`);
          default:
            throw new Error(`Erreur HTTP ${response.status}: ${errorDetail || 'Erreur inconnue'}`);
        }
      }

      const result = await handleResponse(response);
      return result;

    } catch (error) {
      console.error(`❌ [API-RADIATE-${callId}] Erreur:`, error);
      throw error;
    }
  },

  // ✅ Version avec retry automatique
  radiateWithRetry: async (id, radiationData, maxRetries = 2) => {
    let lastError;

    for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
      try {
        console.log(`🔄 [RADIATE-RETRY] Tentative ${attempt}/${maxRetries + 1}`);
        const result = await api.eleves.radiate(id, radiationData);
        return result;
      } catch (error) {
        lastError = error;

        const shouldRetry = error.message.includes('500') ||
                           error.message.includes('Network') ||
                           error.message.includes('timeout');

        if (shouldRetry && attempt <= maxRetries) {
          const delay = 1000 * attempt;
          await new Promise(resolve => setTimeout(resolve, delay));
        } else {
          break;
        }
      }
    }

    throw lastError;
  },

  // ✅ Récupérer les élèves radiés
  getRadies: async (annee_scolaire_id = null) => {
    const query = annee_scolaire_id ? `?annee_scolaire_id=${annee_scolaire_id}` : '';
    const response = await fetch(`${API_URL}/radiation/eleves-radies${query}`, {
      headers: getAuthHeaders()  // ✅ CORRIGÉ
    });
    return handleResponse(response);
  },

  // Preparer la restauration d'un eleve radie
  previewRestore: async (id, restoreData = {}) => {
    const response = await fetch(`${API_URL}/radiation/restore-preview/${id}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(restoreData || {})
    });
    return handleResponse(response);
  },

  // Restaurer un eleve radie
  restore: async (id, restoreData = {}) => {
    const response = await fetch(`${API_URL}/radiation/restore/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(restoreData || {})
    });
    return handleResponse(response);
  },

  // ✅ Supprimer définitivement un élève
  deletePermanent: async (id) => {
    const response = await fetch(`${API_URL}/radiation/permanent/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()  // ✅ CORRIGÉ
    });
    return handleResponse(response);
  },

  // ✅ Upload photo d'élève
  uploadPhoto: async (eleveId, photoFile) => {
    try {
      if (!(photoFile instanceof FormData)) {
        throw new Error('Format incorrect pour l\'upload de photo');
      }
      const response = await fetch(`${API_URL}/eleves/${eleveId}/photo`, {
        method: 'POST',
        headers: getFormDataHeaders(),
        body: photoFile
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur upload photo:', error);
      throw error;
    }
  },

  // ✅ Mettre à jour la photo
  updatePhoto: async (eleveId, photoFile) => {
    try {
      if (!(photoFile instanceof FormData)) {
        throw new Error('Format incorrect pour l\'upload de photo');
      }
      const response = await fetch(`${API_URL}/eleves/${eleveId}/photo`, {
        method: 'PUT',
        headers: getFormDataHeaders(),
        body: photoFile
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur mise à jour photo:', error);
      throw error;
    }
  },

  // ✅ Supprimer la photo
  deletePhoto: async (eleveId) => {
    try {
      const response = await fetch(`${API_URL}/eleves/${eleveId}/photo`, {
        method: 'DELETE',
        headers: getAuthHeaders()  // ✅ CORRIGÉ
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur suppression photo:', error);
      throw error;
    }
  },

  // ✅ Récupérer les documents (déjà correct)
  getDocuments: async (eleveId) => {
    try {
      const response = await fetch(`${API_URL}/eleves/${eleveId}/documents`, {
        headers: getAuthHeaders()  // ✅ DÉJÀ CORRECT
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération documents:', error);
      return { success: false, error: error.message };
    }
  },

  // ⚠️ CES TROIS FONCTIONS RELÈVENT, comme `uploadPhoto` au-dessus.
  //
  // Leur `catch` rendait `{ success: false, error }` : un 500, un 413 « fichier
  // trop volumineux », un refus de multer et une coupure réseau devenaient donc
  // indiscernables d'une réussite pour tout appelant qui ne lit pas le résultat.
  // C'est ce qui faisait annoncer « Inscription réussie » à l'écran d'inscription
  // sans qu'aucune pièce ne soit enregistrée.
  //
  // ⚠️ RELEVER NE SUFFIT PAS : `handleResponse` (ligne 97) rend un objet
  // `{ success: false, message }` pour 400, 403 et 409 SANS lever. Un appelant
  // doit donc vérifier `resultat.success === true`, et pas seulement s'entourer
  // d'un try/catch. NE PAS remettre un `return { success: false }` ici.
  uploadIdentityDoc: async (eleveId, formData) => {
    try {
      const response = await fetch(`${API_URL}/eleves/${eleveId}/documents/identite`, {
        method: 'POST',
        headers: getFormDataHeaders(),
        body: formData
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur upload document identité:', error);
      throw error;
    }
  },

  // ✅ Upload diplômes
  uploadDiplomas: async (eleveId, formData) => {
    try {
      const response = await fetch(`${API_URL}/eleves/${eleveId}/documents/diplomes`, {
        method: 'POST',
        headers: getFormDataHeaders(),
        body: formData
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur upload diplômes:', error);
      throw error;
    }
  },

  // ✅ Upload autres documents
  uploadOtherDocs: async (eleveId, formData) => {
    try {
      const response = await fetch(`${API_URL}/eleves/${eleveId}/documents/autres`, {
        method: 'POST',
        headers: getFormDataHeaders(),
        body: formData
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur upload autres documents:', error);
      throw error;
    }
  },

  // ✅ Supprimer un document (déjà correct)
  deleteDocument: async (eleveId, documentId) => {
    try {
      const response = await fetch(`${API_URL}/eleves/${eleveId}/documents/${documentId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()  // ✅ DÉJÀ CORRECT
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur suppression document:', error);
      return { success: false, error: error.message };
    }
  },


  // 🆕 NOUVELLES MÉTHODES POUR LE PRORATA

  // ✅ Rechercher des élèves (pour modal prorata)
  rechercher: async (term, anneeScolaireId) => {
    try {
      const response = await fetch(`${API_URL}/eleves/rechercher?term=${encodeURIComponent(term)}&annee_scolaire_id=${anneeScolaireId}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur recherche élèves:', error);
      throw error;
    }
  },

  // ✅ Récupérer les infos de paiement d'un élève pour un mois
  getPaiementMois: async (eleveId, moisId, anneeScolaireId) => {
    try {
      const response = await fetch(`${API_URL}/eleves/${eleveId}/paiement-mois/${moisId}?annee_scolaire_id=${anneeScolaireId}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération paiement mois:', error);
      throw error;
    }
  },

  // ✅ Appliquer le prorata pour un élève
  appliquerProrata: async (eleveId, data) => {
    try {
      const response = await fetch(`${API_URL}/eleves/${eleveId}/prorata`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur application prorata élève:', error);
      throw error;
    }
  },


  // 🆕 Annuler le prorata d'un élève
  annulerProrata: async (eleveId, moisId, anneeScolaireId) => {
    try {
      const response = await fetch(
        `${API_URL}/eleves/${eleveId}/prorata/${moisId}?annee_scolaire_id=${anneeScolaireId}`,
        {
          method: 'DELETE',
          headers: getAuthHeaders()
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur annulation prorata élève:', error);
      throw error;
    }
  },

  // ------------------------------------------------------------------
  // CAMPAGNE PHOTOS DES ÉLÈVES — lecture seule
  // ------------------------------------------------------------------
  // Deux appels, aucune écriture : la page de campagne ne pose pas de
  // photo. L'envoi passe par les routes qui existaient déjà — côté école
  // POST/PUT /eleves/:id/photo, côté famille POST /famille/enfants/:lienId/photo.

  // L'avancement complet : totaux de l'école, une ligne par classe (les
  // classes les plus en retard d'abord, c'est le serveur qui trie), et la
  // liste des élèves avec l'origine de leur photo.
  //
  // Renvoie l'objet d'erreur au lieu de le lancer, contrairement à ses
  // voisines de cette section : l'écran doit pouvoir afficher un BANDEAU
  // d'échec plutôt qu'une campagne à zéro. Le piège est propre à cet écran —
  // « aucune photo » est justement le résultat réel attendu, donc un échec
  // silencieux passerait pour une donnée juste. `handleResponse` renvoie
  // déjà { success: false } sur un 403, ce qui fait remonter le refus de
  // permission dans ce même bandeau.
  getCampagnePhotos: async () => {
    try {
      const response = await fetch(`${API_URL}/eleves/campagne-photos`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur campagne photos élèves:', error);
      return { success: false, error: error.message };
    }
  },

  // La feuille que l'enseignant emporte. Sans `classeId`, toutes les classes.
  //
  // Le nom du fichier est celui que le serveur a daté : il le calcule en SQL,
  // là où le fuseau de l'école fait foi. Un nom recalculé ici porterait la date
  // du navigateur, et la veille une partie de la soirée.
  downloadCampagnePhotosPDF: async (classeId) => {
    try {
      const query = classeId ? `?classe=${encodeURIComponent(classeId)}` : '';
      const response = await fetch(`${API_URL}/eleves/campagne-photos/pdf${query}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Erreur génération PDF');

      const disposition = response.headers.get('Content-Disposition');
      let filename = 'Photos_eleves.pdf';
      if (disposition && disposition.includes('filename=')) {
        filename = disposition.split('filename=')[1].replace(/"/g, '').trim() || filename;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error('Erreur export campagne photos:', error);
      return { success: false, error: error.message };
    }
  }



},


  // Services pour les enseignants
  enseignants: {
    getAll: async () => {
      const response = await fetch(`${API_URL}/enseignants`);
      return handleResponse(response);
    },

    getById: async (id) => {
      const response = await fetch(`${API_URL}/enseignants/${id}`);
      return handleResponse(response);
    },

    create: async (enseignantData) => {
      try {
        console.log('DonnÃÂ©es reÃÂ§ues dans l\'API:', enseignantData);

        const jsonData = JSON.stringify(enseignantData);
        console.log('DonnÃÂ©es aprÃÂ¨s JSON.stringify:', jsonData);

        const response = await fetch(`${API_URL}/enseignants`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: jsonData
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('RÃÂ©ponse serveur en erreur:', {
            status: response.status,
            statusText: response.statusText,
            errorData
          });
          throw new Error(errorData.message || 'Erreur lors de la crÃÂ©ation');
        }

        return response.json();
      } catch (error) {
        console.error('Erreur dÃÂ©taillÃÂ©e lors de la crÃÂ©ation:', {
          message: error.message,
          stack: error.stack,
          originalError: error
        });
        throw error;
      }
    },

    update: async (id, enseignantData) => {
      const response = await fetch(`${API_URL}/enseignants/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nom: enseignantData.nom,
          prenom: enseignantData.prenom,
          email: enseignantData.email,
          telephone: enseignantData.telephone,
          classe_id: Number(enseignantData.classe_id),
          matieres: enseignantData.matieres.map(Number)
        })
      });
      return handleResponse(response);
    },

    delete: async (id) => {
      try {
        console.log('Tentative de suppression de l\'enseignant:', id);
        const response = await fetch(`${API_URL}/enseignants/${id}`, {
          method: 'DELETE'
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Erreur lors de la suppression');
        }

        return response.json();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        throw error;
      }
    },

    getMatieres: async (id) => {
      const response = await fetch(`${API_URL}/enseignants/${id}/matieres`);
      return handleResponse(response);
    },


    getEnseignantsAvecPointages: async (mois, annee, anneeScolaireId = null) => {
      try {
        const selectedYearId = anneeScolaireId || localStorage.getItem('annee_scolaire_id');
        const query = selectedYearId ? `?annee_scolaire_id=${encodeURIComponent(selectedYearId)}` : '';
        const response = await fetch(
          `${API_URL}/paiements/enseignants/pointages/${mois}/${annee}${query}`,
          {
            headers: getAuthHeaders()
          }
        );
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur récupération enseignants avec pointages:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },


    // 🆕 AJOUTE CETTE NOUVELLE FONCTION
 updateRetenuePointage: async (pointageId, data) => {
  console.log('🌐 ========== API CALL: updateRetenuePointage ==========');
  console.log('🎯 Pointage ID:', pointageId);
  console.log('📦 Data:', JSON.stringify(data, null, 2));

  try {
    const url = `${API_URL}/pointages/enseignants/retenue-absence/${pointageId}`;
    console.log('🔗 URL complète:', url);
    console.log('🔑 Headers:', {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    });
    console.log('📤 Body:', JSON.stringify(data));

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(data)
    });

    console.log('📥 Status:', response.status);
    console.log('📥 Status Text:', response.statusText);

    const result = await handleResponse(response);
    console.log('✅ Résultat final:', JSON.stringify(result, null, 2));
    console.log('🌐 ========== FIN API CALL ==========');

    return result;
  } catch (error) {
    console.error('💥 Exception dans updateRetenuePointage:', error);
    console.error('   - Message:', error.message);
    console.error('   - Stack:', error.stack);
    console.log('🌐 ========== FIN API CALL (ERREUR) ==========');

    return {
      success: false,
      error: error.message
    };
  }
}

  },


  // Services pour la scolaritÃÂ©
 scolarite: {


  // ========================================
  // MÉTHODES POUR LE COLLÈGE/LYCÉE
  // ========================================

  // Notes
  getNotes: async (classeId, trimestre, annee_scolaire_id) => {
    try {
      const response = await fetch(
        `${API_URL}/scolarite/notes/classe/${classeId}?trimestre=${trimestre}&annee_scolaire_id=${annee_scolaire_id}`,
        { headers: getAuthHeaders() }  // ✅ Déjà OK
      );
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  getNotesByEleve: async (eleveId, trimestre, annee_scolaire_id) => {
    try {
      const response = await fetch(
        `${API_URL}/scolarite/notes/${eleveId}?trimestre=${trimestre}&annee_scolaire_id=${annee_scolaire_id}`,
        { headers: getAuthHeaders() }  // ✅ Déjà OK
      );
      const result = await handleResponse(response);
      return {
        success: true,
        data: result
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des notes:', error);
      return {
        success: false,
        error: 'Erreur lors de la récupération des notes'
      };
    }
  },

  saveNotes: async (notesData) => {
    try {
      const response = await fetch(`${API_URL}/scolarite/notes/bulk`, {
        method: 'POST',
        headers: getAuthHeaders(),  // ✅ Déjà OK
        body: JSON.stringify(notesData)
      });
      return handleResponse(response);
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  generateBulletin: async (params) => {
    try {
      const response = await fetch(
        `${API_URL}/scolarite/bulletin/generate`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),  // ✅ Déjà OK
            'Accept': 'application/pdf'
          },
          body: JSON.stringify({
            eleve_id: params.eleve_id,
            classe_id: params.classe_id,
            trimestre: params.trimestre,
            annee_scolaire_id: params.annee_scolaire_id,
            notes: params.notes
          })
        }
      );

      if (!response.ok) {
        throw new Error('Erreur lors de la génération du bulletin');
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/pdf')) {
        return await response.blob();
      } else {
        throw new Error('Le format de réponse n\'est pas un PDF');
      }
    } catch (error) {
      console.error('Erreur lors de la génération du bulletin:', error);
      throw error;
    }
  },

  deleteNote: async (eleveId, matiereId, trimestre) => {
    const response = await fetch(
      `${API_URL}/scolarite/notes/${eleveId}/${matiereId}/${trimestre}`,
      {
        method: 'DELETE',
        headers: getAuthHeaders()  // ⬅️ AJOUTÉ
      }
    );
    return handleResponse(response);
  },

  // ✅ CORRIGÉ : Matières avec authentification
  getMatieres: async (classeId = null) => {
    try {
      const url = classeId
        ? `${API_URL}/scolarite/matieres?classe_id=${classeId}`
        : `${API_URL}/scolarite/matieres`;

      const response = await fetch(url, {
        headers: getAuthHeaders()  // ⬅️ AJOUTÉ ICI !
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la récupération des matières:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Bulletins
  getBulletinEleve: async (eleveId, trimestre, annee_scolaire_id) => {
    try {
      const response = await fetch(
        `${API_URL}/scolarite/notes/eleve/${eleveId}?trimestre=${trimestre}&annee_scolaire_id=${annee_scolaire_id}`,
        {
          headers: getAuthHeaders()  // ✅ Déjà OK
        }
      );
      const result = await handleResponse(response);
      console.log('Réponse du bulletin:', result);
      return {
        success: true,
        data: result,
        url: `${API_URL}/scolarite/bulletin/${eleveId}/download?trimestre=${trimestre}&annee_scolaire_id=${annee_scolaire_id}`
      };
    } catch (error) {
      console.error('Erreur lors de la récupération du bulletin:', error);
      return {
        success: false,
        error: 'Erreur lors de la récupération du bulletin'
      };
    }
  },



 // ✅ MÉTHODE OPTIMISÉE - Charge TOUT en 1 seule requête
getNotesComplet: async (classeId, annee_scolaire_id) => {
  try {
    console.log('📦 [API] Appel getNotesComplet:', { classeId, annee_scolaire_id });

    const response = await fetch(
      `${API_URL}/scolarite/notes/classe/${classeId}/complet?annee_scolaire_id=${annee_scolaire_id}`,
      { headers: getAuthHeaders() }
    );

    const result = await handleResponse(response);
    console.log('📦 [API] Données complètes reçues');

    return result;
  } catch (error) {
    console.error('❌ [API] Erreur getNotesComplet:', error);
    return {
      success: false,
      error: error.message
    };
  }
},



  // Sauvegarder les appréciations trimestrielles
  saveAppreciationsTrimestrielles: async (appreciations) => {
    try {
      console.log('💬 [API] Sauvegarde appréciations trimestrielles:', appreciations);

      const response = await fetch(`${API_URL}/scolarite/appreciations-trimestrielles/bulk`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ appreciations })
      });

      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur saveAppreciationsTrimestrielles:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },



  // Récupérer les stats de classe (moyenne haute/basse + noms élèves)



  // Récupérer les appréciations trimestrielles d'un élève
  getAppreciationsTrimestrielles: async (eleveId, trimestre, annee_scolaire_id) => {
    try {
      console.log('💬 [API] Récupération appréciations élève:', { eleveId, trimestre, annee_scolaire_id });

      const response = await fetch(
        `${API_URL}/scolarite/appreciations-trimestrielles/${eleveId}?trimestre=${trimestre}&annee_scolaire_id=${annee_scolaire_id}`,
        { headers: getAuthHeaders() }
      );

      const result = await handleResponse(response);
      console.log('💬 [API] Appréciations élève reçues:', result);

      return result;
    } catch (error) {
      console.error('❌ [API] Erreur getAppreciationsTrimestrielles:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Générer le bulletin PDF d'un élève (Collège/Lycée)
  genererBulletinCollege: async (eleveId, trimestre, annee_scolaire_id, classeId) => {
    try {
      console.log('📄 [API] Génération bulletin collège:', { eleveId, trimestre, annee_scolaire_id, classeId });

      const response = await fetch(`${API_URL}/scolarite/bulletin-college/generer`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ eleveId, trimestre, annee_scolaire_id, classeId })
      });

      console.log('📄 [API] Réponse:', response.status, response.headers.get('content-type'));

      if (!response.ok) {
        throw new Error(`Erreur serveur: ${response.status}`);
      }

      const blob = await response.blob();
      console.log('📄 [API] Blob reçu:', blob.size, 'bytes, type:', blob.type);

      return blob;

    } catch (error) {
      console.error('❌ [API] Erreur génération bulletin:', error);
      throw error;
    }
  },

  // Générer tous les bulletins de la classe (Collège/Lycée)
  genererTousBulletinsCollege: async (classeId, trimestre, annee_scolaire_id) => {
    try {
      console.log('📦 [API] Génération tous bulletins collège:', { classeId, trimestre, annee_scolaire_id });

      const response = await fetch(`${API_URL}/scolarite/bulletin-college/generer-tous`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ classeId, trimestre, annee_scolaire_id })
      });

      if (!response.ok) {
        throw new Error(`Erreur serveur: ${response.status}`);
      }

      return await response.blob();

    } catch (error) {
      console.error('❌ [API] Erreur génération bulletins:', error);
      throw error;
    }
  },


  // Récupérer toutes les appréciations d'une classe en 1 fois



  // ========================================
  // MÉTHODES POUR LE PRIMAIRE
  // ========================================

  // Récupérer les notes du primaire
  getNotesPrimaire: async (classeId, mois_id, annee_scolaire_id) => {
    try {
      console.log('📊 [API] Appel getNotesPrimaire:', { classeId, mois_id, annee_scolaire_id });

      const response = await fetch(
        `${API_URL}/scolarite/notes-primaire/classe/${classeId}?mois_id=${mois_id}&annee_scolaire_id=${annee_scolaire_id}`,
        { headers: getAuthHeaders() }
      );

      const result = await handleResponse(response);
      console.log('📊 [API] Notes primaire reçues:', result);

      return result;
    } catch (error) {
      console.error('❌ [API] Erreur getNotesPrimaire:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Sauvegarder les notes du primaire
  saveNotesPrimaire: async (notesData, appreciationsData) => {
    try {
      console.log('💾 [API] Sauvegarde notes primaire:', { notesData, appreciationsData });

      const response = await fetch(`${API_URL}/scolarite/notes-primaire/bulk`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          notes: notesData,
          appreciations: appreciationsData
        })
      });

      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur saveNotesPrimaire:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },




  // Récupérer les paramètres de l'école
getParametresEcole: async () => {
  try {
    const response = await fetch(`${API_URL}/scolarite/parametres-ecole`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur récupération paramètres école:', error);
    throw error;
  }
},

// Générer le bulletin PDF d'un élève
genererBulletinPrimaire: async (eleveId, moisId, annee_scolaire_id, classeId) => {
  try {
    console.log('📄 [API] Génération bulletin:', { eleveId, moisId, annee_scolaire_id, classeId });

    const response = await fetch(`${API_URL}/scolarite/bulletin-primaire/generer`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ eleveId, moisId, annee_scolaire_id, classeId })
    });

    console.log('📄 [API] Réponse:', response.status, response.headers.get('content-type'));

    if (!response.ok) {
      throw new Error(`Erreur serveur: ${response.status}`);
    }

    // Retourner le blob du PDF
    const blob = await response.blob();
    console.log('📄 [API] Blob reçu:', blob.size, 'bytes, type:', blob.type);

    return blob;

  } catch (error) {
    console.error('❌ [API] Erreur génération bulletin:', error);
    throw error;
  }
},

// Générer tous les bulletins de la classe (optionnel pour plus tard)
genererTousBulletinsClasse: async (classeId, moisId, annee_scolaire_id) => {
  try {
    console.log('📦 [API] Génération tous bulletins:', { classeId, moisId, annee_scolaire_id });

    const response = await fetch(`${API_URL}/scolarite/bulletin-primaire/generer-tous`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ classeId, moisId, annee_scolaire_id })
    });

    if (!response.ok) {
      throw new Error(`Erreur serveur: ${response.status}`);
    }

    // Retourner le blob du ZIP
    return await response.blob();

  } catch (error) {
    console.error('❌ [API] Erreur génération bulletins:', error);
    throw error;
  }
},

  getStatistiquesGlobales: async (queryString) => {
    try {
      console.log('📊 [API] Appel getStatistiquesGlobales:', queryString);

      const response = await fetch(
        `${API_URL}/scolarite/statistiques-globales?${queryString}`,
        { headers: getAuthHeaders() }
      );

      const result = await handleResponse(response);
      console.log('📊 [API] Statistiques reçues:', result);

      return result;
    } catch (error) {
      console.error('❌ [API] Erreur getStatistiquesGlobales:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },


  downloadStatistiquesPDF: async (params) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/scolarite/statistiques-globales/pdf?${queryString}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Erreur génération PDF');
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `statistiques.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return { success: true };
  } catch (error) {
    console.error('❌ Erreur downloadStatistiquesPDF:', error);
    return { success: false, error: error.message };
  }
},


// Outil décisionnel - données classe
getOutilDecisionnelClasse: async (classeId, annee_scolaire_id) => {
  try {
    const response = await fetch(
      `${API_URL}/scolarite/outil-decisionnel/classe?classe_id=${classeId}&annee_scolaire_id=${annee_scolaire_id}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  } catch (error) {
    console.error('❌ [API] Erreur getOutilDecisionnelClasse:', error);
    return { success: false, error: error.message };
  }
},

  // Outil décisionnel - fiche élève
getOutilDecisionnelATraiter: async (classeId, annee_scolaire_id) => {
  try {
    const params = new URLSearchParams({ annee_scolaire_id });
    if (classeId) params.append('classe_id', classeId);

    const response = await fetch(
      `${API_URL}/scolarite/outil-decisionnel/a-traiter?${params.toString()}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  } catch (error) {
    console.error('âŒ [API] Erreur getOutilDecisionnelATraiter:', error);
    return { success: false, error: error.message };
  }
},

  getOutilDecisionnelEleve: async (eleveId, annee_scolaire_id) => {
    try {
      const response = await fetch(
        `${API_URL}/scolarite/outil-decisionnel/eleve/${eleveId}?annee_scolaire_id=${annee_scolaire_id}`,
        { headers: getAuthHeaders() }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur getOutilDecisionnelEleve:', error);
      return { success: false, error: error.message };
    }
  },

  downloadFicheDecisionnellePDF: async (eleveId, annee_scolaire_id) => {
    try {
      const response = await fetch(
        `${API_URL}/scolarite/outil-decisionnel/eleve/${eleveId}/pdf?annee_scolaire_id=${annee_scolaire_id}`,
        {
          headers: {
            ...getAuthHeaders(),
            Accept: 'application/pdf',
          },
        }
      );

      if (!response.ok) {
        let message = 'Erreur generation PDF';
        try {
          const errorData = await response.json();
          message = errorData.error || errorData.message || message;
        } catch {
          // La reponse n'est pas forcement en JSON.
        }
        throw new Error(message);
      }

      const disposition = response.headers.get('Content-Disposition');
      let filename = `fiche-decisionnelle-${eleveId}.pdf`;
      if (disposition && disposition.includes('filename=')) {
        filename = disposition.split('filename=')[1].replace(/"/g, '');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error('âŒ [API] Erreur downloadFicheDecisionnellePDF:', error);
      return { success: false, error: error.message };
    }
  },

  checkOutilDecisionnelChanges: async (classeId, annee_scolaire_id, since) => {
    try {
      const params = new URLSearchParams({
        classe_id: classeId,
        annee_scolaire_id,
      });
      if (since) params.append('since', since);

      const response = await fetch(
        `${API_URL}/scolarite/outil-decisionnel/changes?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur checkOutilDecisionnelChanges:', error);
      return { success: false, error: error.message };
    }
  },

  getDecisionDirection: async (eleveId, annee_scolaire_id) => {
    try {
      const response = await fetch(
        `${API_URL}/scolarite/outil-decisionnel/decision/${eleveId}?annee_scolaire_id=${annee_scolaire_id}`,
        { headers: getAuthHeaders() }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur getDecisionDirection:', error);
      return { success: false, error: error.message };
    }
  },

  saveDecisionDirection: async (eleveId, data) => {
    try {
      const response = await fetch(
        `${API_URL}/scolarite/outil-decisionnel/decision/${eleveId}`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(data),
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur saveDecisionDirection:', error);
      return { success: false, error: error.message };
    }
  },

  getDecisionDirections: async (eleveId, annee_scolaire_id) => {
    try {
      const response = await fetch(
        `${API_URL}/scolarite/outil-decisionnel/decisions/${eleveId}?annee_scolaire_id=${annee_scolaire_id}`,
        { headers: getAuthHeaders() }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur getDecisionDirections:', error);
      return { success: false, error: error.message };
    }
  },

  createDecisionDirection: async (eleveId, data) => {
    try {
      const response = await fetch(
        `${API_URL}/scolarite/outil-decisionnel/decisions/${eleveId}`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(data),
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur createDecisionDirection:', error);
      return { success: false, error: error.message };
    }
  },

  closeDecisionDirection: async (decisionId, data) => {
    try {
      const response = await fetch(
        `${API_URL}/scolarite/outil-decisionnel/decisions/${decisionId}/cloturer`,
        {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(data),
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur closeDecisionDirection:', error);
      return { success: false, error: error.message };
    }
  },

  getDecisionParents: async (eleveId, annee_scolaire_id) => {
    try {
      const response = await fetch(
        `${API_URL}/scolarite/outil-decisionnel/parents/${eleveId}?annee_scolaire_id=${annee_scolaire_id}`,
        { headers: getAuthHeaders() }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur getDecisionParents:', error);
      return { success: false, error: error.message };
    }
  },

  getParentCommunications: async (eleveId, annee_scolaire_id) => {
    try {
      const response = await fetch(
        `${API_URL}/scolarite/outil-decisionnel/communications-parent/${eleveId}?annee_scolaire_id=${annee_scolaire_id}`,
        { headers: getAuthHeaders() }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur getParentCommunications:', error);
      return { success: false, error: error.message };
    }
  },

  sendParentCommunication: async (data) => {
    try {
      const response = await fetch(
        `${API_URL}/scolarite/outil-decisionnel/communications-parent`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(data),
        }
      );
      const result = await response.json();
      if (!response.ok) {
        return {
          success: false,
          error: result.error || result.message || 'Erreur lors de l envoi au parent',
          data: result.data || null,
        };
      }
      return result;
    } catch (error) {
      console.error('❌ [API] Erreur sendParentCommunication:', error);
      return { success: false, error: error.message };
    }
  },

  getPlansAccompagnementDecision: async (eleveId, annee_scolaire_id) => {
    try {
      const response = await fetch(
        `${API_URL}/scolarite/outil-decisionnel/plans/${eleveId}?annee_scolaire_id=${annee_scolaire_id}`,
        { headers: getAuthHeaders() }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('âŒ [API] Erreur getPlansAccompagnementDecision:', error);
      return { success: false, error: error.message };
    }
  },

  createPlanAccompagnementDecision: async (data) => {
    try {
      const response = await fetch(
        `${API_URL}/scolarite/outil-decisionnel/plans`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(data),
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('âŒ [API] Erreur createPlanAccompagnementDecision:', error);
      return { success: false, error: error.message };
    }
  },

  updatePlanAccompagnementDecision: async (planId, data) => {
    try {
      const response = await fetch(
        `${API_URL}/scolarite/outil-decisionnel/plans/${planId}`,
        {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(data),
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('âŒ [API] Erreur updatePlanAccompagnementDecision:', error);
      return { success: false, error: error.message };
    }
  },

  terminerPlanAccompagnementDecision: async (planId, data = {}) => {
    try {
      const response = await fetch(
        `${API_URL}/scolarite/outil-decisionnel/plans/${planId}/terminer`,
        {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(data),
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('âŒ [API] Erreur terminerPlanAccompagnementDecision:', error);
      return { success: false, error: error.message };
    }
  },

  deletePlanAccompagnementDecision: async (planId) => {
    try {
      const response = await fetch(
        `${API_URL}/scolarite/outil-decisionnel/plans/${planId}`,
        {
          method: 'DELETE',
          headers: getAuthHeaders(),
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('âŒ [API] Erreur deletePlanAccompagnementDecision:', error);
      return { success: false, error: error.message };
    }
  },



getConfigSeuilsDecisionnels: async () => {
  try {
    const response = await fetch(
      `${API_URL}/scolarite/config-seuils-decisionnels`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  } catch (error) {
    console.error('❌ [API] Erreur getConfigSeuilsDecisionnels:', error);
    return { success: false, error: error.message };
  }
},

saveConfigSeuilsDecisionnels: async (data) => {
  try {
    const response = await fetch(
      `${API_URL}/scolarite/config-seuils-decisionnels`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      }
    );
    return handleResponse(response);
  } catch (error) {
    console.error('❌ [API] Erreur saveConfigSeuilsDecisionnels:', error);
    return { success: false, error: error.message };
  }
},




getSuiviActions: async (eleveId, annee_scolaire_id) => {
  try {
    const response = await fetch(
      `${API_URL}/scolarite/suivi-actions/${eleveId}?annee_scolaire_id=${annee_scolaire_id}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  } catch (error) {
    console.error('❌ [API] Erreur getSuiviActions:', error);
    return { success: false, error: error.message };
  }
},

addSuiviAction: async (data) => {
  try {
    const response = await fetch(
      `${API_URL}/scolarite/suivi-actions`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      }
    );
    return handleResponse(response);
  } catch (error) {
    console.error('❌ [API] Erreur addSuiviAction:', error);
    return { success: false, error: error.message };
  }
},

deleteSuiviAction: async (actionId) => {
  try {
    const response = await fetch(
      `${API_URL}/scolarite/suivi-actions/${actionId}`,
      {
        method: 'DELETE',
        headers: getAuthHeaders(),
      }
    );
    return handleResponse(response);
  } catch (error) {
    console.error('❌ [API] Erreur deleteSuiviAction:', error);
    return { success: false, error: error.message };
  }
},


saveNotesPrimaireEleve: async (data) => {
  try {
    const response = await fetch(
      `${API_URL}/scolarite/notes-primaire/eleve`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      }
    );
    return handleResponse(response);
  } catch (error) {
    console.error('❌ [API] Erreur saveNotesPrimaireEleve:', error);
    return { success: false, error: error.message };
  }
},


saveNotesCollegeEleve: async (data) => {
  try {
    const response = await fetch(
      `${API_URL}/scolarite/notes-college/eleve`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      }
    );
    return handleResponse(response);
  } catch (error) {
    console.error('❌ [API] Erreur saveNotesCollegeEleve:', error);
    return { success: false, error: error.message };
  }
},



},


  notesVerrouillage: {

    // Récupérer la config des délais par section
    getConfig: async () => {
      try {
        const response = await fetch(`${API_URL}/notes-verrouillage/config`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ [API] Erreur getConfig verrouillage:', error);
        return { success: false, error: error.message };
      }
    },

    // Modifier le délai d'une section
    updateConfig: async (configId, delai_reclamation_jours) => {
      try {
        const response = await fetch(`${API_URL}/notes-verrouillage/config/${configId}`, {
          method: 'PUT',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ delai_reclamation_jours })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ [API] Erreur updateConfig verrouillage:', error);
        return { success: false, error: error.message };
      }
    },

    // Récupérer l'état des verrouillages d'une classe
    getEtatClasse: async (classeId, annee_scolaire_id, { mois_id, trimestre } = {}) => {
      try {
        let url = `${API_URL}/notes-verrouillage/classe/${classeId}?annee_scolaire_id=${annee_scolaire_id}`;
        if (mois_id) url += `&mois_id=${mois_id}`;
        if (trimestre) url += `&trimestre=${trimestre}`;

        const response = await fetch(url, { headers: getAuthHeaders() });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ [API] Erreur getEtatClasse verrouillage:', error);
        return { success: false, error: error.message };
      }
    },

    // Déverrouiller notes (élève ou classe)
    deverrouiller: async ({ classe_id, annee_scolaire_id, motif, type, eleve_id, mois_id, trimestre }) => {
      try {
        const response = await fetch(`${API_URL}/notes-verrouillage/deverrouiller`, {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ classe_id, annee_scolaire_id, motif, type, eleve_id, mois_id, trimestre })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ [API] Erreur deverrouiller:', error);
        return { success: false, error: error.message };
      }
    },

    // Historique des déverrouillages
    getHistorique: async ({ classe_id, annee_scolaire_id, limit = 50, offset = 0 } = {}) => {
      try {
        let url = `${API_URL}/notes-verrouillage/historique?limit=${limit}&offset=${offset}`;
        if (classe_id) url += `&classe_id=${classe_id}`;
        if (annee_scolaire_id) url += `&annee_scolaire_id=${annee_scolaire_id}`;

        const response = await fetch(url, { headers: getAuthHeaders() });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ [API] Erreur getHistorique:', error);
        return { success: false, error: error.message };
      }
    },

    // Liste des classes (pour le sélecteur)
    getClasses: async () => {
      try {
        const response = await fetch(`${API_URL}/notes-verrouillage/classes`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ [API] Erreur getClasses:', error);
        return { success: false, error: error.message };
      }
    },


    getConfigChamps: async () => {
      try {
        const response = await fetch(`${API_URL}/notes-verrouillage/config-champs`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur getConfigChamps:', error);
        return { success: false, error: error.message };
      }
    },

    updateConfigChamps: async (section, data) => {
      try {
        const response = await fetch(`${API_URL}/notes-verrouillage/config-champs/${section}`, {
          method: 'PUT',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur updateConfigChamps:', error);
        return { success: false, error: error.message };
      }
    },



  },



  configAppreciations: {

    // Récupérer toutes les appréciations (optionnel: filtrer par type)
    getAll: async (type = null) => {
      try {
        const url = type
          ? `${API_URL}/config-appreciations?type=${type}`
          : `${API_URL}/config-appreciations`;
        const response = await fetch(url, { headers: getAuthHeaders() });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur getAll config-appreciations:', error);
        return { success: false, error: error.message };
      }
    },

    // Créer une appréciation
    create: async (data) => {
      try {
        const response = await fetch(`${API_URL}/config-appreciations`, {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur create config-appreciations:', error);
        return { success: false, error: error.message };
      }
    },

    // Modifier une appréciation
    update: async (id, data) => {
      try {
        const response = await fetch(`${API_URL}/config-appreciations/${id}`, {
          method: 'PUT',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur update config-appreciations:', error);
        return { success: false, error: error.message };
      }
    },

    // Supprimer une appréciation (soft delete)
    delete: async (id) => {
      try {
        const response = await fetch(`${API_URL}/config-appreciations/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur delete config-appreciations:', error);
        return { success: false, error: error.message };
      }
    },

    // Réinitialiser les appréciations par défaut
    reset: async (type) => {
      try {
        const response = await fetch(`${API_URL}/config-appreciations/reset`, {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ type })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur reset config-appreciations:', error);
        return { success: false, error: error.message };
      }
    },
  },


  paiements: {
    // RÃÂ©cupÃÂ©rer tous les paiements (avec filtre possible par annÃÂ©e scolaire)
    getAll: async (annee_scolaire_id) => {
      try {
        const response = await fetch(`${API_URL}/finance/paiements?annee_scolaire_id=${annee_scolaire_id}`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des paiements:', error);
        return { success: false, error: error.message };
      }
    },

    // RÃÂ©cupÃÂ©rer les paiements d'un ÃÂ©lÃÂ¨ve spÃÂ©cifique
    getByEleve: async (eleveId, annee_scolaire_id) => {
      try {
        const response = await fetch(`${API_URL}/finance/paiements/${eleveId}?annee_scolaire_id=${annee_scolaire_id}`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des paiements:', error);
        return { success: false, error: error.message };
      }
    },

    // Effectuer un nouveau paiement
    addPayment: async (paymentData) => {
      try {
        // RÃÂ©cupÃÂ©rer l'utilisateur connectÃÂ©
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

        const response = await fetch(`${API_URL}/finance/paiements`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            eleve_id: paymentData.eleve_id,
            mois_id: paymentData.mois_id,
            montant_paye: parseFloat(paymentData.montant_paye),
            date_paiement: new Date().toISOString().split('T')[0],
            annee_scolaire_id: paymentData.annee_scolaire_id,
            agent_id: paymentData.agent_id || currentUser.id,
            mode_paiement: paymentData.mode_paiement || 'Esp\u00e8ces',
            numero_transaction: paymentData.numero_transaction || null
          })
        });
        return handleResponse(response);
      } catch (error) {
        console.error("Erreur lors de l'enregistrement du paiement:", error);
        return { success: false, error: error.message };
      }
    },

    // Obtenir les dÃÂ©tails d'un paiement spÃÂ©cifique
    getDetails: async (eleveId, moisId, annee_scolaire_id) => {
      try {
        const response = await fetch(`${API_URL}/finance/paiements/${eleveId}/${moisId}/details?annee_scolaire_id=${annee_scolaire_id}`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des dÃÂ©tails du paiement:', error);
        return { success: false, error: error.message };
      }
    },

    // GÃÂ©nÃÂ©rer un reÃÂ§u de paiement
    generateReceipt: async (receiptData) => {
      try {
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

        const response = await fetch(`${API_URL}/finance/receipts/generate`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Accept': 'application/pdf'
          },
          body: JSON.stringify({
            ...receiptData,
            user: {
              id: currentUser.id,
              nom: currentUser.nom,
              prenom: currentUser.prenom
            }
          })
        });

        if (!response.ok) {
          throw new Error('Erreur lors de la gÃÂ©nÃÂ©ration du reÃÂ§u');
        }

        const pdfBlob = await response.blob();
        return {
          success: true,
          data: pdfBlob,
          filename: `recu-paiement-${new Date().toISOString().split('T')[0]}.pdf`
        };
      } catch (error) {
        console.error('Erreur lors de la gÃÂ©nÃÂ©ration du reÃÂ§u:', error);
        return { success: false, error: error.message };
      }
    },

    // RÃÂ©cupÃÂ©rer les modes de paiement disponibles
    // Dans la section getModesPaiement de l'objet finance
getModesPaiement: async () => {
  try {
    const response = await fetch(`${API_URL}/finance/modes-paiement`, {
      headers: getAuthHeaders()
    });

    return handleResponse(response);
  } catch (error) {
    console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des modes de paiement:', error);
    // Toujours renvoyer une erreur plutÃÂ´t que des valeurs par dÃÂ©faut
    return {
      success: false,
      error: error.message,
      // Vous pouvez toujours inclure des donnÃÂ©es de secours si nÃÂ©cessaire
      data: []
    };
  }
}

  },



  // ENDPOINT DES MOIS SCOLAIRE

moisScolaires: {
  // RÃÂ©cupÃÂ©rer tous les mois scolaires
  getAll: async () => {
    try {
      const response = await fetch(`${API_URL}/mois-scolaires`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des mois scolaires:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // RÃÂ©cupÃÂ©rer les mois scolaires par trimestre
  // Le calendrier reel de l'annee active : les mois, leurs dates, et ce qui
  // les verrouille (etat des salaires valide, salaires deja payes).
  getCalendrier: async () => {
    try {
      const response = await fetch(`${API_URL}/mois-scolaires/calendrier`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la lecture du calendrier:', error);
      return { success: false, message: error.message };
    }
  },

  // Corriger un mois que rien n'a encore consomme.
  update: async (id, donnees) => {
    try {
      const response = await fetch(`${API_URL}/mois-scolaires/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(donnees)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la mise a jour du mois scolaire:', error);
      return { success: false, message: error.message };
    }
  },

  getByTrimestre: async (trimestre) => {
    try {
      const response = await fetch(`${API_URL}/mois-scolaires/trimestre/${trimestre}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des mois scolaires par trimestre:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
},


// Ajoutez ce nouveau service sections dans votre objet api
sections: {
  getAll: async () => {
    console.log('[API CLIENT] DÃÂ©marrage de la requÃÂªte getAll des sections');
    try {
      console.log('[API CLIENT] PrÃÂ©paration de la requÃÂªte fetch pour /sections');
      const response = await fetch(`${API_URL}/sections`, {
        headers: getAuthHeaders()
      });
      console.log('[API CLIENT] RÃÂ©ponse fetch reÃÂ§ue:', {
        status: response.status,
        ok: response.ok,
        statusText: response.statusText
      });

      if (!response.ok) {
        console.error('[API CLIENT] RÃÂ©ponse non-OK pour les sections:', response.status, response.statusText);
        return {
          success: false,
          error: `Erreur HTTP: ${response.status} ${response.statusText}`,
          data: []
        };
      }

      const result = await handleResponse(response);
      console.log('[API CLIENT] DonnÃÂ©es des sections aprÃÂ¨s traitement:', result);

      return {
        success: true,
        data: result.success ? result.data : result
      };
    } catch (error) {
      console.error('[API CLIENT] Exception lors de la rÃÂ©cupÃÂ©ration des sections:', error);
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  },

  create: async (sectionData) => {
    try {
      const response = await fetch(`${API_URL}/sections`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nom: sectionData.nom,
          description: sectionData.description || ''
        })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la crÃÂ©ation de section:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  update: async (id, sectionData) => {
    try {
      const response = await fetch(`${API_URL}/sections/${id}`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nom: sectionData.nom,
          description: sectionData.description || ''
        })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la mise ÃÂ  jour de section:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  delete: async (id) => {
    try {
      const response = await fetch(`${API_URL}/sections/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la suppression de section:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
},


// Ajouter ceci dans api.js


groupesSecurite: {
  // Creation, modification et suppression : elles n'existaient ni ici ni sur
  // le serveur. Un groupe se posait a la main dans la base.
  create: async (donnees) => {
    try {
      const response = await fetch(`${API_URL}/groupes-securite`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(donnees)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la creation du groupe:', error);
      return { success: false, error: error.message };
    }
  },

  update: async (id, donnees) => {
    try {
      const response = await fetch(`${API_URL}/groupes-securite/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(donnees)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la modification du groupe:', error);
      return { success: false, error: error.message };
    }
  },

  remove: async (id) => {
    try {
      const response = await fetch(`${API_URL}/groupes-securite/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la suppression du groupe:', error);
      return { success: false, error: error.message };
    }
  },

  // Rattacher des roles a un groupe. Sans eux, un groupe ne peut porter que
  // des portes de module : toute autre permission est ignoree cote serveur.
  setRoles: async (id, roleIds) => {
    try {
      const response = await fetch(`${API_URL}/groupes-securite/${id}/roles`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ roleIds })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors du rattachement des roles:', error);
      return { success: false, error: error.message };
    }
  },

  getAll: async () => {
    try {
      const response = await fetch(`${API_URL}/groupes-securite`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des groupes de sÃÂ©curitÃÂ©:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  getById: async (id) => {
    try {
      const response = await fetch(`${API_URL}/groupes-securite/${id}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration du groupe de sÃÂ©curitÃÂ©:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Ã¢ÂÂ AJOUTER CETTE MÃÂTHODE :
  getAllWithPermissions: async () => {
    try {
      const response = await fetch(`${API_URL}/groupes-securite/with-permissions`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des groupes avec permissions:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Ã¢ÂÂ AJOUTER AUSSI CETTE MÃÂTHODE POUR LA SAUVEGARDE :
  updatePermissions: async (groupId, permissions) => {
    try {
      const response = await fetch(`${API_URL}/groupes-securite/${groupId}/permissions`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ permissions })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la mise ÃÂ  jour des permissions:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
},


// Ajouter cette nouvelle section ÃÂ  votre objet api
securityGroups: {
  getAll: async () => {
    try {
      // D'abord essayer d'utiliser l'endpoint dÃÂ©diÃÂ© aux groupes de sÃÂ©curitÃÂ© avec permissions
      const response = await fetch(`${API_URL}/security-groups`, {
        headers: getAuthHeaders()
      });

      // Si l'endpoint n'existe pas encore, utiliser les groupes existants comme fallback
      if (!response.ok && response.status === 404) {
        console.log("API /security-groups non disponible, fallback sur /groupes-securite");

        // RÃÂ©cupÃÂ©rer les groupes de sÃÂ©curitÃÂ© existants
        const groupesResponse = await fetch(`${API_URL}/groupes-securite`, {
          headers: getAuthHeaders()
        });

        if (!groupesResponse.ok) {
          throw new Error(`Erreur HTTP: ${groupesResponse.status}`);
        }

        const groupesData = await groupesResponse.json();

        // Adapter le format pour la nouvelle structure
        let data = Array.isArray(groupesData.data) ? groupesData.data : groupesData;

        // Transformer les donnÃÂ©es pour correspondre au format attendu
        const formattedGroups = data.map(group => ({
          id: group.id,
          name: group.nom || group.name,
          description: group.description,
          color: group.color || 'blue',
          permissions: group.permissions || {}
        }));

        return {
          success: true,
          data: formattedGroups
        };
      }

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des groupes de sÃÂ©curitÃÂ©:', error);
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  },

  createGroup: async (groupData) => {
    try {
      // Adapter le format des donnÃÂ©es pour l'API
      const formattedData = {
        nom: groupData.name,
        description: groupData.description,
        color: groupData.color
      };

      const response = await fetch(`${API_URL}/security-groups`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formattedData)
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la crÃÂ©ation du groupe de sÃÂ©curitÃÂ©:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  updatePermissions: async (groupId, dataToSend) => {
    try {
      // VÃÂ©rifier le format des donnÃÂ©es
      const permissions = dataToSend.permissions || {};

      const response = await fetch(`${API_URL}/security-groups/${groupId}/permissions`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ permissions })
      });

      // Si l'API n'existe pas encore, utiliser celle des rÃÂ´les comme fallback
      if (!response.ok && response.status === 404) {
        console.log("API /security-groups/permissions non disponible, fallback sur /roles");

        // RÃÂ©cupÃÂ©rer les rÃÂ´les associÃÂ©s au groupe
        const rolesResponse = await fetch(`${API_URL}/roles/groupe/${groupId}`, {
          headers: getAuthHeaders()
        });

        if (!rolesResponse.ok) {
          throw new Error(`Erreur lors de la rÃÂ©cupÃÂ©ration des rÃÂ´les: ${rolesResponse.status}`);
        }

        // Analyser la rÃÂ©ponse JSON avec gestion d'erreur
        let rolesData;
        try {
          rolesData = await rolesResponse.json();

          // Normaliser les donnÃÂ©es
          if (rolesData && rolesData.data) {
            rolesData = rolesData.data;
          }

          // VÃÂ©rifier que c'est bien un tableau
          if (!Array.isArray(rolesData)) {
            console.error("Format de rÃÂ©ponse inattendu pour les rÃÂ´les:", rolesData);
            rolesData = [];
          }
        } catch (parseError) {
          console.error("Erreur lors de l'analyse des donnÃÂ©es de rÃÂ´les:", parseError);
          rolesData = [];
        }

        // VÃÂ©rifier que nous avons des rÃÂ´les ÃÂ  traiter
        if (rolesData.length === 0) {
          return {
            success: true,
            message: "Aucun rÃÂ´le associÃÂ© ÃÂ  ce groupe"
          };
        }

        // Pour chaque rÃÂ´le du groupe, mettre ÃÂ  jour ses permissions
        for (const role of rolesData) {
          if (!role || !role.id) {
            console.warn("RÃÂ´le invalide ignorÃÂ©:", role);
            continue;
          }

          await fetch(`${API_URL}/roles/${role.id}/permissions`, {
            method: 'PUT',
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ permissions })
          });
        }

        return {
          success: true,
          message: `Permissions mises ÃÂ  jour pour ${rolesData.length} rÃÂ´le(s)`
        };
      }

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la mise ÃÂ  jour des permissions:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  deleteGroup: async (groupId) => {
    try {
      const response = await fetch(`${API_URL}/security-groups/${groupId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      // Si l'API n'existe pas encore, utiliser le systÃÂ¨me de groupes existant
      if (!response.ok && response.status === 404) {
        console.log("API /security-groups/delete non disponible, fallback");

        // Marquer comme supprimÃÂ© dans le systÃÂ¨me existant
        const fallbackResponse = await fetch(`${API_URL}/groupes-securite/${groupId}/desactiver`, {
          method: 'PUT',
          headers: getAuthHeaders()
        });

        if (!fallbackResponse.ok) {
          throw new Error(`Erreur lors de la dÃÂ©sactivation: ${fallbackResponse.status}`);
        }

        return {
          success: true,
          message: "Groupe marquÃÂ© comme dÃÂ©sactivÃÂ©"
        };
      }

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la suppression du groupe:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  restoreGroup: async (groupId) => {
    try {
      const response = await fetch(`${API_URL}/security-groups/${groupId}/restore`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      // Si l'API n'existe pas encore, utiliser le systÃÂ¨me de groupes existant
      if (!response.ok && response.status === 404) {
        console.log("API /security-groups/restore non disponible, fallback");

        // RÃÂ©activer dans le systÃÂ¨me existant
        const fallbackResponse = await fetch(`${API_URL}/groupes-securite/${groupId}/activer`, {
          method: 'PUT',
          headers: getAuthHeaders()
        });

        if (!fallbackResponse.ok) {
          throw new Error(`Erreur lors de la rÃÂ©activation: ${fallbackResponse.status}`);
        }

        return {
          success: true,
          message: "Groupe rÃÂ©activÃÂ©"
        };
      }

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la restauration du groupe:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  emptyTrash: async (groupIds) => {
    try {
      const response = await fetch(`${API_URL}/security-groups/empty-trash`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ groupIds })
      });

      // Si l'API n'existe pas encore, simuler une suppression dÃÂ©finitive
      if (!response.ok && response.status === 404) {
        console.log("API /security-groups/empty-trash non disponible, suppression simulÃÂ©e");

        for (const groupId of groupIds) {
          // Suppression dÃÂ©finitive si possible
          try {
            await fetch(`${API_URL}/groupes-securite/${groupId}`, {
              method: 'DELETE',
              headers: getAuthHeaders()
            });
          } catch (err) {
            console.warn(`Erreur lors de la suppression dÃÂ©finitive du groupe ${groupId}:`, err);
          }
        }

        return {
          success: true,
          message: "Groupes supprimÃÂ©s dÃÂ©finitivement"
        };
      }

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors du vidage de la corbeille:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Fonction pour obtenir la structure des permissions disponibles
  getPermissionsStructure: async () => {
    try {
      const response = await fetch(`${API_URL}/permissions/structure`, {
        headers: getAuthHeaders()
      });

      // Si l'API n'existe pas encore, retourner une structure minimale
      if (!response.ok && response.status === 404) {
        console.log("API /permissions/structure non disponible, structure par dÃÂ©faut");

        return {
          success: true,
          data: {
            modules: [
              {
                id: 1,
                name: "ELEVES",
                subModules: [
                  {
                    id: 101,
                    name: "LISTE",
                    permissions: [
                      { id: 10101, key: "ELEVES.LISTE.VIEW", name: "Consulter la liste" }
                    ]
                  }
                ]
              }
            ]
          }
        };
      }

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration de la structure des permissions:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  getAllWithPermissions: async () => {
    try {
      const response = await fetch(`${API_URL}/groupes-securite/with-permissions`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des groupes avec permissions:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // DANS ta section securityGroups existante, AJOUTER AUSSI :
  updatePermissions: async (groupId, permissions) => {
    try {
      const response = await fetch(`${API_URL}/groupes-securite/${groupId}/permissions`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ permissions })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la mise ÃÂ  jour des permissions:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }


},


permissions: {
  // RÃÂ©cupÃÂ©rer toutes les permissions
  getAll: async () => {
    try {
      const response = await fetch(`${API_URL}/permissions`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des permissions:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // RÃÂ©cupÃÂ©rer les permissions groupÃÂ©es par module
  getByModule: async () => {
    try {
      const response = await fetch(`${API_URL}/permissions/by-module`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des permissions par module:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // ------------------------------------------------------------------
  // Derogations individuelles
  // ------------------------------------------------------------------
  // Accorder ou retirer une permission a UN employe, par-dessus son groupe et
  // ses roles. C'est le seul chemin : le circuit de demande qui les
  // accompagnait n a jamais ete raccorde, et a ete retire.

  getDerogations: async (employeId) => {
    try {
      const response = await fetch(`${API_URL}/permissions/derogations/${employeId}`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
        cache: 'no-store'
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la lecture des derogations:', error);
      return { success: false, error: error.message };
    }
  },

  getAllDerogations: async () => {
    try {
      const response = await fetch(`${API_URL}/permissions/derogations`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
        cache: 'no-store'
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la lecture des derogations:', error);
      return { success: false, error: error.message };
    }
  },

  setDerogation: async (employeId, { permissionKey, type, raison }) => {
    try {
      const response = await fetch(`${API_URL}/permissions/derogations/${employeId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ permissionKey, type, raison })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de l enregistrement de la derogation:', error);
      return { success: false, error: error.message };
    }
  },

  removeDerogation: async (employeId, permissionId) => {
    try {
      const response = await fetch(`${API_URL}/permissions/derogations/${employeId}/${permissionId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors du retrait de la derogation:', error);
      return { success: false, error: error.message };
    }
  },
},


systemPermissions: {
  // RÃÂ©cupÃÂ©rer toutes les permissions du systÃÂ¨me avec leur statut par dÃÂ©faut
  getAllSystemPermissions: async () => {
    try {
      const response = await fetch(`${API_URL}/permissions`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des permissions systÃÂ¨me:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // RÃÂ©cupÃÂ©rer les permissions d'un module spÃÂ©cifique (UTILISÃÂ PAR ElevesPermissionsConfig)
  getModulePermissions: async (module) => {
    try {
      const response = await fetch(`${API_URL}/permissions/by-module?module=${module}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des permissions du module:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },
},



// REMPLACER ENTIÃÂREMENT votre section affectations existante par celle-ci :

affectations: {
  // ========================================
  // MÃÂTHODES GÃÂNÃÂRIQUES
  // ========================================

  // RÃÂ©cupÃÂ©rer toutes les affectations (mix des deux types)
  getAll: async (annee_scolaire_id) => {
    try {
      const response = await fetch(`${API_URL}/affectations/all?annee_scolaire_id=${annee_scolaire_id}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des affectations:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Supprimer une affectation (dÃÂ©tecte automatiquement le type)
  delete: async (type, id) => {
    try {
      const response = await fetch(`${API_URL}/affectations/${type}/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'affectation:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // RÃÂ©cupÃÂ©rer les statistiques globales
  getStatistiques: async (annee_scolaire_id) => {
    try {
      const response = await fetch(`${API_URL}/affectations/statistiques?annee_scolaire_id=${annee_scolaire_id}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des statistiques:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Recuperer les enseignants horaires lies a une classe RH mais sans matiere affectee
  getHorairesACompleter: async (annee_scolaire_id) => {
    try {
      const response = await fetch(`${API_URL}/affectations/enseignants-horaires/a-completer?annee_scolaire_id=${annee_scolaire_id}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la recuperation des enseignants horaires a completer:', error);
      return {
        success: false,
        data: [],
        error: error.message
      };
    }
  },

  // ========================================
  // ENSEIGNANTS PRINCIPAUX (role_id = 32)
  // ========================================

  enseignantsPrincipaux: {
    // RÃÂ©cupÃÂ©rer toutes les affectations d'enseignants principaux
    getAll: async (annee_scolaire_id) => {
      try {
        const response = await fetch(`${API_URL}/affectations/enseignants-principaux?annee_scolaire_id=${annee_scolaire_id}`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des enseignants principaux:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    // CrÃÂ©er une affectation enseignant principal
    create: async (affectationData) => {
      try {
        const response = await fetch(`${API_URL}/affectations/enseignants-principaux`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            enseignant_id: affectationData.enseignant_id,
            classe_id: affectationData.classe_id,
            annee_scolaire_id: affectationData.annee_scolaire_id
          })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur lors de la crÃÂ©ation de l\'affectation enseignant principal:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    // Mettre ÃÂ  jour une affectation enseignant principal
    update: async (id, affectationData) => {
      try {
        const response = await fetch(`${API_URL}/affectations/enseignants-principaux/${id}`, {
          method: 'PUT',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            enseignant_id: affectationData.enseignant_id,
            classe_id: affectationData.classe_id,
            annee_scolaire_id: affectationData.annee_scolaire_id
          })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur lors de la mise ÃÂ  jour de l\'affectation enseignant principal:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    // Supprimer une affectation enseignant principal
    delete: async (id) => {
      try {
        const response = await fetch(`${API_URL}/affectations/enseignants-principaux/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur lors de la suppression de l\'affectation enseignant principal:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    // RÃÂ©cupÃÂ©rer les enseignants principaux disponibles (sans classe)
    getDisponibles: async (annee_scolaire_id) => {
      try {
        const response = await fetch(`${API_URL}/affectations/enseignants-principaux/disponibles?annee_scolaire_id=${annee_scolaire_id}`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des enseignants principaux disponibles:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    // RÃÂ©cupÃÂ©rer les classes sans enseignant principal
    getClassesSansEnseignant: async (annee_scolaire_id) => {
      try {
        const response = await fetch(`${API_URL}/affectations/classes/sans-enseignant-principal?annee_scolaire_id=${annee_scolaire_id}`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des classes sans enseignant principal:', error);
        return {
          success: false,
          error: error.message
        };
      }
    }
  },

  // ========================================
  // ENSEIGNANTS DE MATIÃÂRE (role_id = 33)
  // ========================================

  enseignantsMatieres: {
    // RÃÂ©cupÃÂ©rer toutes les affectations d'enseignants de matiÃÂ¨re
    getAll: async (annee_scolaire_id, filters = {}) => {
      try {
        let url = `${API_URL}/affectations/enseignants-matieres?annee_scolaire_id=${annee_scolaire_id}`;

        // Ajouter les filtres
        if (filters.enseignant_id) url += `&enseignant_id=${filters.enseignant_id}`;
        if (filters.classe_id) url += `&classe_id=${filters.classe_id}`;
        if (filters.matiere_id) url += `&matiere_id=${filters.matiere_id}`;

        const response = await fetch(url, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des enseignants de matiÃÂ¨re:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    // CrÃÂ©er une affectation enseignant de matiÃÂ¨re
    create: async (affectationData) => {
      try {
        const response = await fetch(`${API_URL}/affectations/enseignants-matieres`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            enseignant_id: affectationData.enseignant_id,
            matiere_id: affectationData.matiere_id,
            classe_id: affectationData.classe_id,
            annee_scolaire_id: affectationData.annee_scolaire_id
          })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur lors de la crÃÂ©ation de l\'affectation enseignant de matiÃÂ¨re:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    // CrÃÂ©er plusieurs affectations en lot (enseignant -> plusieurs matiÃÂ¨res/classes)
    createBatch: async (batchData) => {
      try {
        const response = await fetch(`${API_URL}/affectations/enseignants-matieres/batch`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            enseignant_id: batchData.enseignant_id,
            annee_scolaire_id: batchData.annee_scolaire_id,
            affectations: batchData.affectations // [{matiere_id, classe_id}, ...]
          })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur lors de la crÃÂ©ation en lot des affectations:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    // Mettre ÃÂ  jour une affectation enseignant de matiÃÂ¨re
    update: async (id, affectationData) => {
      try {
        const response = await fetch(`${API_URL}/affectations/enseignants-matieres/${id}`, {
          method: 'PUT',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            enseignant_id: affectationData.enseignant_id,
            matiere_id: affectationData.matiere_id,
            classe_id: affectationData.classe_id,
            annee_scolaire_id: affectationData.annee_scolaire_id
          })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur lors de la mise ÃÂ  jour de l\'affectation enseignant de matiÃÂ¨re:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    // Supprimer une affectation enseignant de matiÃÂ¨re
    delete: async (id) => {
      try {
        const response = await fetch(`${API_URL}/affectations/enseignants-matieres/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur lors de la suppression de l\'affectation enseignant de matiÃÂ¨re:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    // RÃÂ©cupÃÂ©rer les enseignants de matiÃÂ¨re disponibles
    getDisponibles: async (annee_scolaire_id) => {
      try {
        const response = await fetch(`${API_URL}/affectations/enseignants-matieres/disponibles?annee_scolaire_id=${annee_scolaire_id}`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des enseignants de matiÃÂ¨re disponibles:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    // RÃÂ©cupÃÂ©rer les matiÃÂ¨res disponibles pour une classe
    getMatieresDisponibles: async (classe_id, annee_scolaire_id) => {
      try {
        const response = await fetch(`${API_URL}/affectations/matieres/disponibles?classe_id=${classe_id}&annee_scolaire_id=${annee_scolaire_id}`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des matiÃÂ¨res disponibles:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    // RÃÂ©cupÃÂ©rer les classes-matiÃÂ¨res sans enseignant
    getClassesMatieresNonAffectees: async (annee_scolaire_id) => {
      try {
        const response = await fetch(`${API_URL}/affectations/classes-matieres/non-affectees?annee_scolaire_id=${annee_scolaire_id}`, {
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des classes-matiÃÂ¨res non affectÃÂ©es:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    // Supprimer toutes les affectations d'un enseignant
    deleteAllByEnseignant: async (enseignant_id, annee_scolaire_id) => {
      try {
        const response = await fetch(`${API_URL}/affectations/enseignants-matieres/enseignant/${enseignant_id}?annee_scolaire_id=${annee_scolaire_id}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur lors de la suppression des affectations de l\'enseignant:', error);
        return {
          success: false,
          error: error.message
        };
      }
    }
  },

  // ========================================
  // MÃÂTHODES UTILITAIRES
  // ========================================

  // RÃÂ©cupÃÂ©rer tous les enseignants avec leurs rÃÂ´les
  getEnseignantsAvecRoles: async () => {
    try {
      const response = await fetch(`${API_URL}/affectations/enseignants/avec-roles`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des enseignants avec rÃÂ´les:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // VÃÂ©rifier les conflits d'emploi du temps avant affectation
  verifierConflits: async (affectationData) => {
    try {
      const response = await fetch(`${API_URL}/affectations/verifier-conflits`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(affectationData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la vÃÂ©rification des conflits:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // GÃÂ©nÃÂ©rer un rapport complet des affectations
  genererRapport: async (annee_scolaire_id, format = 'json') => {
    try {
      const response = await fetch(`${API_URL}/affectations/rapport?annee_scolaire_id=${annee_scolaire_id}&format=${format}`, {
        headers: getAuthHeaders()
      });

      if (format === 'pdf' || format === 'excel') {
        const blob = await response.blob();
        return {
          success: true,
          data: blob,
          filename: `rapport_affectations_${annee_scolaire_id}.${format}`
        };
      }

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la gÃÂ©nÃÂ©ration du rapport:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // ========================================
  // MÃÂTHODES DE COMPATIBILITÃÂ (anciennes mÃÂ©thodes)
  // ========================================

  // Maintenir la compatibilitÃÂ© avec l'ancien systÃÂ¨me
  create: async (affectationData) => {
    // Rediriger vers la bonne mÃÂ©thode selon le type d'enseignant
    if (affectationData.type === 'principal' || affectationData.role_id === 32) {
      return api.affectations.enseignantsPrincipaux.create(affectationData);
    } else if (affectationData.type === 'matiere' || affectationData.role_id === 33) {
      return api.affectations.enseignantsMatieres.create(affectationData);
    } else {
      return {
        success: false,
        error: 'Type d\'enseignant non spÃÂ©cifiÃÂ©'
      };
    }
  },

  update: async (id, affectationData) => {
    // Rediriger vers la bonne mÃÂ©thode selon le type d'affectation
    if (affectationData.type === 'principal') {
      return api.affectations.enseignantsPrincipaux.update(id, affectationData);
    } else if (affectationData.type === 'matiere') {
      return api.affectations.enseignantsMatieres.update(id, affectationData);
    } else {
      return {
        success: false,
        error: 'Type d\'affectation non spÃÂ©cifiÃÂ©'
      };
    }
  },

  getById: async (id, type = null) => {
    try {
      let url = `${API_URL}/affectations/${id}`;
      if (type) url += `?type=${type}`;

      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration de l\'affectation:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
},


// Dans api.js, ajouter cette nouvelle section aprÃÂ¨s les autres services

attendance: {
  // Enregistrer un badgeage
  recordBadge: async (badgeData) => {
    try {
      const response = await fetch(`${API_URL}/attendance/badge`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          badge_number: badgeData.badge_number,
          event_type: badgeData.event_type, // 'entry' ou 'exit'
          location: badgeData.location || 'EntrÃÂ©e principale',
          device_id: badgeData.device_id || 'web_interface'
        })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors du badgeage:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // RÃÂ©cupÃÂ©rer les donnÃÂ©es de prÃÂ©sence
  getAttendanceData: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.date) queryParams.append('date', filters.date);
      if (filters.period) queryParams.append('period', filters.period);
      if (filters.employe_id) queryParams.append('employe_id', filters.employe_id);

      const response = await fetch(`${API_URL}/attendance/data?${queryParams}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des donnÃÂ©es de prÃÂ©sence:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // RÃÂ©cupÃÂ©rer les statistiques de prÃÂ©sence
  getStats: async (date = null) => {
    try {
      const queryParams = new URLSearchParams();
      if (date) queryParams.append('date', date);

      const response = await fetch(`${API_URL}/attendance/stats?${queryParams}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des statistiques:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Badgeage manuel par l'admin
  manualBadge: async (employeeId, eventType, notes = '') => {
    try {
      // D'abord rÃÂ©cupÃÂ©rer le numÃÂ©ro de badge de l'employÃÂ©
      const employeeResponse = await api.employes.getOne(employeeId);
      if (!employeeResponse.success) {
        throw new Error('EmployÃÂ© non trouvÃÂ©');
      }

      const employee = employeeResponse.data;
      const response = await fetch(`${API_URL}/attendance/manual-badge`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          badge_number: employee.numero_badge,
          event_type: eventType,
          location: 'Badgeage manuel',
          device_id: 'admin_interface',
          notes: notes,
          manual_entry: true
        })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors du badgeage manuel:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Simuler un badgeage pour les tests
  simulateBadge: async (employeeId, eventType = 'entry') => {
    try {
      const employeeResponse = await api.employes.getOne(employeeId);
      if (!employeeResponse.success) {
        throw new Error('EmployÃÂ© non trouvÃÂ©');
      }

      const employee = employeeResponse.data;
      return await api.attendance.recordBadge({
        badge_number: employee.numero_badge || `BD${employeeId.toString().padStart(5, '0')}`,
        event_type: eventType,
        location: 'Simulation',
        device_id: 'simulator'
      });
    } catch (error) {
      console.error('Erreur lors de la simulation de badgeage:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

 generateSessions: async (data) => {
  try {
    const response = await fetch(`${API_URL}/attendance/generate-sessions`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      credentials: 'include',          // <-- cookies de session
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur lors de la gÃÂ©nÃÂ©ration des sessions:', error);
    return {
      success: false,
      error: error.message
    };
  }
},


  getSessionsWithBadgeStatus: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.date) queryParams.append('date', filters.date);
      if (filters.employe_id) queryParams.append('employe_id', filters.employe_id);
      if (filters.period) queryParams.append('period', filters.period);

      const response = await fetch(`${API_URL}/attendance/sessions-status?${queryParams}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des sessions:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // ÃÂ ajouter dans ton objet attendance existant

  validateSession: async (sessionId, action, notes = '') => {
    try {
      const response = await fetch(`${API_URL}/attendance/sessions/${sessionId}/validate`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: action, // 'VALIDE', 'JUSTIFIE', 'RETENUE'
          notes: notes
        })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la validation de session:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },


// Calculs de paie basÃÂ©s sur prÃÂ©sences
calculatePayrollHours: async (employeId, moisScolaireId) => {
  try {
    const response = await fetch(`${API_URL}/attendance/payroll/${employeId}/${moisScolaireId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur calcul paie prÃÂ©sences:', error);
    return { success: false, error: error.message };
  }
},

generateMonthlyPayroll: async (moisScolaireId) => {
  try {
    const token = localStorage.getItem('token');
    // VÃÂ©rification token avant appel
    console.log('Ã°ÂÂÂ Token prÃÂ©sent:', token ? 'OUI' : 'NON');
    if (token) {
      console.log('Ã°ÂÂÂ Token (premiers chars):', token.substring(0, 20) + '...');
    }

    const headers = getAuthHeaders();
    console.log('Ã°ÂÂÂ¤ Headers finaux:', headers);

    const url = `${API_URL}/attendance/payroll/generate/${moisScolaireId}`;
    console.log('Ã°ÂÂÂ URL appelÃÂ©e:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: headers
    });

    console.log('Ã°ÂÂÂ¥ Statut rÃÂ©ponse:', response.status);
    console.log('Ã°ÂÂÂ¥ Headers rÃÂ©ponse:', response.headers);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Ã¢ÂÂ Erreur serveur complÃÂ¨te:', errorText);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    return handleResponse(response);
  } catch (error) {
    console.error('Ã¢ÂÂ Erreur complÃÂ¨te generateMonthlyPayroll:', error);
    return { success: false, error: error.message };
  }
},


validatePayrollBatch: async (batchId, data) => {
  try {
    const response = await fetch(`${API_URL}/attendance/payroll/batch/${batchId}/validate`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur validation batch paie:', error);
    return { success: false, error: error.message };
  }
},

getPayrollBatchDetails: async (batchId) => {
  try {
    const response = await fetch(`${API_URL}/attendance/payroll/batch/${batchId}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur rÃÂ©cupÃÂ©ration dÃÂ©tails batch:', error);
    return { success: false, error: error.message };
  }
}


},


notifications: {
  // CrÃÂ©er une notification
  create: async (notificationData) => {
    try {
      const response = await fetch(`${API_URL}/notifications`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(notificationData)
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la crÃÂ©ation de la notification:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // RÃÂ©cupÃÂ©rer les notifications d'un utilisateur
  getByUser: async (userId, limit = 10, offset = 0) => {
    try {
      const response = await fetch(`${API_URL}/notifications/user/${userId}?limit=${limit}&offset=${offset}`, {
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des notifications:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Marquer une notification comme lue
  markAsRead: async (notificationId) => {
    try {
      const response = await fetch(`${API_URL}/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors du marquage comme lu:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Marquer toutes les notifications comme lues
  markAllAsRead: async (userId) => {
    try {
      const response = await fetch(`${API_URL}/notifications/user/${userId}/read-all`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors du marquage global comme lu:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Supprimer une notification
  delete: async (notificationId) => {
    try {
      const response = await fetch(`${API_URL}/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la suppression de la notification:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
},

// ====================================
// Ã°ÂÂÂ§ SECTION MESSAGERIE CORRIGÃÂE - REMPLACER TOUTE LA SECTION
// ====================================

messagerie: {
  // ====================================
  // ROUTES PRINCIPALES
  // ====================================

  getDashboard: async () => {
    try {
      console.log('Ã°ÂÂÂ [API] Appel getDashboard messagerie');
      const response = await fetch(`${API_URL}/messagerie/dashboard`, {
        headers: getAuthHeaders()
      });

      console.log('Ã°ÂÂÂ¡ [API] RÃÂ©ponse dashboard:', {
        status: response.status,
        ok: response.ok
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getDashboard:', error);
      return {
        success: false,
        error: error.message,
        data: {
          conversations: [],
          groups: [],
          unreadCount: 0,
          onlineUsers: [],
          recentNotifications: []
        }
      };
    }
  },

  getConversations: async () => {
    try {
      const response = await fetch(`${API_URL}/messagerie/conversations`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getConversations:', error);
      return { success: false, error: error.message, data: [] };
    }
  },

  getMessages: async (conversationId) => {
    try {
      const response = await fetch(`${API_URL}/messagerie/conversations/${conversationId}/messages`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getMessages:', error);
      return { success: false, error: error.message, data: [] };
    }
  },


sendMessage: async (conversationId, messageData) => {
  try {
    // Ã°ÂÂÂ¨ VALIDATION CRITIQUE DE L'ID
    if (!conversationId || conversationId === 'undefined' || conversationId.toString().includes('group_')) {
      throw new Error(`ID de conversation invalide: ${conversationId}`);
    }

    console.log('Ã°ÂÂÂ [API] Envoi message:', { conversationId, hasContent: !!messageData.content });

    const formData = new FormData();

    if (messageData.content) {
      formData.append('content', messageData.content);
    }

    formData.append('messageType', messageData.messageType || 'text');

    if (messageData.attachments && messageData.attachments.length > 0) {
      messageData.attachments.forEach(file => {
        formData.append('attachments', file);
      });
    }

    const response = await fetch(`${API_URL}/messagerie/conversations/${conversationId}/messages`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: formData
    });

    return handleResponse(response);
  } catch (error) {
    console.error('Ã¢ÂÂ [API] Erreur sendMessage:', error);
    return { success: false, error: error.message };
  }
},


// Ã°ÂÂÂ§ AJOUTER CETTE MÃÂTHODE dans ton API messagerie (aprÃÂ¨s sendMessage)

sendMessageWithFiles: async (conversationId, messageData) => {
  try {
    // Ã°ÂÂÂ¨ VALIDATION CRITIQUE DE L'ID
    if (!conversationId || conversationId === 'undefined' || conversationId.toString().includes('group_')) {
      throw new Error(`ID de conversation invalide: ${conversationId}`);
    }

    console.log('Ã°ÂÂÂ [API] sendMessageWithFiles:', {
      conversationId,
      hasContent: !!messageData.content,
      hasFormData: !!messageData.formData,
      hasAttachments: messageData.attachments?.length || 0
    });

    let response;

    // Ã°ÂÂÂ CAS 1: DonnÃÂ©es FormData (avec fichiers)
    if (messageData.formData) {
      console.log('Ã°ÂÂÂ [API] Envoi avec FormData (fichiers inclus)');

      response = await fetch(`${API_URL}/messagerie/conversations/${conversationId}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
          // Ã¢ÂÂ Ã¯Â¸Â PAS de Content-Type pour FormData - le navigateur le gÃÂ¨re
        },
        body: messageData.formData
      });
    }

    // Ã°ÂÂÂ¬ CAS 2: Message JSON normal (texte seulement)
    else {
      console.log('Ã°ÂÂÂ¬ [API] Envoi message texte normal');

      const formData = new FormData();

      if (messageData.content) {
        formData.append('content', messageData.content);
      }

      formData.append('messageType', messageData.messageType || 'text');

      // Ajouter les fichiers s'il y en a
      if (messageData.attachments && messageData.attachments.length > 0) {
        messageData.attachments.forEach(file => {
          formData.append('attachments', file);
        });
      }

      response = await fetch(`${API_URL}/messagerie/conversations/${conversationId}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
    }

    // Ã°ÂÂÂ GESTION DE LA RÃÂPONSE
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Ã¢ÂÂ [API] Erreur HTTP:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData
      });
      throw new Error(errorData.error || errorData.message || `Erreur HTTP ${response.status}`);
    }

    const data = await response.json();
    console.log('Ã¢ÂÂ [API] Message envoyÃÂ© avec succÃÂ¨s:', {
      messageId: data.data?.id,
      hasAttachments: data.data?.attachments?.length || 0
    });

    return {
      success: true,
      data: data.data || data
    };

  } catch (error) {
    console.error('Ã¢ÂÂ [API] Erreur sendMessageWithFiles:', error);
    return {
      success: false,
      error: error.message
    };
  }
},




// Ã°ÂÂÂ§ CORRECTION 2 - createOrGetGroupConversation avec BODY

createOrGetGroupConversation: async (groupId) => {
  try {
    // Ã°ÂÂÂ¨ VALIDATION DE L'ID GROUPE
    if (!groupId || isNaN(parseInt(groupId))) {
      throw new Error(`ID de groupe invalide: ${groupId}`);
    }

    const cleanGroupId = parseInt(groupId);
    console.log('Ã°ÂÂÂ [API] CrÃÂ©ation/rÃÂ©cupÃÂ©ration conversation de groupe:', cleanGroupId);

    const response = await fetch(`${API_URL}/messagerie/groups/${cleanGroupId}/conversation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      // Ã°ÂÂÂ§ AJOUTER LE BODY MANQUANT
      body: JSON.stringify({
        groupId: cleanGroupId
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Erreur lors de la crÃÂ©ation de la conversation de groupe');
    }

    const data = await response.json();
    console.log('Ã¢ÂÂ [API] Conversation de groupe crÃÂ©ÃÂ©e/rÃÂ©cupÃÂ©rÃÂ©e:', data.data);
    return { success: true, data: data.data }; // Ã°ÂÂÂ§ Retourner data.data pour correspondre au format attendu
  } catch (error) {
    console.error('Ã¢ÂÂ [API] Erreur conversation de groupe:', error);
    return { success: false, error: error.message };
  }
},

  createIndividualConversation: async (targetEmployeId) => {
    try {
      const response = await fetch(`${API_URL}/messagerie/conversations/individual`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          targetEmployeId: parseInt(targetEmployeId)
        })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur createIndividualConversation:', error);
      return { success: false, error: error.message };
    }
  },

  markAsRead: async (conversationId) => {
    try {
      const response = await fetch(`${API_URL}/messagerie/conversations/${conversationId}/read-all`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur markAsRead:', error);
      return { success: false, error: error.message };
    }
  },



  markMessageAsRead: async (messageId) => {
  try {
    console.log('Ã°ÂÂÂ [API] Marquage message comme lu:', { messageId });

    const response = await fetch(`${API_URL}/messagerie/messages/${messageId}/read`, {
      method: 'POST',
      headers: getAuthHeaders()
    });

    const result = handleResponse(response);

    if (result.success) {
      console.log('Ã¢ÂÂ [API] Message marquÃÂ© comme lu:', messageId);
    }

    return result;
  } catch (error) {
    console.error('Ã¢ÂÂ [API] Erreur markMessageAsRead:', error);
    return { success: false, error: error.message };
  }
},

markConversationAsRead: async (conversationId) => {
  try {
    console.log('Ã°ÂÂÂ [API] Marquage conversation comme lue:', { conversationId });

    const response = await fetch(`${API_URL}/messagerie/conversations/${conversationId}/read-all`, {
      method: 'POST',
      headers: getAuthHeaders()
    });

    const result = handleResponse(response);

    if (result.success) {
      console.log('Ã¢ÂÂ [API] Conversation marquÃÂ©e comme lue:', conversationId);
    }

    return result;
  } catch (error) {
    console.error('Ã¢ÂÂ [API] Erreur markConversationAsRead:', error);
    return { success: false, error: error.message };
  }
},

// Ã°ÂÂÂ§ CORRIGE TON getUnreadCount EXISTANT (ligne ~420 environ)
getUnreadCount: async () => {
  try {
    console.log('Ã°ÂÂÂ [API] RÃÂ©cupÃÂ©ration nombre messages non lus');

    const response = await fetch(`${API_URL}/messagerie/messages/unread/count`, {
      headers: getAuthHeaders()
    });

    const result = await handleResponse(response);

    if (result.success) {
      const count = result.data?.unreadCount || 0;
      console.log('Ã¢ÂÂ [API] Messages non lus:', count);
      return { success: true, data: { unreadCount: count } };
    }

    return result;
  } catch (error) {
    console.error('Ã¢ÂÂ [API] Erreur getUnreadCount:', error);
    return { success: false, error: error.message, data: { unreadCount: 0 } };
  }
},

// Ã°ÂÂÂ NOUVELLE MÃÂTHODE : Obtenir le statut de lecture d'un message
getMessageReadStatus: async (messageId) => {
  try {
    console.log('Ã°ÂÂÂ [API] RÃÂ©cupÃÂ©ration statut lecture message:', { messageId });

    // Note: Cette route n'existe peut-ÃÂªtre pas encore, ÃÂ  implÃÂ©menter cÃÂ´tÃÂ© backend si nÃÂ©cessaire
    const response = await fetch(`${API_URL}/messagerie/messages/${messageId}/read-status`, {
      headers: getAuthHeaders()
    });

    return handleResponse(response);
  } catch (error) {
    console.error('Ã¢ÂÂ [API] Erreur getMessageReadStatus:', error);
    return { success: false, error: error.message };
  }
},

// Ã°ÂÂÂ MÃÂTHODE UTILITAIRE : Marquer automatiquement au scroll
markVisibleMessagesAsRead: async (conversationId, visibleMessageIds) => {
  try {
    if (!visibleMessageIds || visibleMessageIds.length === 0) {
      return { success: true, data: { markedCount: 0 } };
    }

    console.log('Ã°ÂÂÂ [API] Marquage messages visibles:', {
      conversationId,
      messageCount: visibleMessageIds.length
    });

    // Option 1: Marquer individuellement (si on veut le contrÃÂ´le prÃÂ©cis)
    const results = await Promise.allSettled(
      visibleMessageIds.map(messageId =>
        this.markMessageAsRead(messageId)
      )
    );

    const successCount = results.filter(r => r.status === 'fulfilled' && r.value.success).length;

    console.log('Ã¢ÂÂ [API] Messages marquÃÂ©s comme lus:', successCount);

    return {
      success: true,
      data: {
        markedCount: successCount,
        totalProcessed: visibleMessageIds.length
      }
    };

    // Option 2: Marquer toute la conversation (plus simple)
    // return this.markConversationAsRead(conversationId);

  } catch (error) {
    console.error('Ã¢ÂÂ [API] Erreur markVisibleMessagesAsRead:', error);
    return { success: false, error: error.message };
  }
},


  // ====================================
  // GROUPES ET DIFFUSION
  // ====================================

  getGroups: async () => {
    try {
      const response = await fetch(`${API_URL}/messagerie/groups`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getGroups:', error);
      return { success: false, error: error.message, data: [] };
    }
  },

  getAllGroups: async () => {
    try {
      console.log('Ã°ÂÂÂ [API] Chargement de tous les groupes');
      const response = await fetch(`${API_URL}/messagerie/groups/all`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getAllGroups:', error);
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  },

  getMyGroups: async () => {
    try {
      console.log('Ã°ÂÂÂ [API] Chargement de mes groupes uniquement');
      const response = await fetch(`${API_URL}/messagerie/groups/mine`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getMyGroups:', error);
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  },

  broadcastToGroup: async (groupId, messageData) => {
    try {
      console.log('Ã°ÂÂÂ¤ [API] Diffusion vers groupe:', { groupId, messageData });

      const formData = new FormData();

      if (messageData.content) {
        formData.append('content', messageData.content);
      }

      formData.append('messageType', messageData.messageType || 'text');

      if (messageData.attachments && messageData.attachments.length > 0) {
        messageData.attachments.forEach(file => {
          formData.append('attachments', file);
        });
      }

      const response = await fetch(`${API_URL}/messagerie/groups/${groupId}/broadcast`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur broadcastToGroup:', error);
      return { success: false, error: error.message };
    }
  },

  // ====================================
  // Ã°ÂÂÂ NOUVELLES MÃÂTHODES POUR DIFFUSION TYPE EMAIL
  // ====================================

  getRecipientOptions: async (searchTerm = '') => {
    try {
      console.log('Ã°ÂÂÂ [API] Chargement options destinataires:', { searchTerm });

      const url = searchTerm
        ? `${API_URL}/messagerie/recipients?search=${encodeURIComponent(searchTerm)}`
        : `${API_URL}/messagerie/recipients`;

      const response = await fetch(url, {
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getRecipientOptions:', error);
      return {
        success: false,
        error: error.message,
        data: {
          groups: [],
          individuals: []
        }
      };
    }
  },

  sendUnifiedMessage: async (recipients, messageData) => {
    try {
      console.log('Ã°ÂÂÂ¤ [API] Envoi message unifiÃÂ©:', {
        recipients: recipients.length,
        hasContent: !!messageData.content,
        hasAttachments: messageData.attachments?.length || 0
      });

      const formData = new FormData();

      // Ajouter les destinataires
      formData.append('recipients', JSON.stringify(recipients));

      // Ajouter le contenu
      if (messageData.content) {
        formData.append('content', messageData.content);
      }

      formData.append('messageType', messageData.messageType || 'text');

      // Ajouter les piÃÂ¨ces jointes
      if (messageData.attachments && messageData.attachments.length > 0) {
        messageData.attachments.forEach(file => {
          formData.append('attachments', file);
        });
      }

      const response = await fetch(`${API_URL}/messagerie/send-unified`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
          // Pas de Content-Type pour FormData
        },
        body: formData
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur sendUnifiedMessage:', error);
      return { success: false, error: error.message };
    }
  },

  // ====================================
  // EMPLOYÃÂS ET UTILISATEURS
  // ====================================

  getEmployeesWithAccess: async (searchTerm = '') => {
    try {
      console.log('Ã°ÂÂÂ [API] Chargement employÃÂ©s avec accÃÂ¨s:', { searchTerm });

      const url = searchTerm
        ? `${API_URL}/messagerie/employees?search=${encodeURIComponent(searchTerm)}`
        : `${API_URL}/messagerie/employees`;

      const response = await fetch(url, {
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getEmployeesWithAccess:', error);
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  },

  // ====================================
  // RECHERCHE
  // ====================================

  searchMessages: async (query) => {
    try {
      const response = await fetch(`${API_URL}/messagerie/search/messages?q=${encodeURIComponent(query)}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur searchMessages:', error);
      return { success: false, error: error.message, data: [] };
    }
  },

  searchConversations: async (query) => {
    try {
      const response = await fetch(`${API_URL}/messagerie/search/conversations?q=${encodeURIComponent(query)}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur searchConversations:', error);
      return { success: false, error: error.message, data: [] };
    }
  },

  searchUsers: async (query) => {
    if (!query || query.trim().length < 2) {
      console.log('Ã°ÂÂÂ [API] Recherche trop courte, retour liste vide');
      return { success: true, data: [] };
    }

    try {
      console.log('Ã°ÂÂÂ [API] Recherche utilisateurs:', { query });
      const response = await fetch(`${API_URL}/messagerie/search/users?q=${encodeURIComponent(query.trim())}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur searchUsers:', error);
      return { success: false, error: error.message, data: [] };
    }
  },

  // ====================================
  // STATUTS ET NOTIFICATIONS
  // ====================================

  getOnlineUsers: async () => {
    try {
      const response = await fetch(`${API_URL}/messagerie/status/online`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getOnlineUsers:', error);
      return { success: false, error: error.message, data: [] };
    }
  },

  getNotifications: async () => {
    try {
      const response = await fetch(`${API_URL}/messagerie/notifications`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getNotifications:', error);
      return { success: false, error: error.message, data: [] };
    }
  },

  getUnreadCount: async () => {
    try {
      const response = await fetch(`${API_URL}/messagerie/messages/unread/count`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getUnreadCount:', error);
      return { success: false, error: error.message, data: { count: 0 } };
    }
  },

  // ====================================
  // UTILITAIRES ET TESTS
  // ====================================

  testConnection: async () => {
    try {
      console.log('Ã°ÂÂ§Âª [API] Test de connexion messagerie');
      const response = await fetch(`${API_URL}/messagerie/test`, {
        headers: getAuthHeaders()
      });

      console.log('Ã°ÂÂÂ¡ [API] RÃÂ©ponse test:', {
        status: response.status,
        ok: response.ok
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur testConnection:', error);
      return { success: false, error: error.message };
    }
  },

  testUser: async () => {
    try {
      console.log('Ã°ÂÂ§Âª [API] Test utilisateur messagerie');
      const response = await fetch(`${API_URL}/messagerie/test/user`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur testUser:', error);
      return { success: false, error: error.message };
    }
  },

  // ====================================
  // MÃÂTHODES UTILITAIRES POUR LA DIFFUSION
  // ====================================

  createRecipient: (type, id, name, extra = {}) => {
    return {
      type, // 'group' ou 'individual'
      id,
      name,
      display_name: name,
      ...extra
    };
  },

  validateRecipients: (recipients) => {
    if (!Array.isArray(recipients) || recipients.length === 0) {
      return { valid: false, error: 'SÃÂ©lectionnez au moins un destinataire' };
    }

    const invalidRecipients = recipients.filter(r =>
      !r.type || !r.id || !r.name ||
      !['group', 'individual'].includes(r.type)
    );

    if (invalidRecipients.length > 0) {
      return { valid: false, error: 'Certains destinataires sont invalides' };
    }

    return { valid: true };
  },

  formatSendResults: (results) => {
    const total = (results.groups_sent?.length || 0) + (results.individuals_sent?.length || 0);
    const errors = results.errors?.length || 0;

    let message = '';
    if (total > 0 && errors === 0) {
      message = `Message envoyÃÂ© avec succÃÂ¨s ÃÂ  ${total} destinataire${total > 1 ? 's' : ''}`;
    } else if (total > 0 && errors > 0) {
      message = `Message envoyÃÂ© ÃÂ  ${total} destinataire${total > 1 ? 's' : ''}, ${errors} erreur${errors > 1 ? 's' : ''}`;
    } else {
      message = 'Aucun message n\'a pu ÃÂªtre envoyÃÂ©';
    }

    return {
      message,
      totalSent: total,
      totalErrors: errors,
      success: total > 0,
      details: results
    };
  },


  getMyGroupConversations: async () => {
  try {
    console.log(`Ã°ÂÂÂ [API] getMyGroupConversations`);

    const response = await fetch(`${API_URL}/messagerie/conversations/my-groups`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    const data = await response.json();

    if (!response.ok) {
      console.error('Ã¢ÂÂ [API] Erreur getMyGroupConversations:', data);
      return {
        success: false,
        error: data.error || `Erreur HTTP ${response.status}`
      };
    }

    console.log(`Ã¢ÂÂ [API] ${data.conversations?.length || 0} conversations de groupe trouvÃÂ©es`);
    return {
      success: true,
      data: data.conversations || []
    };

  } catch (error) {
    console.error('Ã¢ÂÂ [API] Exception getMyGroupConversations:', error);
    return {
      success: false,
      error: error.message || 'Erreur rÃÂ©seau'
    };
  }
},


// 1Ã¯Â¸ÂÃ¢ÂÂ£ Archiver une conversation
  archiveConversation: async (conversationId) => {
    try {
      console.log(`Ã°ÂÂÂ¦ [API] Archivage conversation: ${conversationId}`);

      const response = await fetch(`${API_URL}/messagerie/conversations/${conversationId}/archive`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('Ã¢ÂÂ [API] Erreur archivage:', data);
        return {
          success: false,
          error: data.error || `Erreur HTTP ${response.status}`
        };
      }

      console.log(`Ã¢ÂÂ [API] Conversation ${conversationId} archivÃÂ©e`);
      return {
        success: true,
        data: data.data || {}
      };

    } catch (error) {
      console.error(`Ã¢ÂÂ [API] Exception archivage conversation ${conversationId}:`, error);
      return {
        success: false,
        error: error.message || 'Erreur lors de l\'archivage de la conversation'
      };
    }
  },

  // 2Ã¯Â¸ÂÃ¢ÂÂ£ DÃÂ©sarchiver une conversation
  unarchiveConversation: async (conversationId) => {
    try {
      console.log(`Ã°ÂÂÂ¤ [API] DÃÂ©sarchivage conversation: ${conversationId}`);

      const response = await fetch(`${API_URL}/messagerie/conversations/${conversationId}/unarchive`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('Ã¢ÂÂ [API] Erreur dÃÂ©sarchivage:', data);
        return {
          success: false,
          error: data.error || `Erreur HTTP ${response.status}`
        };
      }

      console.log(`Ã¢ÂÂ [API] Conversation ${conversationId} dÃÂ©sarchivÃÂ©e`);
      return {
        success: true,
        data: data.data || {}
      };

    } catch (error) {
      console.error(`Ã¢ÂÂ [API] Exception dÃÂ©sarchivage conversation ${conversationId}:`, error);
      return {
        success: false,
        error: error.message || 'Erreur lors de la restauration de la conversation'
      };
    }
  },

  // 3Ã¯Â¸ÂÃ¢ÂÂ£ Supprimer dÃÂ©finitivement une conversation
  deleteConversation: async (conversationId) => {
    try {
      console.log(`Ã°ÂÂÂÃ¯Â¸Â [API] Suppression dÃÂ©finitive conversation: ${conversationId}`);

      const response = await fetch(`${API_URL}/messagerie/conversations/${conversationId}/delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('Ã¢ÂÂ [API] Erreur suppression:', data);
        return {
          success: false,
          error: data.error || `Erreur HTTP ${response.status}`
        };
      }

      console.log(`Ã¢ÂÂ [API] Conversation ${conversationId} supprimÃÂ©e dÃÂ©finitivement`);
      return {
        success: true,
        data: data.data || {}
      };

    } catch (error) {
      console.error(`Ã¢ÂÂ [API] Exception suppression conversation ${conversationId}:`, error);
      return {
        success: false,
        error: error.message || 'Erreur lors de la suppression de la conversation'
      };
    }
  },

  // 4Ã¯Â¸ÂÃ¢ÂÂ£ RÃÂ©cupÃÂ©rer les conversations archivÃÂ©es
  getArchivedConversations: async () => {
    try {
      console.log('Ã°ÂÂÂ [API] RÃÂ©cupÃÂ©ration conversations archivÃÂ©es');

      const response = await fetch(`${API_URL}/messagerie/conversations/archived`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('Ã¢ÂÂ [API] Erreur rÃÂ©cupÃÂ©ration archives:', data);
        return {
          success: false,
          error: data.error || `Erreur HTTP ${response.status}`,
          data: []
        };
      }

      console.log(`Ã¢ÂÂ [API] ${data.conversations?.length || data.data?.length || 0} conversations archivÃÂ©es rÃÂ©cupÃÂ©rÃÂ©es`);
      return {
        success: true,
        data: data.conversations || data.data || []
      };

    } catch (error) {
      console.error('Ã¢ÂÂ [API] Exception rÃÂ©cupÃÂ©ration conversations archivÃÂ©es:', error);
      return {
        success: false,
        error: error.message || 'Erreur lors de la rÃÂ©cupÃÂ©ration des conversations archivÃÂ©es',
        data: []
      };
    }
  },



},


// ÃÂ ajouter dans api.js - Section TÃÂCHES
// Ajouter cette section aprÃÂ¨s votre section "messagerie" existante

taches: {
  // ============================================================================
  // RÃÂCUPÃÂRATION DES TÃÂCHES
  // ============================================================================

  /**
   * RÃÂ©cupÃÂ©rer toutes les tÃÂ¢ches avec filtres et pagination
   */
  getAll: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      // Ajouter tous les filtres fournis
      Object.keys(filters).forEach(key => {
        if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
          queryParams.append(key, filters[key]);
        }
      });
      if (!queryParams.has('annee_scolaire_id')) {
        const selectedYearId = localStorage.getItem('annee_scolaire_id');
        if (selectedYearId) queryParams.set('annee_scolaire_id', selectedYearId);
      }

      const response = await fetch(`${API_URL}/taches?${queryParams}`, {
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des tÃÂ¢ches:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * RÃÂ©cupÃÂ©rer une tÃÂ¢che spÃÂ©cifique par ID
   */
  getById: async (id) => {
    try {
      const response = await fetch(`${API_URL}/taches/${id}`, {
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration de la tÃÂ¢che:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * RÃÂ©cupÃÂ©rer les statistiques des tÃÂ¢ches
   */
  getStatistiques: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      Object.keys(filters).forEach(key => {
        if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
          queryParams.append(key, filters[key]);
        }
      });
      if (!queryParams.has('annee_scolaire_id')) {
        const selectedYearId = localStorage.getItem('annee_scolaire_id');
        if (selectedYearId) queryParams.set('annee_scolaire_id', selectedYearId);
      }

      const response = await fetch(`${API_URL}/taches/statistiques?${queryParams}`, {
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des statistiques:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * RÃÂ©cupÃÂ©rer les employÃÂ©s d'un groupe pour assignation
   */
  getEmployesGroupe: async (groupe) => {
    try {
      const response = await fetch(`${API_URL}/taches/employes/${groupe}`, {
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la rÃÂ©cupÃÂ©ration des employÃÂ©s:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // ============================================================================
  // CRÃÂATION DE TÃÂCHES
  // ============================================================================

  /**
   * CrÃÂ©er une nouvelle tÃÂ¢che normale
   */
  create: async (tacheData, fichierJoint = null) => {
    try {
      const formData = new FormData();

      // Ajouter les donnÃÂ©es de la tÃÂ¢che
      Object.keys(tacheData).forEach(key => {
        if (tacheData[key] !== null && tacheData[key] !== undefined) {
          formData.append(key, tacheData[key]);
        }
      });
      if (!formData.has('annee_scolaire_id')) {
        const selectedYearId = localStorage.getItem('annee_scolaire_id');
        if (selectedYearId) formData.append('annee_scolaire_id', selectedYearId);
      }

      // Ajouter le fichier joint s'il existe
      if (fichierJoint) {
        formData.append('piece_jointe', fichierJoint);
      }

      const headers = getAuthHeaders();
      delete headers['Content-Type']; // Laisser le navigateur dÃÂ©finir le Content-Type pour FormData

      const response = await fetch(`${API_URL}/taches`, {
        method: 'POST',
        headers: headers,
        body: formData
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la crÃÂ©ation de la tÃÂ¢che:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * CrÃÂ©er une demande de crÃÂ©ation d'employÃÂ©
   */
  createDemandeEmploye: async (ficheData) => {
    try {
      const selectedYearId = localStorage.getItem('annee_scolaire_id');
      const payload = selectedYearId && !ficheData.annee_scolaire_id
        ? { ...ficheData, annee_scolaire_id: selectedYearId }
        : ficheData;

      const response = await fetch(`${API_URL}/taches/creation-employe`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la crÃÂ©ation de la demande d\'employÃÂ©:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // ============================================================================
  // MODIFICATION DES TÃÂCHES
  // ============================================================================

  /**
   * Modifier une tÃÂ¢che existante
   */
  update: async (id, tacheData, fichierJoint = null) => {
    try {
      const formData = new FormData();

      // Ajouter les donnÃÂ©es de la tÃÂ¢che
      Object.keys(tacheData).forEach(key => {
        if (tacheData[key] !== null && tacheData[key] !== undefined) {
          formData.append(key, tacheData[key]);
        }
      });

      // Ajouter le fichier joint s'il existe
      if (fichierJoint) {
        formData.append('piece_jointe', fichierJoint);
      }

      const headers = getAuthHeaders();
      delete headers['Content-Type']; // Laisser le navigateur dÃÂ©finir le Content-Type pour FormData

      const response = await fetch(`${API_URL}/taches/${id}`, {
        method: 'PUT',
        headers: headers,
        body: formData
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la modification de la tÃÂ¢che:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Changer le statut d'une tÃÂ¢che
   */
  changerStatut: async (id, statutData) => {
    try {
      const response = await fetch(`${API_URL}/taches/${id}/statut`, {
        method: 'PATCH',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(statutData)
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Assigner une tÃÂ¢che ÃÂ  un employÃÂ© spÃÂ©cifique
   */
  assigner: async (id, assignationData) => {
    try {
      const response = await fetch(`${API_URL}/taches/${id}/assigner`, {
        method: 'PATCH',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(assignationData)
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de l\'assignation:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // ============================================================================
  // COMMENTAIRES
  // ============================================================================

  /**
   * Ajouter un commentaire ÃÂ  une tÃÂ¢che
   */
  ajouterCommentaire: async (id, commentaireData) => {
    try {
      const response = await fetch(`${API_URL}/taches/${id}/commentaires`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(commentaireData)
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de l\'ajout du commentaire:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // ============================================================================
  // SUPPRESSION
  // ============================================================================

  /**
   * Supprimer une tÃÂ¢che
   */
  delete: async (id) => {
    try {
      const response = await fetch(`${API_URL}/taches/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la suppression de la tÃÂ¢che:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // ============================================================================
  // GESTION DES FICHIERS
  // ============================================================================

  /**
   * TÃÂ©lÃÂ©charger un fichier joint
   */
  downloadFile: async (tacheId, filename) => {
    try {
      const response = await fetch(`${API_URL}/taches/${tacheId}/fichiers/${filename}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Erreur lors du tÃÂ©lÃÂ©chargement');
      }

      // RÃÂ©cupÃÂ©rer le nom du fichier depuis les headers
      const disposition = response.headers.get('Content-Disposition');
      let fileName = filename;
      if (disposition && disposition.includes('filename=')) {
        fileName = disposition.split('filename=')[1].replace(/"/g, '');
      }

      // CrÃÂ©er un blob et dÃÂ©clencher le tÃÂ©lÃÂ©chargement
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error('Erreur lors du tÃÂ©lÃÂ©chargement:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Supprimer un fichier joint
   */
  deleteFile: async (tacheId, filename) => {
    try {
      const response = await fetch(`${API_URL}/taches/${tacheId}/fichiers/${filename}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la suppression du fichier:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // ============================================================================
  // UTILITAIRES
  // ============================================================================

  /**
   * Valider les donnÃÂ©es d'une tÃÂ¢che
   */
  validateTaskData: (data) => {
    const errors = [];

    if (!data.titre || data.titre.trim().length < 3) {
      errors.push("Le titre doit contenir au moins 3 caractÃÂ¨res");
    }

    if (!data.description || data.description.trim().length < 10) {
      errors.push("La description doit contenir au moins 10 caractÃÂ¨res");
    }

    if (!data.assigne_groupe) {
      errors.push("Le groupe assignÃÂ© est requis");
    }

    if (!['normale', 'importante', 'urgente'].includes(data.priorite)) {
      errors.push("PrioritÃÂ© invalide");
    }

    if (data.echeance) {
      const echeanceDate = new Date(data.echeance);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (echeanceDate < today) {
        errors.push("L'ÃÂ©chÃÂ©ance ne peut pas ÃÂªtre dans le passÃÂ©");
      }
    }

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  },

  /**
   * Valider les donnÃÂ©es d'une fiche employÃÂ©
   */
  validateEmployeeData: (data) => {
    const errors = [];

    if (!data.nom || data.nom.trim() === '') {
      errors.push('Le nom est requis');
    }

    if (!data.prenom || data.prenom.trim() === '') {
      errors.push('Le prÃÂ©nom est requis');
    }

    if (!data.sexe || !['M', 'F', 'Autre'].includes(data.sexe)) {
      errors.push('Le sexe est requis (M, F, Autre)');
    }

    if (!data.telephone || data.telephone.trim() === '') {
      errors.push('Le tÃÂ©lÃÂ©phone est requis');
    }

    if (!data.poste || data.poste.trim() === '') {
      errors.push('Le poste est requis');
    }

    if (!data.typeContrat || data.typeContrat.trim() === '') {
      errors.push('Le type de contrat est requis');
    }

    if (!data.departement || data.departement.trim() === '') {
      errors.push('Le dÃÂ©partement est requis');
    }

    // Validation du salaire
    if (data.salaire && (isNaN(data.salaire) || parseFloat(data.salaire) <= 0)) {
      errors.push('Le salaire doit ÃÂªtre un nombre positif');
    }

    // Validation du tÃÂ©lÃÂ©phone (format basique)
    if (data.telephone && !/^\+?[\d\s\-\(\)]{8,15}$/.test(data.telephone.trim())) {
      errors.push('Format de tÃÂ©lÃÂ©phone invalide');
    }

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  },

  /**
   * Formater les donnÃÂ©es de filtres pour l'URL
   */
  formatFilters: (filters) => {
    const formatted = {};

    Object.keys(filters).forEach(key => {
      const value = filters[key];
      if (value !== null && value !== undefined && value !== '' && value !== 'all') {
        formatted[key] = value;
      }
    });
    if (!formatted.annee_scolaire_id) {
      const selectedYearId = localStorage.getItem('annee_scolaire_id');
      if (selectedYearId) formatted.annee_scolaire_id = selectedYearId;
    }

    return formatted;
  },

  /**
   * Obtenir les options de prioritÃÂ©
   */
  getPriorityOptions: () => [
    { value: 'normale', label: 'Normale', color: 'gray' },
    { value: 'importante', label: 'Importante', color: 'orange' },
    { value: 'urgente', label: 'Urgente', color: 'red' }
  ],

  /**
   * Obtenir les options de statut
   */
  getStatusOptions: () => [
    { value: 'nouveau', label: 'Nouveau', color: 'blue' },
    { value: 'en_cours', label: 'En cours', color: 'yellow' },
    { value: 'termine', label: 'TerminÃÂ©e', color: 'green' }
  ],

  /**
   * Obtenir les groupes disponibles pour assignation
   */
  getGroupsOptions: () => [
    { value: 'Direction', label: 'Direction' },
    { value: 'Administration', label: 'Administration' },
    { value: 'RH', label: 'Ressources Humaines' },
    { value: 'Enseignants', label: 'Enseignants' },
    { value: 'Surveillance', label: 'Surveillance' },
    { value: 'Technique', label: 'Technique' }
  ],



/**
   * 🆕 Obtenir le timestamp de dernière modification
   */
  getLastUpdateTimestamp: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      // Ajouter tous les filtres fournis
      Object.keys(filters).forEach(key => {
        if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '' && filters[key] !== 'all') {
          queryParams.append(key, filters[key]);
        }
      });
      if (!queryParams.has('annee_scolaire_id')) {
        const selectedYearId = localStorage.getItem('annee_scolaire_id');
        if (selectedYearId) queryParams.set('annee_scolaire_id', selectedYearId);
      }

      const response = await fetch(`${API_URL}/taches/last-update?${queryParams}`, {
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lors de la récupération du timestamp:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },



  // ⬇️ AJOUTE ICI LES NOUVELLES MÉTHODES


/**
 * 🔓 Déverrouiller une tâche et la réouvrir
 */
deverrouiller: async (tacheId) => {
  try {
    const response = await fetch(`${API_URL}/taches/${tacheId}/deverrouiller`, {
      method: 'PATCH',
      headers: getAuthHeaders()
    });

    return handleResponse(response);
  } catch (error) {
    console.error('Erreur lors du déverrouillage de la tâche:', error);
    return {
      success: false,
      error: error.message
    };
  }
}




},


// ============================================================================
// Ã°ÂÂÂ APIS POUR LE SYSTÃÂME DE TRANSITION D'ANNÃÂE SCOLAIRE
// ÃÂ ajouter dans votre fichier api.js existant
// ============================================================================

// Ajouter cette section aprÃÂ¨s votre section "taches" existante

transition: {
  // ============================================================================
  // DASHBOARD ET VUE D'ENSEMBLE
  // ============================================================================

  /**
   * RÃÂ©cupÃÂ©rer les donnÃÂ©es du dashboard principal
   */
  getDashboard: async (annee_scolaire_id) => {
    try {
      console.log('Ã°ÂÂÂ [API] RÃÂ©cupÃÂ©ration dashboard transition:', annee_scolaire_id);

      const response = await fetch(`${API_URL}/transition/dashboard?annee_scolaire_id=${annee_scolaire_id}`, {
        headers: getAuthHeaders()
      });

      const result = await handleResponse(response);
      console.log('Ã¢ÂÂ [API] Dashboard rÃÂ©cupÃÂ©rÃÂ©:', result);

      return result;
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getDashboard:', error);
      return {
        success: false,
        error: error.message,
        data: {
          progression: 0,
          scoreIA: 0,
          nbAttention: 0,
          nbAutoValides: 0,
          examens: {},
          transitions: {},
          familles: {}
        }
      };
    }
  },

  /**
   * RÃÂ©cupÃÂ©rer les statistiques de progression
   */
  getProgression: async (annee_scolaire_id) => {
    try {
      const response = await fetch(`${API_URL}/transition/progression?annee_scolaire_id=${annee_scolaire_id}`, {
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getProgression:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // ============================================================================
  // INITIALISATION ET LANCEMENT IA
  // ============================================================================

  /**
   * Lancer la transition intelligente (calcul des scores IA)
   */
  initierTransition: async (transitionData) => {
    try {
      console.log('Ã°ÂÂ¤Â [API] Lancement transition IA:', transitionData);

      const response = await fetch(`${API_URL}/transition/initier`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          annee_precedente: transitionData.annee_precedente,
          annee_nouvelle: transitionData.annee_nouvelle,
          seuil_validation_auto: transitionData.seuil_validation_auto || 90,
          traiter_examens: transitionData.traiter_examens || true
        })
      });

      const result = await handleResponse(response);
      console.log('Ã¢ÂÂ [API] Transition initiÃÂ©e:', result);

      return result;
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur initierTransition:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * VÃÂ©rifier l'ÃÂ©tat d'avancement de la transition
   */
  getEtatTransition: async (annee_scolaire_id) => {
    try {
      const response = await fetch(`${API_URL}/transition/etat?annee_scolaire_id=${annee_scolaire_id}`, {
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getEtatTransition:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // ============================================================================
  // VALIDATION EXPRESS ET PAR LOTS
  // ============================================================================

  /**
   * RÃÂ©cupÃÂ©rer les classes ÃÂ©ligibles pour validation express (score Ã¢ÂÂ¥90%)
   */
  getClassesValidationExpress: async (annee_scolaire_id) => {
    try {
      const response = await fetch(`${API_URL}/transition/validation-express?annee_scolaire_id=${annee_scolaire_id}`, {
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getClassesValidationExpress:', error);
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  },

  /**
   * Valider une classe entiÃÂ¨re d'un coup (validation express)
   */
  validerClasseComplete: async (classeId, annee_scolaire_id) => {
    try {
      console.log('Ã¢ÂÂ¡ [API] Validation express classe:', classeId);

      const response = await fetch(`${API_URL}/transition/valider-classe/${classeId}`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          annee_scolaire_id: annee_scolaire_id,
          type_validation: 'EXPRESS'
        })
      });

      const result = await handleResponse(response);
      console.log('Ã¢ÂÂ [API] Classe validÃÂ©e:', result);

      return result;
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur validerClasseComplete:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * RÃÂ©cupÃÂ©rer les classes pour validation par lot
   */
  getClassesValidationLot: async (annee_scolaire_id) => {
    try {
      const response = await fetch(`${API_URL}/transition/validation-lot?annee_scolaire_id=${annee_scolaire_id}`, {
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getClassesValidationLot:', error);
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  },

  // ============================================================================
  // CAS D'ATTENTION ET VALIDATION INDIVIDUELLE
  // ============================================================================

  /**
   * RÃÂ©cupÃÂ©rer les ÃÂ©lÃÂ¨ves nÃÂ©cessitant une attention particuliÃÂ¨re
   */
  getCasAttention: async (annee_scolaire_id, filtres = {}) => {
    try {
      const queryParams = new URLSearchParams({
        annee_scolaire_id: annee_scolaire_id,
        ...filtres
      });

      const response = await fetch(`${API_URL}/transition/cas-attention?${queryParams}`, {
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getCasAttention:', error);
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  },

  /**
   * Valider un ÃÂ©lÃÂ¨ve individuellement avec dÃÂ©cision manuelle
   */
  validerEleveIndividuel: async (eleveId, decisionData) => {
    try {
      console.log('Ã°ÂÂÂ¤ [API] Validation individuelle ÃÂ©lÃÂ¨ve:', eleveId, decisionData);

      const response = await fetch(`${API_URL}/transition/valider-eleve/${eleveId}`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          decision: decisionData.decision, // 'VALIDE', 'REFUSE', 'REDOUBLEMENT'
          classe_proposee_id: decisionData.classe_proposee_id,
          commentaire_admin: decisionData.commentaire || '',
          annee_scolaire_id: decisionData.annee_scolaire_id
        })
      });

      const result = await handleResponse(response);
      console.log('Ã¢ÂÂ [API] ÃÂlÃÂ¨ve validÃÂ© individuellement:', result);

      return result;
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur validerEleveIndividuel:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * RÃÂ©cupÃÂ©rer le dÃÂ©tail d'un ÃÂ©lÃÂ¨ve pour validation manuelle
   */
  getDetailEleveValidation: async (eleveId, annee_scolaire_id) => {
    try {
      const response = await fetch(`${API_URL}/transition/eleve/${eleveId}/detail?annee_scolaire_id=${annee_scolaire_id}`, {
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getDetailEleveValidation:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // ============================================================================
  // GESTION DES LAURÃÂATS ET EXAMENS
  // ============================================================================

  /**
   * RÃÂ©cupÃÂ©rer le palmarÃÂ¨s complet des laurÃÂ©ats
   */
  getPalmares: async (annee_scolaire_id) => {
    try {
      const response = await fetch(`${API_URL}/transition/palmares?annee_scolaire_id=${annee_scolaire_id}`, {
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getPalmares:', error);
      return {
        success: false,
        error: error.message,
        data: {
          bac: [],
          bepc: [],
          cep: [],
          statistiques: {}
        }
      };
    }
  },

  /**
   * RÃÂ©cupÃÂ©rer les statistiques d'examens
   */
  getStatistiquesExamens: async (annee_scolaire_id) => {
    try {
      const response = await fetch(`${API_URL}/transition/examens/statistiques?annee_scolaire_id=${annee_scolaire_id}`, {
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getStatistiquesExamens:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * GÃÂ©nÃÂ©rer les certificats de rÃÂ©ussite automatiquement
   */
  genererCertificats: async (type_examen, annee_scolaire_id) => {
    try {
      console.log('Ã°ÂÂÂ [API] GÃÂ©nÃÂ©ration certificats:', type_examen);

      const response = await fetch(`${API_URL}/transition/certificats/generer`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type_examen: type_examen, // 'BAC', 'BEPC', 'CEP'
          annee_scolaire_id: annee_scolaire_id
        })
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur genererCertificats:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Publier le palmarÃÂ¨s sur le site web
   */
  publierPalmares: async (annee_scolaire_id, options = {}) => {
    try {
      const response = await fetch(`${API_URL}/transition/palmares/publier`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          annee_scolaire_id: annee_scolaire_id,
          inclure_photos: options.inclure_photos || false,
          publier_site_web: options.publier_site_web || true
        })
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur publierPalmares:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // ============================================================================
  // ANALYTICS IA ET PERFORMANCE
  // ============================================================================

  /**
   * RÃÂ©cupÃÂ©rer les mÃÂ©triques de performance de l'IA
   */
  getAnalyticsIA: async (annee_scolaire_id) => {
    try {
      const response = await fetch(`${API_URL}/transition/analytics/ia?annee_scolaire_id=${annee_scolaire_id}`, {
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getAnalyticsIA:', error);
      return {
        success: false,
        error: error.message,
        data: {
          precisionIA: 0,
          autoValidation: 0,
          patternsDetectes: 0,
          gainTemps: 0,
          distributionScores: [],
          facteursInfluence: []
        }
      };
    }
  },

  /**
   * RÃÂ©cupÃÂ©rer les prÃÂ©dictions pour l'annÃÂ©e suivante
   */
  getPredictions: async (annee_cible) => {
    try {
      const response = await fetch(`${API_URL}/transition/predictions?annee_cible=${annee_cible}`, {
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getPredictions:', error);
      return {
        success: false,
        error: error.message,
        data: {
          effectifPrevu: 0,
          revenusEstimes: 0,
          classesRisque: []
        }
      };
    }
  },

  /**
   * RÃÂ©cupÃÂ©rer l'historique des transitions prÃÂ©cÃÂ©dentes
   */
  getHistoriqueTransitions: async (nb_annees = 3) => {
    try {
      const response = await fetch(`${API_URL}/transition/historique?nb_annees=${nb_annees}`, {
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getHistoriqueTransitions:', error);
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  },

  // ============================================================================
  // CLÃÂTURE DÃÂFINITIVE D'ANNÃÂE
  // ============================================================================

  /**
   * VÃÂ©rifier si l'annÃÂ©e peut ÃÂªtre clÃÂ´turÃÂ©e
   */
  verifierConditionsCloture: async (annee_scolaire_id) => {
    try {
      console.log('Ã°ÂÂÂ [API] VÃÂ©rification conditions clÃÂ´ture:', annee_scolaire_id);

      const response = await fetch(`${API_URL}/transition/verifier-cloture?annee_scolaire_id=${annee_scolaire_id}`, {
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur verifierConditionsCloture:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Effectuer la clÃÂ´ture dÃÂ©finitive de l'annÃÂ©e
   */
  cloturerAnneeDefinitive: async (cloture_data) => {
    try {
      console.log('Ã°ÂÂÂ [API] ClÃÂ´ture dÃÂ©finitive annÃÂ©e:', cloture_data);

      const response = await fetch(`${API_URL}/transition/cloturer-definitive`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          annee_a_cloturer: cloture_data.annee_a_cloturer,
          annee_nouvelle: cloture_data.annee_nouvelle,
          confirmation_phrase: cloture_data.confirmation_phrase,
          sauvegarde_confirmee: cloture_data.sauvegarde_confirmee || true,
          responsabilite_confirmee: cloture_data.responsabilite_confirmee || true
        })
      });

      const result = await handleResponse(response);
      console.log('Ã¢ÂÂ [API] AnnÃÂ©e clÃÂ´turÃÂ©e dÃÂ©finitivement:', result);

      return result;
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur cloturerAnneeDefinitive:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * GÃÂ©nÃÂ©rer le rapport final de clÃÂ´ture
   */
  genererRapportFinal: async (annee_cloturee, annee_nouvelle) => {
    try {
      const response = await fetch(`${API_URL}/transition/rapport-final`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Accept': 'application/pdf'
        },
        body: JSON.stringify({
          annee_cloturee: annee_cloturee,
          annee_nouvelle: annee_nouvelle
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la gÃÂ©nÃÂ©ration du rapport');
      }

      const pdfBlob = await response.blob();
      return {
        success: true,
        data: pdfBlob,
        filename: `rapport-cloture-${annee_cloturee}.pdf`
      };
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur genererRapportFinal:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // ============================================================================
  // AUDIT ET HISTORIQUE
  // ============================================================================

  /**
   * RÃÂ©cupÃÂ©rer l'historique des actions de transition
   */
  getHistoriqueActions: async (annee_scolaire_id, filtre = {}) => {
    try {
      const queryParams = new URLSearchParams({
        annee_scolaire_id: annee_scolaire_id,
        ...filtre
      });

      const response = await fetch(`${API_URL}/transition/audit/actions?${queryParams}`, {
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getHistoriqueActions:', error);
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  },

  /**
   * RÃÂ©cupÃÂ©rer les statistiques de sÃÂ©curitÃÂ© du systÃÂ¨me
   */
  getStatistiquesSecurite: async () => {
    try {
      const response = await fetch(`${API_URL}/transition/audit/securite`, {
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getStatistiquesSecurite:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Exporter les donnÃÂ©es de transition
   */
  exporterDonnees: async (annee_scolaire_id, format = 'excel') => {
    try {
      console.log('Ã°ÂÂÂ [API] Export donnÃÂ©es transition:', format);

      const response = await fetch(`${API_URL}/transition/export?annee_scolaire_id=${annee_scolaire_id}&format=${format}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'export');
      }

      const blob = await response.blob();
      const extension = format === 'excel' ? 'xlsx' : format;

      return {
        success: true,
        data: blob,
        filename: `transition-${annee_scolaire_id}.${extension}`
      };
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur exporterDonnees:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // ============================================================================
  // UTILITAIRES ET HELPERS
  // ============================================================================

  /**
   * Tester la connexion au systÃÂ¨me de transition
   */
  testerConnexion: async () => {
    try {
      console.log('Ã°ÂÂ§Âª [API] Test connexion systÃÂ¨me transition');

      const response = await fetch(`${API_URL}/transition/test`, {
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur testerConnexion:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * RÃÂ©cupÃÂ©rer la configuration actuelle du systÃÂ¨me
   */
  getConfiguration: async () => {
    try {
      const response = await fetch(`${API_URL}/transition/config`, {
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getConfiguration:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Mettre ÃÂ  jour la configuration du systÃÂ¨me
   */
  updateConfiguration: async (configData) => {
    try {
      const response = await fetch(`${API_URL}/transition/config`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(configData)
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur updateConfiguration:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Valider les donnÃÂ©es de transition avant traitement
   */
  validerDonneesTransition: (data) => {
    const errors = [];

    if (!data.annee_precedente || !/^\d{4}-\d{4}$/.test(data.annee_precedente)) {
      errors.push("AnnÃÂ©e prÃÂ©cÃÂ©dente invalide (format: 2023-2024)");
    }

    if (!data.annee_nouvelle || !/^\d{4}-\d{4}$/.test(data.annee_nouvelle)) {
      errors.push("Nouvelle annÃÂ©e invalide (format: 2024-2025)");
    }

    if (data.seuil_validation_auto && (data.seuil_validation_auto < 50 || data.seuil_validation_auto > 100)) {
      errors.push("Seuil de validation automatique doit ÃÂªtre entre 50% et 100%");
    }

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  },

  /**
   * Formater les donnÃÂ©es pour l'affichage
   */
  formaterDonneesAffichage: (data) => {
    return {
      ...data,
      progression: Math.round(data.progression || 0),
      scoreIA: Math.round((data.scoreIA || 0) * 10) / 10,
      dates: {
        ...data.dates,
        formatted: data.dates ? Object.keys(data.dates).reduce((acc, key) => {
          acc[key] = new Date(data.dates[key]).toLocaleDateString('fr-FR');
          return acc;
        }, {}) : {}
      }
    };
  },

  /**
   * Calculer les statistiques de performance
   */
  calculerPerformance: (data) => {
    const total = data.total || 0;
    const valides = data.valides || 0;
    const autoValides = data.autoValides || 0;

    return {
      tauxValidation: total > 0 ? Math.round((valides / total) * 100) : 0,
      tauxAutoValidation: total > 0 ? Math.round((autoValides / total) * 100) : 0,
      gainTemps: autoValides * 0.5, // 30 secondes ÃÂ©conomisÃÂ©es par ÃÂ©lÃÂ¨ve auto-validÃÂ©
      precisionIA: data.precisionIA || 0
    };
  },



   /**
   * Ã¢ÂÂ AJOUTÃÂ : RÃÂ©cupÃÂ©rer la validation par classes (alias ou fonction spÃÂ©cifique)
   */
  getValidationParClasses: async (annee_scolaire_id) => {
    try {
      console.log('Ã°ÂÂÂ [API] Validation par classes:', annee_scolaire_id);

      const response = await fetch(`${API_URL}/transition/validation-classes?annee_scolaire_id=${annee_scolaire_id}`, {
        headers: getAuthHeaders()
      });

      const result = await handleResponse(response);
      console.log('Ã¢ÂÂ [API] Validation classes rÃÂ©cupÃÂ©rÃÂ©e:', result);

      return result;
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getValidationParClasses:', error);
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  },

  // ============================================================================
  // GESTION DES LAURÃÂATS ET EXAMENS - Ã¢ÂÂ IMPLÃÂMENTÃÂES
  // ============================================================================

  /**
   * Ã¢ÂÂ IMPLÃÂMENTÃÂ : RÃÂ©cupÃÂ©rer le palmarÃÂ¨s complet des laurÃÂ©ats
   */
  getPalmares: async (annee_scolaire_id) => {
    try {
      console.log('Ã°ÂÂÂ [API] RÃÂ©cupÃÂ©ration palmarÃÂ¨s:', annee_scolaire_id);

      const response = await fetch(`${API_URL}/transition/palmares?annee_scolaire_id=${annee_scolaire_id}`, {
        headers: getAuthHeaders()
      });

      const result = await handleResponse(response);
      console.log('Ã¢ÂÂ [API] PalmarÃÂ¨s rÃÂ©cupÃÂ©rÃÂ©:', result);

      return result;
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getPalmares:', error);
      return {
        success: false,
        error: error.message,
        data: {
          palmares: [],
          statistiques: {},
          major_general: null
        }
      };
    }
  },

  /**
   * Ã¢ÂÂ IMPLÃÂMENTÃÂ : GÃÂ©nÃÂ©rer les certificats de rÃÂ©ussite automatiquement
   */
  genererCertificats: async (type_examen, annee_scolaire_id) => {
    try {
      console.log('Ã°ÂÂÂ [API] GÃÂ©nÃÂ©ration certificats:', { type_examen, annee_scolaire_id });

      const response = await fetch(`${API_URL}/transition/certificats/generer`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type_examen: type_examen, // 'BAC', 'BEPC', 'CEP'
          annee_scolaire_id: annee_scolaire_id
        })
      });

      const result = await handleResponse(response);
      console.log('Ã¢ÂÂ [API] Certificats gÃÂ©nÃÂ©rÃÂ©s:', result);

      return result;
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur genererCertificats:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // ============================================================================
  // ANALYTICS IA ET PERFORMANCE - Ã¢ÂÂ IMPLÃÂMENTÃÂES
  // ============================================================================

  /**
   * Ã¢ÂÂ IMPLÃÂMENTÃÂ : RÃÂ©cupÃÂ©rer les mÃÂ©triques de performance de l'IA
   */
  getAnalyticsIA: async (annee_scolaire_id) => {
    try {
      console.log('Ã°ÂÂ§Â  [API] RÃÂ©cupÃÂ©ration analytics IA:', annee_scolaire_id);

      const response = await fetch(`${API_URL}/transition/analytics/ia?annee_scolaire_id=${annee_scolaire_id}`, {
        headers: getAuthHeaders()
      });

      const result = await handleResponse(response);
      console.log('Ã¢ÂÂ [API] Analytics IA rÃÂ©cupÃÂ©rÃÂ©es:', result);

      return result;
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getAnalyticsIA:', error);
      return {
        success: false,
        error: error.message,
        data: {
          performance: { precision: 0, auto_validation: 0 },
          distribution: [],
          facteurs: {},
          predictions: {}
        }
      };
    }
  },

  // ============================================================================
  // AUDIT ET HISTORIQUE - Ã¢ÂÂ IMPLÃÂMENTÃÂES
  // ============================================================================

  /**
   * Ã¢ÂÂ AJOUTÃÂ : RÃÂ©cupÃÂ©rer l'historique des actions et audit
   */
  getHistoriqueAudit: async (annee_scolaire_id, options = {}) => {
    try {
      console.log('Ã°ÂÂÂ¡Ã¯Â¸Â [API] RÃÂ©cupÃÂ©ration historique audit:', annee_scolaire_id);

      const queryParams = new URLSearchParams({
        annee_scolaire_id: annee_scolaire_id,
        limit: options.limit || 20,
        ...options
      });

      const response = await fetch(`${API_URL}/transition/audit/historique?${queryParams}`, {
        headers: getAuthHeaders()
      });

      const result = await handleResponse(response);
      console.log('Ã¢ÂÂ [API] Historique audit rÃÂ©cupÃÂ©rÃÂ©:', result);

      return result;
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getHistoriqueAudit:', error);
      return {
        success: false,
        error: error.message,
        data: {
          historique: [],
          securite: {},
          exports: []
        }
      };
    }
  },

  /**
   * Ã¢ÂÂ BONUS : RÃÂ©cupÃÂ©rer l'historique des actions de transition (alias)
   */
  getHistoriqueActions: async (annee_scolaire_id, filtre = {}) => {
    try {
      const queryParams = new URLSearchParams({
        annee_scolaire_id: annee_scolaire_id,
        ...filtre
      });

      const response = await fetch(`${API_URL}/transition/audit/actions?${queryParams}`, {
        headers: getAuthHeaders()
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Ã¢ÂÂ [API] Erreur getHistoriqueActions:', error);
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  },



},


  // ================================================================================
  // MODULE MISSION CONTROL - GESTION TRANSITIONS ÃÂLÃÂVES - CLOTURE ANNEE SCOLAIRE
  // ================================================================================
  missionControl: {

    // ============================================================================
    // DASHBOARD PRINCIPAL
    // ============================================================================

    /**
     * RÃÂ©cupÃÂ¨re le dashboard principal avec cycles + statistiques
     * @returns {Promise<Object>} Dashboard complet avec cycles et stats
     */
    getDashboard: async () => {
      try {
        console.log('Ã°ÂÂÂ [API] RÃÂ©cupÃÂ©ration dashboard MissionControl...');

        const response = await fetch(`${API_URL}/mission-control/dashboard`, {
          method: 'GET',
          headers: getAuthHeaders()
        });

        const data = await handleResponse(response);
        console.log('Ã¢ÂÂ [API] Dashboard rÃÂ©cupÃÂ©rÃÂ©:', data.data?.stats_globales);
        return data;

      } catch (error) {
        console.error('Ã¢ÂÂ [API] Erreur dashboard MissionControl:', error);
        return {
          success: false,
          error: 'Erreur lors du chargement du dashboard'
        };
      }
    },

    /**
     * RÃÂ©cupÃÂ¨re la liste des cycles avec classes et effectifs
     * @returns {Promise<Object>} Liste des 4 cycles scolaires
     */
    getCycles: async () => {
      try {
        console.log('Ã°ÂÂÂ« [API] RÃÂ©cupÃÂ©ration cycles...');

        const response = await fetch(`${API_URL}/mission-control/cycles`, {
          method: 'GET',
          headers: getAuthHeaders()
        });

        return await handleResponse(response);

      } catch (error) {
        console.error('Ã¢ÂÂ [API] Erreur rÃÂ©cupÃÂ©ration cycles:', error);
        return {
          success: false,
          error: 'Erreur lors du chargement des cycles'
        };
      }
    },

    /**
     * Force le recalcul des prÃÂ©requis pour tous les ÃÂ©lÃÂ¨ves
     * @returns {Promise<Object>} RÃÂ©sultat du recalcul
     */
    actualiserPrerequis: async () => {
      try {
        console.log('Ã°ÂÂÂ [API] Actualisation prÃÂ©requis...');

        const response = await fetch(`${API_URL}/mission-control/actualiser-prerequis`, {
          method: 'POST',
          headers: getAuthHeaders()
        });

        const data = await handleResponse(response);
        console.log('Ã¢ÂÂ [API] PrÃÂ©requis actualisÃÂ©s:', data.data?.eleves_traites);
        return data;

      } catch (error) {
        console.error('Ã¢ÂÂ [API] Erreur actualisation prÃÂ©requis:', error);
        return {
          success: false,
          error: 'Erreur lors de l\'actualisation des prÃÂ©requis'
        };
      }
    },

    // ============================================================================
    // GESTION CLASSES + ÃÂLÃÂVES
    // ============================================================================

    /**
     * RÃÂ©cupÃÂ¨re les classes d'un cycle avec statistiques
     * @param {string} cycleId - ID du cycle (pre_scolaire, primaire, college, lycee)
     * @returns {Promise<Object>} Classes du cycle avec stats
     */
    getClassesParCycle: async (cycleId) => {
      try {
        console.log(`Ã°ÂÂÂ« [API] RÃÂ©cupÃÂ©ration classes cycle ${cycleId}...`);

        const response = await fetch(`${API_URL}/mission-control/cycles/${cycleId}/classes`, {
          method: 'GET',
          headers: getAuthHeaders()
        });

        return await handleResponse(response);

      } catch (error) {
        console.error('Ã¢ÂÂ [API] Erreur rÃÂ©cupÃÂ©ration classes:', error);
        return {
          success: false,
          error: 'Erreur lors du chargement des classes'
        };
      }
    },

    /**
     * RÃÂ©cupÃÂ¨re les ÃÂ©lÃÂ¨ves d'une classe avec prÃÂ©requis enrichis
     * @param {number} classeId - ID de la classe
     * @param {Object} filtres - Filtres optionnels (statut, recherche, pagination)
     * @returns {Promise<Object>} ÃÂlÃÂ¨ves de la classe enrichis
     */
    getElevesParClasse: async (classeId, filtres = {}) => {
      try {
        console.log(`Ã°ÂÂÂ¥ [API] RÃÂ©cupÃÂ©ration ÃÂ©lÃÂ¨ves classe ${classeId}...`);

        // Construction des query params
        const queryParams = new URLSearchParams();
        if (filtres.statut_filtre) queryParams.append('statut_filtre', filtres.statut_filtre);
        if (filtres.recherche) queryParams.append('recherche', filtres.recherche);
        if (filtres.page) queryParams.append('page', filtres.page);
        if (filtres.limit) queryParams.append('limit', filtres.limit);

        const url = `${API_URL}/mission-control/classes/${classeId}/eleves${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

        const response = await fetch(url, {
          method: 'GET',
          headers: getAuthHeaders()
        });

        return await handleResponse(response);

      } catch (error) {
        console.error('Ã¢ÂÂ [API] Erreur rÃÂ©cupÃÂ©ration ÃÂ©lÃÂ¨ves:', error);
        return {
          success: false,
          error: 'Erreur lors du chargement des ÃÂ©lÃÂ¨ves'
        };
      }
    },

    /**
     * RÃÂ©cupÃÂ¨re le dÃÂ©tail complet d'un ÃÂ©lÃÂ¨ve
     * @param {number} eleveId - ID de l'ÃÂ©lÃÂ¨ve
     * @returns {Promise<Object>} DÃÂ©tail ÃÂ©lÃÂ¨ve avec prÃÂ©requis complets
     */
    getDetailEleve: async (eleveId) => {
      try {
        console.log(`Ã°ÂÂÂ¤ [API] RÃÂ©cupÃÂ©ration dÃÂ©tail ÃÂ©lÃÂ¨ve ${eleveId}...`);

        const response = await fetch(`${API_URL}/mission-control/eleves/${eleveId}`, {
          method: 'GET',
          headers: getAuthHeaders()
        });

        return await handleResponse(response);

      } catch (error) {
        console.error('Ã¢ÂÂ [API] Erreur dÃÂ©tail ÃÂ©lÃÂ¨ve:', error);
        return {
          success: false,
          error: 'Erreur lors du chargement du dÃÂ©tail ÃÂ©lÃÂ¨ve'
        };
      }
    },

    // ============================================================================
    // ACTIONS SUR LES ÃÂLÃÂVES
    // ============================================================================

    /**
     * Relance les parents d'un ÃÂ©lÃÂ¨ve
     * @param {number} eleveId - ID de l'ÃÂ©lÃÂ¨ve
     * @param {Object} donnees - DonnÃÂ©es relance (type, message personnalisÃÂ©)
     * @returns {Promise<Object>} RÃÂ©sultat de la relance
     */
    relancerParents: async (eleveId, donnees = {}) => {
      try {
        console.log(`Ã°ÂÂÂ [API] Relance parents ÃÂ©lÃÂ¨ve ${eleveId}...`);

        const response = await fetch(`${API_URL}/mission-control/eleves/${eleveId}/relancer`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            type_relance: donnees.type_relance || 'paiement',
            message_personnalise: donnees.message_personnalise
          })
        });

        const data = await handleResponse(response);
        console.log('Ã¢ÂÂ [API] Relance parents envoyÃÂ©e');
        return data;

      } catch (error) {
        console.error('Ã¢ÂÂ [API] Erreur relance parents:', error);
        return {
          success: false,
          error: 'Erreur lors de la relance des parents'
        };
      }
    },

    /**
     * Accorde un report de paiement ÃÂ  un ÃÂ©lÃÂ¨ve
     * @param {number} eleveId - ID de l'ÃÂ©lÃÂ¨ve
     * @param {Object} donnees - DonnÃÂ©es report (mois, commentaire)
     * @returns {Promise<Object>} RÃÂ©sultat du report
     */
    accorderReport: async (eleveId, donnees) => {
      try {
        console.log(`Ã°ÂÂÂ [API] Accord report ÃÂ©lÃÂ¨ve ${eleveId}...`);

        const response = await fetch(`${API_URL}/mission-control/eleves/${eleveId}/reporter`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            mois_a_reporter: donnees.mois_a_reporter,
            commentaire_report: donnees.commentaire_report
          })
        });

        const data = await handleResponse(response);
        console.log('Ã¢ÂÂ [API] Report accordÃÂ©:', data.data?.mois_reportes);
        return data;

      } catch (error) {
        console.error('Ã¢ÂÂ [API] Erreur accord report:', error);
        return {
          success: false,
          error: 'Erreur lors de l\'accord du report'
        };
      }
    },

    /**
     * Accorde un passage exceptionnel (conseil de classe)
     * @param {number} eleveId - ID de l'ÃÂ©lÃÂ¨ve
     * @param {Object} donnees - DonnÃÂ©es passage (classe destination, commentaires)
     * @returns {Promise<Object>} RÃÂ©sultat du passage exceptionnel
     */
    accorderPassageExceptionnel: async (eleveId, donnees) => {
      try {
        console.log(`Ã°ÂÂ§Â  [API] Passage exceptionnel ÃÂ©lÃÂ¨ve ${eleveId}...`);

        const response = await fetch(`${API_URL}/mission-control/eleves/${eleveId}/passage-exceptionnel`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            classe_destination_id: donnees.classe_destination_id,
            commentaire_conseil: donnees.commentaire_conseil,
            decision_motivee: donnees.decision_motivee
          })
        });

        const data = await handleResponse(response);
        console.log('Ã¢ÂÂ [API] Passage exceptionnel accordÃÂ©');
        return data;

      } catch (error) {
        console.error('Ã¢ÂÂ [API] Erreur passage exceptionnel:', error);
        return {
          success: false,
          error: 'Erreur lors du passage exceptionnel'
        };
      }
    },

    /**
     * Confirme la rÃÂ©inscription d'un ÃÂ©lÃÂ¨ve
     * @param {number} eleveId - ID de l'ÃÂ©lÃÂ¨ve
     * @param {Object} donnees - DonnÃÂ©es confirmation (classe destination, commentaire, force)
     * @returns {Promise<Object>} RÃÂ©sultat de la confirmation
     */
    confirmerReinscription: async (eleveId, donnees) => {
      try {
        console.log(`Ã¢ÂÂ [API] Confirmation rÃÂ©inscription ÃÂ©lÃÂ¨ve ${eleveId}...`);

        const response = await fetch(`${API_URL}/mission-control/eleves/${eleveId}/confirmer`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            classe_destination_id: donnees.classe_destination_id,
            commentaire_reinscription: donnees.commentaire_reinscription,
            force_validation: donnees.force_validation || false
          })
        });

        const data = await handleResponse(response);
        console.log('Ã¢ÂÂ [API] RÃÂ©inscription confirmÃÂ©e');
        return data;

      } catch (error) {
        console.error('Ã¢ÂÂ [API] Erreur confirmation rÃÂ©inscription:', error);
        return {
          success: false,
          error: 'Erreur lors de la confirmation de rÃÂ©inscription'
        };
      }
    },

    /**
     * Modifie la classe de destination d'un ÃÂ©lÃÂ¨ve
     * @param {number} eleveId - ID de l'ÃÂ©lÃÂ¨ve
     * @param {Object} donnees - DonnÃÂ©es modification (nouvelle classe, raison)
     * @returns {Promise<Object>} RÃÂ©sultat de la modification
     */
    modifierClasseDestination: async (eleveId, donnees) => {
      try {
        console.log(`Ã°ÂÂÂ [API] Modification classe destination ÃÂ©lÃÂ¨ve ${eleveId}...`);

        const response = await fetch(`${API_URL}/mission-control/eleves/${eleveId}/classe-destination`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            nouvelle_classe_id: donnees.nouvelle_classe_id,
            raison_changement: donnees.raison_changement
          })
        });

        const data = await handleResponse(response);
        console.log('Ã¢ÂÂ [API] Classe destination modifiÃÂ©e');
        return data;

      } catch (error) {
        console.error('Ã¢ÂÂ [API] Erreur modification classe:', error);
        return {
          success: false,
          error: 'Erreur lors de la modification de classe'
        };
      }
    },

    // ============================================================================
    // FONCTIONS BONUS - EXPORT + STATISTIQUES + DIAGNOSTIC
    // ============================================================================

    /**
     * Exporte les donnÃÂ©es MissionControl
     * @param {Object} options - Options export (format, filtres)
     * @returns {Promise<Object>} DonnÃÂ©es exportÃÂ©es
     */
    exporterDonnees: async (options = {}) => {
      try {
        console.log('Ã°ÂÂÂ [API] Export donnÃÂ©es MissionControl...');

        // Construction des query params
        const queryParams = new URLSearchParams();
        if (options.format) queryParams.append('format', options.format);
        if (options.cycle_filtre) queryParams.append('cycle_filtre', options.cycle_filtre);
        if (options.statut_filtre) queryParams.append('statut_filtre', options.statut_filtre);

        const url = `${API_URL}/mission-control/export${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

        const response = await fetch(url, {
          method: 'GET',
          headers: getAuthHeaders()
        });

        const data = await handleResponse(response);
        console.log('Ã¢ÂÂ [API] Export terminÃÂ©:', data.data?.total_eleves);
        return data;

      } catch (error) {
        console.error('Ã¢ÂÂ [API] Erreur export:', error);
        return {
          success: false,
          error: 'Erreur lors de l\'export des donnÃÂ©es'
        };
      }
    },

    /**
     * RÃÂ©cupÃÂ¨re les statistiques avancÃÂ©es
     * @returns {Promise<Object>} Statistiques dÃÂ©taillÃÂ©es par cycle
     */
    getStatistiquesAvancees: async () => {
      try {
        console.log('Ã°ÂÂÂ [API] RÃÂ©cupÃÂ©ration statistiques avancÃÂ©es...');

        const response = await fetch(`${API_URL}/mission-control/statistiques`, {
          method: 'GET',
          headers: getAuthHeaders()
        });

        return await handleResponse(response);

      } catch (error) {
        console.error('Ã¢ÂÂ [API] Erreur statistiques avancÃÂ©es:', error);
        return {
          success: false,
          error: 'Erreur lors du chargement des statistiques'
        };
      }
    },

    /**
     * Lance un diagnostic systÃÂ¨me MissionControl
     * @returns {Promise<Object>} Rapport de diagnostic avec problÃÂ¨mes dÃÂ©tectÃÂ©s
     */
    diagnostiquerSysteme: async () => {
      try {
        console.log('Ã°ÂÂÂ [API] Diagnostic systÃÂ¨me MissionControl...');

        const response = await fetch(`${API_URL}/mission-control/diagnostic`, {
          method: 'GET',
          headers: getAuthHeaders()
        });

        const data = await handleResponse(response);
        console.log('Ã¢ÂÂ [API] Diagnostic terminÃÂ©:', data.data?.niveau_global);
        return data;

      } catch (error) {
        console.error('Ã¢ÂÂ [API] Erreur diagnostic:', error);
        return {
          success: false,
          error: 'Erreur lors du diagnostic systÃÂ¨me'
        };
      }
    },

    // ============================================================================
    // FONCTIONS DEBUG (DÃÂVELOPPEMENT UNIQUEMENT)
    // ============================================================================

    /**
     * VÃÂ©rifie les permissions utilisateur pour MissionControl
     * @returns {Promise<Object>} ÃÂtat des permissions utilisateur
     */
    debugPermissions: async () => {
      if (process.env.NODE_ENV !== 'development') {
        return { success: false, error: 'Debug disponible uniquement en dÃÂ©veloppement' };
      }

      try {
        console.log('Ã°ÂÂÂ [API Debug] VÃÂ©rification permissions...');

        const response = await fetch(`${API_URL}/mission-control/debug/permissions`, {
          method: 'GET',
          headers: getAuthHeaders()
        });

        return await handleResponse(response);

      } catch (error) {
        console.error('Ã¢ÂÂ [API Debug] Erreur permissions:', error);
        return {
          success: false,
          error: 'Erreur lors de la vÃÂ©rification des permissions'
        };
      }
    },

    /**
     * Test la connexion DB pour MissionControl
     * @returns {Promise<Object>} ÃÂtat de la connexion
     */
    debugTestConnection: async () => {
      if (process.env.NODE_ENV !== 'development') {
        return { success: false, error: 'Debug disponible uniquement en dÃÂ©veloppement' };
      }

      try {
        console.log('Ã°ÂÂÂ§ [API Debug] Test connexion DB...');

        const response = await fetch(`${API_URL}/mission-control/debug/test-connection`, {
          method: 'GET',
          headers: getAuthHeaders()
        });

        return await handleResponse(response);

      } catch (error) {
        console.error('Ã¢ÂÂ [API Debug] Erreur test connexion:', error);
        return {
          success: false,
          error: 'Erreur lors du test de connexion'
        };
      }
    }

  },

 parametres: {
    getAll: async (opts = {}) => {
      try {
        const response = await fetch(`${API_URL}/parametres`, {
          method: 'GET',
          headers: {
            ...getAuthHeaders()
          },
          credentials: 'include', // session si tu en utilises
          cache: 'no-store',
          ...opts,
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur getAll parametres:', error);
        return { success: false, error: error.message };
      }
    },

    updateGeneraux: async (data, opts = {}) => {
      try {
        const response = await fetch(`${API_URL}/parametres/generaux`, {
          method: 'PUT',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          credentials: 'include',
          cache: 'no-store',
          body: JSON.stringify(data),
          ...opts,
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur updateGeneraux:', error);
        return { success: false, error: error.message };
      }
    },

    updateSection: async (id, data, opts = {}) => {
      try {
        const response = await fetch(`${API_URL}/parametres/sections/${id}`, {
          method: 'PUT',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          credentials: 'include',
          cache: 'no-store',
          body: JSON.stringify(data),
          ...opts,
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur updateSection:', error);
        return { success: false, error: error.message };
      }
    },

    // Supprimer une section. Le serveur refuse si elle contient des classes,
    // avec un 409 qui dit combien — l'ecran peut alors proposer la desactivation.
    deleteSection: async (id, opts = {}) => {
      try {
        const response = await fetch(`${API_URL}/parametres/sections/${id}`, {
          method: 'DELETE',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          credentials: 'include',
          cache: 'no-store',
          ...opts,
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur deleteSection:', error);
        return { success: false, message: error.message };
      }
    },

    createSection: async (data, opts = {}) => {
      try {
        const response = await fetch(`${API_URL}/parametres/sections`, {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          credentials: 'include',
          cache: 'no-store',
          body: JSON.stringify(data),
          ...opts,
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur createSection:', error);
        return { success: false, error: error.message };
      }
    },

    reorderSections: async (ordres, opts = {}) => {
      try {
        const response = await fetch(`${API_URL}/parametres/sections/reorder`, {
          method: 'PUT',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          credentials: 'include',
          cache: 'no-store',
          body: JSON.stringify({ nouveaux_ordres: ordres }),
          ...opts,
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Erreur reorderSections:', error);
        return { success: false, error: error.message };
      }
    }
  },




  //ENDPOINT POUR LE POINTAGE MANUELLE
  pointages: {
  getEnseignantsAvecAffectations: async (anneeId, date = null) => {
    try {
      const url = `/pointages/enseignants-avec-affectations/${anneeId}`;
      const params = date ? `?date=${date}` : '';
      const response = await fetch(`${API_URL}${url}${params}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur rÃÂ©cupÃÂ©ration enseignants avec affectations:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  getByDate: async (date, filters = {}) => {
    try {
      const searchParams = new URLSearchParams();
      if (filters.enseignant_id) searchParams.append('enseignant_id', filters.enseignant_id);
      if (filters.classe_id) searchParams.append('classe_id', filters.classe_id);
      if (filters.contrat) searchParams.append('contrat', filters.contrat);
      if (filters.annee_id) searchParams.append('annee_id', filters.annee_id);
      if (filters.annee_scolaire_id) searchParams.append('annee_scolaire_id', filters.annee_scolaire_id);

      const queryString = searchParams.toString();
      const url = `/pointages/by-date/${date}${queryString ? `?${queryString}` : ''}`;
      const response = await fetch(`${API_URL}${url}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur rÃÂ©cupÃÂ©ration pointages par date:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  enregistrer: async (pointageData) => {
    try {
      const response = await fetch(`${API_URL}/pointages/enregistrer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(pointageData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur enregistrement pointage:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },


completer: async (pointageId, finData) => {
    try {
      const response = await fetch(`${API_URL}/pointages/completer/${pointageId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(finData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur complÃÂ©tion pointage:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },



  enregistrerLot: async (lotData) => {
    try {
      const response = await fetch(`${API_URL}/pointages/enregistrer-lot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(lotData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur enregistrement lot pointages:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },




envoyerEmployeComptabilite: async (employeId, date, anneeId) => {
  try {
    const response = await fetch(`${API_URL}/pointages/employes/envoyer-comptabilite/${employeId}?date=${date}&annee_id=${anneeId}`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur envoi comptabilitÃÂ© employÃÂ©:', error);
    return {
      success: false,
      error: error.message
    };
  }
},




justifierAnomalie: async (pointageId, justification) => {
    try {
      const response = await fetch(`${API_URL}/pointages/${pointageId}/justifier-anomalie`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ justification })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur justification anomalie pointage:', error);
      return { success: false, error: error.message, message: error.message };
    }
  },
envoyerComptabilite: async (pointageId) => {
    try {
      const response = await fetch(`${API_URL}/pointages/envoyer-comptabilite/${pointageId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        }
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur envoi comptabilitÃÂ©:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },


  envoyerTousComptabilite: async (datePointage, filters = {}) => {
    try {
      const response = await fetch(`${API_URL}/pointages/envoyer-tous-comptabilite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ date_pointage: datePointage, ...filters })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur envoi tous comptabilitÃÂ©:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },


  enregistrerLotEmployes: async (data) => {
    try {
      const response = await fetch(`${API_URL}/pointages/enregistrer-lot-employes`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur enregistrement lot employÃÂ©s:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },


envoyerEmployeComptabilite: async (pointageId) => {
  try {
    const response = await fetch(`${API_URL}/pointages/employes/envoyer-comptabilite/${pointageId}`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur envoi comptabilitÃÂ© employÃÂ©:', error);
    return {
      success: false,
      error: error.message
    };
  }
},


getEmployesPourPointage: async (anneeId, date) => {
    try {
      const response = await fetch(`${API_URL}/pointages/employes-pointage/${anneeId}?date=${date}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur rÃÂ©cupÃÂ©ration employÃÂ©s:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },


  getEmployesAvecPresences: async (mois, annee) => {
    try {
      const response = await fetch(`${API_URL}/pointages/employes/mensuels/${mois}/${annee}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur rÃÂ©cupÃÂ©ration employÃÂ©s mensuels:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },


  enregistrerBadgeuse: async (pointageData) => {
    try {
      const response = await fetch(`${API_URL}/pointages/badgeuse/enregistrer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(pointageData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur enregistrement badgeuse:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Le kiosque : les règles du jour, celles d'une personne, et la lecture
  // obligatoire confirmée en repassant son badge (31/10/2026).
  kiosqueRegles: async () => {
    const response = await fetch(`${API_URL}/pointages/kiosque/regles`, { headers: getAuthHeaders() });
    return handleResponse(response);
  },

  kiosqueReglesPersonne: async (employeId) => {
    const response = await fetch(`${API_URL}/pointages/kiosque/regles/${employeId}`, { headers: getAuthHeaders() });
    return handleResponse(response);
  },

  kiosqueLecture: async (lecture) => {
    const response = await fetch(`${API_URL}/pointages/kiosque/lecture`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(lecture)
    });
    return handleResponse(response);
  },


},


comptabilite: {
  // ========================================
  // 📊 STATS DASHBOARD (NOUVEAU)
  // ========================================

  // 🆕 Stats personnelles (utilisateur connecté)
  getDashboardStatsPersonnel: async (anneeScolaireId = null, date = null) => {
    try {
      const selectedYearId = anneeScolaireId || localStorage.getItem('annee_scolaire_id');
      const query = new URLSearchParams();
      if (selectedYearId) query.append('annee_scolaire_id', selectedYearId);
      const selectedDate = normalizeDateParam(date);
      if (selectedDate) query.append('date', selectedDate);
      const queryString = query.toString();
      const url = API_URL + '/comptabilite/dashboard/stats/personnel' + (queryString ? '?' + queryString : '');
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur recuperation stats personnelles:', error);
      return { success: false, error: error.message };
    }
  },

  getDashboardStatsGlobal: async () => {
    try {
      const response = await fetch(`${API_URL}/comptabilite/dashboard/stats/global`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération stats globales:', error);
      return { success: false, error: error.message };
    }
  },

  // ========================================
  // 🔧 MÉTHODES EXISTANTES
  // ========================================

  getPointagesRecu: async (mois, annee) => {
    try {
      const searchParams = new URLSearchParams();
      if (mois) searchParams.append('mois', mois);
      if (annee) searchParams.append('annee', annee);

      const queryString = searchParams.toString();
      const url = `/comptabilite/pointages-recu${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(`${API_URL}${url}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération pointages comptabilité:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  getEnseignantsAvecPointages: async (mois, annee, anneeScolaireId = null) => {
    try {
      const selectedYearId = anneeScolaireId || localStorage.getItem('annee_scolaire_id');
      const query = selectedYearId ? `?annee_scolaire_id=${encodeURIComponent(selectedYearId)}` : '';

      const response = await fetch(
      `${API_URL}/paiements/enseignants/pointages/${mois}/${annee}${query}`,
      { headers: getAuthHeaders() }
        );

        return handleResponse(response);
      } catch (error) {
        console.error('Erreur récupération enseignants avec pointages:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

  getEmployesAvecPresences: async (mois, annee, anneeScolaireId = null) => {
    try {
      const selectedYearId = anneeScolaireId || localStorage.getItem('annee_scolaire_id');
      const query = selectedYearId ? `?annee_scolaire_id=${encodeURIComponent(selectedYearId)}` : '';
      const response = await fetch(`${API_URL}/pointages/employes/mensuels/${mois}/${annee}${query}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération employés mensuels:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  getPointagesMensuelsEmployes: async (mois, annee, anneeScolaireId = null) => {
    try {
      const selectedYearId = anneeScolaireId || localStorage.getItem('annee_scolaire_id');
      const query = selectedYearId ? `?annee_scolaire_id=${encodeURIComponent(selectedYearId)}` : '';
      const response = await fetch(`${API_URL}/pointages/employes/mensuels/${mois}/${annee}${query}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération pointages employés:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  updateJustificationDecalage: async (pointageId, justification) => {
    try {
      const response = await fetch(`${API_URL}/pointages/employes/justification-decalage/${pointageId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ justification })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur mise à jour justification:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  updateRetenueAbsence: async (pointageId, data) => {
    try {
      const response = await fetch(`${API_URL}/pointages/employes/retenue-absence/${pointageId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur mise à jour retenue:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  validerCalculEmploye: async (data) => {
    try {
      const response = await fetch(`${API_URL}/pointages/employes/valider-calcul`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur validation calcul employé:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  getPaiementsMensuels: async () => {
    try {
      const response = await fetch(`${API_URL}/comptabilite/paiements-mensuels`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération paiements mensuels:', error);
      return { success: false, error: error.message };
    }
  },

  getDepensesAnnexes: async () => {
    try {
      const response = await fetch(`${API_URL}/comptabilite/depenses-annexes`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération dépenses annexes:', error);
      return { success: false, error: error.message };
    }
  }
},




// Renommer cette section
paiementsSalaires: {


    // ========================================
  // 🆕 EMPLOYES
  // ========================================

  getModesPaiement: async () => {
    try {
      const response = await fetch(`${API_URL}/paiements/modes-paiement`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur rÃÂ©cupÃÂ©ration modes paiement:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  creerPaiement: async (paiementData) => {
    try {
      const response = await fetch(`${API_URL}/paiements/creer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(paiementData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur crÃÂ©ation paiement:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

 verifierPaiementEmployeExistant: async (employeId, mois, annee) => {
  try {
    const url = `${API_URL}/paiements/employes/verifier/${employeId}/${mois}/${annee}`;
    console.log('📡 API verifierPaiementEmployeExistant:', url);

    const response = await fetch(url, {
      headers: getAuthHeaders()
    });

    const result = await handleResponse(response);
    console.log('📡 Réponse API verifierPaiementEmployeExistant:', result);

    return result;
  } catch (error) {
    console.error('❌ Erreur vérification paiement employé:', error);
    return {
      success: false,
      error: error.message
    };
  }
},


  cloturerPaiement: async (paiementId) => {
  try {
    const response = await fetch(`${API_URL}/paiements/cloturer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({ paiement_id: paiementId })
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur clÃÂ´ture paiement:', error);
    return {
      success: false,
      error: error.message
    };
  }
},


creerPaiementEmploye: async (paiementData) => {
  try {
    const response = await fetch(`${API_URL}/paiements/employes/creer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(paiementData)
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur crÃÂ©ation paiement employÃÂ©:', error);
    return {
      success: false,
      error: error.message
    };
  }
},

cloturerPaiementEmploye: async (paiementId) => {
  try {
    const response = await fetch(`${API_URL}/paiements/employes/cloturer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({ paiement_id: paiementId })
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur clÃÂ´ture paiement employÃÂ©:', error);
    return {
      success: false,
      error: error.message
    };
  }
},

verifierPaiementEmployeExistant: async (employeId, mois, annee) => {
  try {
    const response = await fetch(`${API_URL}/paiements/employes/verifier/${employeId}/${mois}/${annee}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur vÃÂ©rification paiement employÃÂ©:', error);
    return {
      success: false,
      error: error.message
    };
  }
},




getHistoriquePaiementsEmployes: async () => {
    try {
      const response = await fetch(`${API_URL}/paiements/employes/historique`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        }
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur historique:', error);
      return { success: false, error: error.message };
    }
  },

  getSalaryModificationContext: async (employeId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      const suffix = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await fetch(`${API_URL}/paiements/salaires/modifications/${employeId}/context${suffix}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur contexte modification salaire:', error);
      return { success: false, error: error.message };
    }
  },

  createSalaryModificationRequest: async (data) => {
    try {
      const response = await fetch(`${API_URL}/paiements/salaires/modifications/demande`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur demande modification salaire:', error);
      return { success: false, error: error.message };
    }
  },

  getSalaryModificationHistory: async (employeId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      const suffix = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await fetch(`${API_URL}/paiements/salaires/modifications/${employeId}/historique${suffix}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur historique modification salaire:', error);
      return { success: false, error: error.message };
    }
  },


  getFichePaieData: async (paiementId) => {
    try {
      const response = await fetch(`${API_URL}/paiements/employes/fiche-paie/${paiementId}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération fiche paie:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },



  // ========================================
  // 🆕 ENSEIGNANTS
  // ========================================

  // ========================================
  // EMPLOYES HYBRIDES
  // ========================================

  getHybridesAvecPointages: async (mois, annee, anneeScolaireId = null) => {
    try {
      const selectedYearId = anneeScolaireId || localStorage.getItem('annee_scolaire_id');
      const query = selectedYearId ? `?annee_scolaire_id=${encodeURIComponent(selectedYearId)}` : '';
      const response = await fetch(`${API_URL}/paiements/hybrides/pointages/${mois}/${annee}${query}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur recuperation employes hybrides:', error);
      return { success: false, error: error.message };
    }
  },

  verifierPaiementHybrideExistant: async (employeId, mois, annee, anneeScolaireId = null) => {
    try {
      const selectedYearId = anneeScolaireId || localStorage.getItem('annee_scolaire_id');
      const query = selectedYearId ? `?annee_scolaire_id=${encodeURIComponent(selectedYearId)}` : '';
      const response = await fetch(`${API_URL}/paiements/hybrides/verifier/${employeId}/${mois}/${annee}${query}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur verification paiement hybride:', error);
      return { success: false, error: error.message };
    }
  },

  creerPaiementHybride: async (paiementData) => {
    try {
      const response = await fetch(`${API_URL}/paiements/hybrides/creer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(paiementData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur creation paiement hybride:', error);
      return { success: false, error: error.message };
    }
  },

  cloturerPaiementHybride: async (paiements) => {
    try {
      const response = await fetch(`${API_URL}/paiements/hybrides/cloturer`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ paiements })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur cloture paiement hybride:', error);
      return { success: false, error: error.message };
    }
  },

  getHistoriquePaiementsHybrides: async () => {
    try {
      const response = await fetch(`${API_URL}/paiements/hybrides/historique`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        }
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur historique paiements hybrides:', error);
      return { success: false, error: error.message };
    }
  },

  creerPaiementEnseignant: async (paiementData) => {
    try {
      const response = await fetch(`${API_URL}/paiements/enseignants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(paiementData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur création paiement enseignant:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  verifierPaiementEnseignantExistant: async (employeId, mois, annee) => {
    try {
      const url = `${API_URL}/paiements/enseignants/verifier/${employeId}/${mois}/${annee}`;
      console.log('📡 API verifierPaiementEnseignantExistant:', url);

      const response = await fetch(url, {
        headers: getAuthHeaders()
      });

      const result = await handleResponse(response);
      console.log('📡 Réponse API verifierPaiementEnseignantExistant:', result);

      return result;
    } catch (error) {
      console.error('❌ Erreur vérification paiement enseignant:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  cloturerPaiementEnseignant: async (paiementId) => {
    try {
      const response = await fetch(`${API_URL}/paiements/enseignants/${paiementId}/cloturer`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur clôture paiement enseignant:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  getHistoriquePaiementsEnseignants: async () => {
    try {
      const response = await fetch(`${API_URL}/paiements/enseignants/historique`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        }
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur historique enseignants:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  getFichePaieEnseignantData: async (paiementId) => {
    try {
      const response = await fetch(`${API_URL}/paiements/enseignants/fiche-paie/${paiementId}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération fiche paie enseignant:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },



  genererFichePaieEnseignant: async (paiementId) => {
    try {
      console.log(`📄 Génération fiche de paie pour paiement ID: ${paiementId}`);

      const response = await fetch(`${API_URL}/paiements/enseignants/${paiementId}/fiche-paie`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors de la génération de la fiche de paie');
      }

      // Récupérer le blob PDF
      const blob = await response.blob();

      // Extraire le nom du fichier depuis les headers
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `fiche-paie-${paiementId}.pdf`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log(`✅ Fiche de paie téléchargée: ${filename}`);

      return { success: true, filename };

    } catch (error) {
      console.error('❌ Erreur génération fiche de paie:', error);
      throw error;
    }
  },


  genererFichePaieEmploye: async (paiementId) => {
  try {
    console.log(`📄 Génération fiche de paie employé pour paiement ID: ${paiementId}`);

    const response = await fetch(`${API_URL}/paiements/employes/${paiementId}/fiche-paie`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Erreur lors de la génération de la fiche de paie');
    }

    // Récupérer le blob PDF
    const blob = await response.blob();

    // Extraire le nom du fichier
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `fiche-paie-employe-${paiementId}.pdf`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // Créer un lien de téléchargement
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    console.log(`✅ Fiche de paie employé téléchargée: ${filename}`);

    return { success: true, filename };

  } catch (error) {
    console.error('❌ Erreur génération fiche de paie employé:', error);
    throw error;
  }
}




},


// avance sur salaire
avancesSalaire: {
  // Récupérer l'historique des avances d'un employé
  getHistoriqueAvances: async (employeId) => {
    try {
      const response = await fetch(`${API_URL}/avances/employee/${employeId}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération historique avances:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Récupérer les avances pour un mois spécifique
  getAvancesMois: async (employeId, monthId) => {
    try {
      const response = await fetch(`${API_URL}/avances/employee/${employeId}/month/${monthId}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération avances du mois:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Créer une nouvelle avance
  creerAvance: async (avanceData) => {
    try {
      const response = await fetch(`${API_URL}/avances/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(avanceData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur création avance:', error);
      return {
        success: false,
        message: error.message,
        code: error.code,
        error: error.message
      };
    }
  },

  // Modifier une avance existante
  modifierAvance: async (avanceId, avanceData) => {
    try {
      const response = await fetch(`${API_URL}/avances/${avanceId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(avanceData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur modification avance:', error);
      return {
        success: false,
        message: error.message,
        code: error.code,
        error: error.message
      };
    }
  },

  // Supprimer (annuler) une avance
  supprimerAvance: async (avanceId, justification) => {
    try {
      const response = await fetch(`${API_URL}/avances/${avanceId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ justification_suppression: justification })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur suppression avance:', error);
      return {
        success: false,
        message: error.message,
        code: error.code,
        error: error.message
      };
    }
  },


  // Marquer une avance comme appliquée (après paiement)
  marquerAvanceAppliquee: async (avanceId) => {
    try {
      const response = await fetch(`${API_URL}/avances/mark-applied/${avanceId}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur marquage avance appliquée:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },



// 🆕 Récupérer les enseignants à taux horaire avec calcul théorique
  getEnseignantsTauxHoraire: async (moisScolaireId) => {
    try {
      const response = await fetch(
        `${API_URL}/avances/enseignants-taux-horaire/${moisScolaireId}`,
        {
          headers: getAuthHeaders()
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération enseignants taux horaire:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Capacite d'avance restante par employe, pour dimensionner une dotation
  getCapacite: async ({ mois_scolaire_id, type_employe, employe_ids }) => {
    try {
      const response = await fetch(`${API_URL}/avances/capacite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ mois_scolaire_id, type_employe, employe_ids })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur capacite avances:', error);
      return { success: false, error: error.message };
    }
  },

  // 🆕 Récupérer les statistiques globales pour un mois
  // Recuperer les employes hybrides avec calcul consolide
  getEmployesHybrides: async (moisScolaireId) => {
    try {
      const response = await fetch(
        API_URL + '/avances/employes-hybrides/' + moisScolaireId,
        {
          headers: getAuthHeaders()
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur recuperation employes hybrides:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  getStatistiques: async (moisScolaireId) => {
    try {
      const response = await fetch(
        `${API_URL}/avances/statistiques/${moisScolaireId}`,
        {
          headers: getAuthHeaders()
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération statistiques avances:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // ✏️ MODIFIER creerAvance pour inclure type_employe
  creerAvance: async (avanceData) => {
    try {
      const response = await fetch(`${API_URL}/avances/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          ...avanceData,
          type_employe: avanceData.type_employe || 'FIXE' // Par défaut FIXE
        })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur création avance:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },


},



salary: {
  // Récupérer tous les employés avec leurs informations de salaire
  getEmployees: async (monthId = null) => {
    try {
      const url = monthId
        ? `${API_URL}/salary/employees/${monthId}`
        : `${API_URL}/salary/employees`;

      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération employés:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Récupérer le dashboard des salaires
  getDashboard: async (monthId = null) => {
    try {
      const url = monthId
        ? `${API_URL}/salary/dashboard/${monthId}`
        : `${API_URL}/salary/dashboard`;

      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur dashboard salaires:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Récupérer le détail d'un employé
  getEmployeeDetail: async (employeeId) => {
    try {
      const response = await fetch(`${API_URL}/salary/employees/${employeeId}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur détail employé:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Calculer le salaire d'un employé pour un mois
  calculateSalary: async (employeeId, monthId) => {
    try {
      const response = await fetch(`${API_URL}/salary/employees/${employeeId}/current-salary/${monthId}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur calcul salaire:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Effectuer un paiement
  processPayment: async (employeeId, monthId, paymentData) => {
    try {
      const response = await fetch(`${API_URL}/salary/employees/${employeeId}/pay/${monthId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(paymentData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur paiement:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
},


// ============================================================================
// FAMILLES NOMBREUSES
// ============================================================================
familles: {
  // Récupérer toutes les familles actives
  getAll: async (params = {}) => {
    try {
      const query = new URLSearchParams();
      if (params.page) query.set('page', params.page);
      if (params.limit) query.set('limit', params.limit);
      if (params.search) query.set('search', params.search);
      if (params.type) query.set('type', params.type);
      if (params.nb_enfants) query.set('nb_enfants', params.nb_enfants);
      if (params.annee_scolaire_id) query.set('annee_scolaire_id', params.annee_scolaire_id);

      const queryString = query.toString();
      const url = queryString ? `${API_URL}/familles?${queryString}` : `${API_URL}/familles`;

      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération familles:', error);
      return { success: false, error: error.message };
    }
  },

  // Récupérer une famille par ID
  getById: async (id) => {
    try {
      const response = await fetch(`${API_URL}/familles/${id}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération famille:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // 🆕 Créer une nouvelle famille (MODIFIÉ)
  create: async (familleData) => {
    try {
      // Validation et transformation des données
      const dataToSend = {
        nom: familleData.nom,
        responsable_nom: familleData.responsable_nom || '',
        telephone: familleData.telephone || '',
        telephone_secondaire: familleData.telephone_secondaire || '',
        adresse: familleData.adresse || '',
        reduction_inscription: parseFloat(familleData.reduction_inscription) || 0,
        reduction_mensuel: parseFloat(familleData.reduction_mensuel) || 0,
        description: familleData.description || ''
      };

      console.log('Données famille envoyées:', dataToSend);

      const response = await fetch(`${API_URL}/familles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(dataToSend)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur création famille:', error);
      return {
        success: false,
        message: 'Erreur lors de la création de la famille',
        error: error.message
      };
    }
  },

  // 🆕 Mettre à jour une famille (MODIFIÉ)
  update: async (id, familleData) => {
    try {
      // Validation et transformation des données
      const dataToSend = {
        nom: familleData.nom,
        responsable_nom: familleData.responsable_nom || '',
        telephone: familleData.telephone || '',
        telephone_secondaire: familleData.telephone_secondaire || '',
        adresse: familleData.adresse || '',
        reduction_inscription: parseFloat(familleData.reduction_inscription) || 0,
        reduction_mensuel: parseFloat(familleData.reduction_mensuel) || 0,
        description: familleData.description || ''
      };

      console.log('Données famille mises à jour:', dataToSend);

      const response = await fetch(`${API_URL}/familles/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(dataToSend)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur mise à jour famille:', error);
      return {
        success: false,
        message: 'Erreur lors de la mise à jour de la famille',
        error: error.message
      };
    }
  },

  // Supprimer une famille
  delete: async (id) => {
    try {
      const response = await fetch(`${API_URL}/familles/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur suppression famille:', error);
      return {
        success: false,
        message: 'Erreur lors de la suppression de la famille',
        error: error.message
      };
    }
  },

  // Récupérer les élèves d'une famille
  getEleves: async (id) => {
    try {
      const response = await fetch(`${API_URL}/familles/${id}/eleves`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération élèves famille:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },


  getDetailsComplets: async (id, annee_scolaire_id) => {
    try {
      const response = await fetch(
        `${API_URL}/familles/${id}/details-complets?annee_scolaire_id=${annee_scolaire_id}`,
        {
          headers: getAuthHeaders()
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération détails complets famille:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // 2️⃣ Récupérer les stats pour un mois spécifique
  getStatsMois: async (id, moisId, annee_scolaire_id) => {
    try {
      const response = await fetch(
        `${API_URL}/familles/${id}/stats-mois/${moisId}?annee_scolaire_id=${annee_scolaire_id}`,
        {
          headers: getAuthHeaders()
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération stats mois:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // 3️⃣ Récupérer les totaux (Mensuel/Trimestriel/Annuel)
  getTotaux: async (id, moisId, annee_scolaire_id) => {
    try {
      const response = await fetch(
        `${API_URL}/familles/${id}/totaux?mois_id=${moisId}&annee_scolaire_id=${annee_scolaire_id}`,
        {
          headers: getAuthHeaders()
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération totaux:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // 4️⃣ Effectuer un paiement groupé pour une famille
  effectuerPaiement: async (id, paiementData) => {
    try {
      // Validation des données
      const dataToSend = {
        montant: parseFloat(paiementData.montant),
        mode_paiement_id: parseInt(paiementData.mode_paiement_id),
        type_paiement: paiementData.type_paiement, // 'total' ou 'partiel'
        mois_ids: paiementData.mois_ids, // Array d'IDs
        annee_scolaire_id: parseInt(paiementData.annee_scolaire_id),
        observations: paiementData.observations || ''
      };

      console.log('Données paiement envoyées:', dataToSend);

      const response = await fetch(`${API_URL}/familles/${id}/paiements`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(dataToSend)
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur effectuer paiement famille:', error);
      return {
        success: false,
        message: error.message || 'Erreur lors de l\'enregistrement du paiement',
        error: error.message,
        code: error.code
      };
    }
  },

  getAnnexes: async (id, categorie, annee_scolaire_id) => {
    try {
      const query = new URLSearchParams();
      if (annee_scolaire_id) query.set('annee_scolaire_id', annee_scolaire_id);

      const response = await fetch(
        `${API_URL}/familles/${id}/annexes/${categorie}?${query.toString()}`,
        { headers: getAuthHeaders() }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur r?cup?ration frais annexes famille:', error);
      return {
        success: false,
        message: error.message || 'Erreur lors de la r?cup?ration des frais annexes',
        error: error.message
      };
    }
  },

  getAnnexesPonctuels: async (id, categorie, annee_scolaire_id) => {
    try {
      const query = new URLSearchParams();
      if (annee_scolaire_id) query.set('annee_scolaire_id', annee_scolaire_id);

      const response = await fetch(
        `${API_URL}/familles/${id}/annexes/${categorie}/ponctuels?${query.toString()}`,
        { headers: getAuthHeaders() }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur recuperation paiements annexes ponctuels famille:', error);
      return { success: false, message: error.message, error: error.message };
    }
  },

  effectuerPaiementAnnexePonctuel: async (id, paiementData) => {
    try {
      const response = await fetch(`${API_URL}/familles/${id}/paiements-annexes-ponctuels`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(paiementData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur paiement annexe ponctuel famille:', error);
      return { success: false, message: error.message, error: error.message };
    }
  },
  effectuerPaiementAnnexe: async (id, paiementData) => {
    try {
      const dataToSend = {
        categorie: paiementData.categorie,
        montant: parseFloat(paiementData.montant),
        mode_paiement_id: parseInt(paiementData.mode_paiement_id),
        type_paiement: paiementData.type_paiement,
        mois_ids: paiementData.mois_ids,
        annee_scolaire_id: parseInt(paiementData.annee_scolaire_id),
        observations: paiementData.observations || ''
      };

      const response = await fetch(`${API_URL}/familles/${id}/paiements-annexes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(dataToSend)
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur paiement annexe famille:', error);
      return {
        success: false,
        message: error.message || 'Erreur lors du paiement annexe famille',
        error: error.message
      };
    }
  },
  // 5️⃣ Récupérer le rapport financier par mois
  getPaiementsParMois: async (id, annee_scolaire_id) => {
    try {
      const response = await fetch(
        `${API_URL}/familles/${id}/paiements-par-mois?annee_scolaire_id=${annee_scolaire_id}`,
        {
          headers: getAuthHeaders()
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération paiements par mois:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // 6️⃣ Récupérer l'historique complet des paiements
  getHistoriquePaiements: async (id, annee_scolaire_id, options = {}) => {
    try {
      const limit = options.limit || 50;
      const offset = options.offset || 0;

      const response = await fetch(
        `${API_URL}/familles/${id}/historique-paiements?annee_scolaire_id=${annee_scolaire_id}&limit=${limit}&offset=${offset}`,
        {
          headers: getAuthHeaders()
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération historique paiements:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // ✅ Méthode 1 : Modifier les infos générales (nom, tél, adresse)
modifierInfosGenerales: async (id, infosData) => {
  try {
    const response = await fetch(`${API_URL}/familles/${id}/modifier-infos`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(infosData)
    });

    return handleResponse(response);
  } catch (error) {
    console.error('Erreur modification infos famille:', error);
    return {
      success: false,
      error: error.message
    };
  }
},

// 🆕 Méthode 2 : Modifier le montant mensuel total
modifierMontantMensuel: async (id, data) => {
  try {
    const response = await fetch(`${API_URL}/familles/${id}/modifier-montant-mensuel`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(data)
    });

    return handleResponse(response);
  } catch (error) {
    console.error('Erreur modification montant famille:', error);
    return {
      success: false,
      error: error.message
    };
  }
},


getStatistiquesEnfants: async (anneeScolaireId = null) => {
  try {
    const query = anneeScolaireId ? `?annee_scolaire_id=${encodeURIComponent(anneeScolaireId)}` : '';
    const response = await fetch(`${API_URL}/familles/statistiques-enfants${query}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur récupération statistiques enfants:', error);
    return { success: false, error: error.message };
  }
},



  genererRecuMois: async (familleId, moisId, annee_scolaire_id) => {
    try {
      const response = await fetch(`${API_URL}/recus/famille/${familleId}/mois/${moisId}`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ annee_scolaire_id })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la génération du reçu');
      }

      // Récupérer le PDF en blob
      const blob = await response.blob();
      return blob;
    } catch (error) {
      console.error('Erreur génération reçu:', error);
      throw error;
    }
  },


  genererRecuMois: async (familleId, moisId, annee_scolaire_id) => {
  try {
    const response = await fetch(`${API_URL}/recus/famille/${familleId}/mois/${moisId}`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ annee_scolaire_id })
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la génération du reçu');
    }

    // Récupérer le PDF en blob
    const blob = await response.blob();
    return blob;
  } catch (error) {
    console.error('Erreur génération reçu:', error);
    throw error;
  }
},

// 🆕 NOUVELLE MÉTHODE 1 : Récupérer les élèves disponibles pour une famille
getElevesDisponibles: async (anneeScolaireId, searchTerm = '') => {
  try {
    const response = await fetch(
      `${API_URL}/familles/eleves-disponibles?annee_scolaire_id=${anneeScolaireId}&search=${encodeURIComponent(searchTerm)}`,
      {
        headers: getAuthHeaders()
      }
    );
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur getElevesDisponibles:', error);
    return {
      success: false,
      error: error.message
    };
  }
},

// 🆕 NOUVELLE MÉTHODE 2 : Ajouter un élève à une famille
getElevesDisponiblesConstitution: async (anneeScolaireId, params = {}) => {
  try {
    const query = new URLSearchParams();
    query.set('annee_scolaire_id', anneeScolaireId);
    if (params.search) query.set('search', params.search);
    if (params.page) query.set('page', params.page);
    if (params.limit) query.set('limit', params.limit);

    const response = await fetch(
      `${API_URL}/familles/eleves-disponibles-constitution?${query.toString()}`,
      {
        headers: getAuthHeaders()
      }
    );
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur getElevesDisponiblesConstitution:', error);
    return {
      success: false,
      error: error.message
    };
  }
},

constituerDepuisEleves: async (familleData) => {
  try {
    const response = await fetch(`${API_URL}/familles/constituer-depuis-eleves`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(familleData)
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur constituerDepuisEleves:', error);
    return {
      success: false,
      message: 'Erreur lors de la constitution de la famille',
      error: error.message
    };
  }
},

ajouterEleve: async (familleId, data) => {
  try {
    const response = await fetch(
      `${API_URL}/familles/${familleId}/ajouter-eleve`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(data)
      }
    );
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur ajouterEleve:', error);
    return {
      success: false,
      message: 'Erreur lors de l\'ajout de l\'élève',
      error: error.message
    };
  }
},



retirerEleve: async (familleId, eleveId, data = {}) => {
  try {
    const payload = typeof data === 'object' && data !== null
      ? data
      : { annee_scolaire_id: data };
    const response = await fetch(
      `${API_URL}/familles/${familleId}/retrait-eleve/${eleveId}/demande`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(payload)
      }
    );
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur retirerEleve:', error);
    return {
      success: false,
      message: 'Erreur lors du retrait de l\'élève',
      error: error.message
    };
  }
},


// 🆕 Récupérer les mois non scolarisés
  getMoisNonScolarises: async (familleId, anneeScolaireId) => {
    const response = await fetch(
      `${API_URL}/familles/${familleId}/mois-non-scolarises?annee_scolaire_id=${anneeScolaireId}`,
      {
        headers: getAuthHeaders()
      }
    );
    return handleResponse(response);
  },

  // 🆕 Sauvegarder les mois non scolarisés
  saveMoisNonScolarises: async (familleId, data) => {
    const response = await fetch(
      `${API_URL}/familles/${familleId}/mois-non-scolarises`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      }
    );
    return handleResponse(response);
  },


  appliquerProrata: async (familleId, data) => {
    try {
      const response = await fetch(`${API_URL}/familles/${familleId}/prorata`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur application prorata famille:', error);
      throw error;
    }
  },


  rechercher: async (term) => {
    try {
      const response = await fetch(
        `${API_URL}/familles/rechercher?term=${encodeURIComponent(term)}`,
        {
          method: 'GET',
          headers: getAuthHeaders()
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur recherche familles:', error);
      throw error;
    }
  },

  // 🆕 Récupérer détails complets famille
  getDetailsComplets: async (familleId, anneeScolaireId) => {
    try {
      const response = await fetch(
        `${API_URL}/familles/${familleId}/details-complets?annee_scolaire_id=${anneeScolaireId}`,
        {
          method: 'GET',
          headers: getAuthHeaders()
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur getDetailsComplets:', error);
      throw error;
    }
  },

  // ✅ Appliquer prorata famille (vous l'avez déjà)
  appliquerProrata: async (familleId, data) => {
    try {
      const response = await fetch(`${API_URL}/familles/${familleId}/prorata`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur application prorata famille:', error);
      throw error;
    }
  },



annulerProrata: async (familleId, moisId, anneeScolaireId, elevesIds) => {
    try {
      const elevesIdsParam = elevesIds.join(',');
      const response = await fetch(
        `${API_URL}/familles/${familleId}/prorata/${moisId}?annee_scolaire_id=${anneeScolaireId}&eleves_ids=${elevesIdsParam}`,
        {
          method: 'DELETE',
          headers: getAuthHeaders()
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur annulation prorata famille:', error);
      throw error;
    }
  },



  // ============================================================================
  // FAMILLES INTER-SITES
  // ============================================================================

  // Activer une famille locale en inter-site (la promouvoir vers le Hub)
  activerInterSite: async (familleLocaleId) => {
    try {
      const response = await fetch(`${API_URL}/intersite/familles/activer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ famille_locale_id: familleLocaleId })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur activation inter-site:', error);
      return { success: false, error: error.message };
    }
  },

  // Rechercher les familles inter-sites disponibles dans le groupe
  getInterSiteDisponibles: async (search = '') => {
    try {
      const response = await fetch(
        `${API_URL}/intersite/familles/disponibles?search=${encodeURIComponent(search)}`,
        { headers: getAuthHeaders() }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur recherche familles inter-site:', error);
      return { success: false, error: error.message };
    }
  },

  // Rattacher des élèves locaux à une famille inter-site du Hub
  rattacherInterSite: async (familleUuid, eleveIds, reductionsParEleve = {}, motif = '') => {
    try {
      const response = await fetch(`${API_URL}/intersite/familles/${familleUuid}/rattacher`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          eleve_ids: eleveIds,
          reductions_par_eleve: reductionsParEleve,
          motif
        })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur rattachement inter-site:', error);
      return { success: false, error: error.message };
    }
  },

  // Vue consolidée d'une famille inter-site (locaux + distants)
  getVueConsolidee: async (familleUuid, anneeScolaireId) => {
    try {
      const response = await fetch(
        `${API_URL}/intersite/familles/${familleUuid}/vue-consolidee?annee_scolaire_id=${anneeScolaireId}`,
        { headers: getAuthHeaders() }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur vue consolidée:', error);
      return { success: false, error: error.message };
    }
  },

  // Vérifier si une famille locale est inter-site
  getInterSiteInfo: async (familleLocaleId) => {
    try {
      const response = await fetch(
        `${API_URL}/intersite/familles/info-locale/${familleLocaleId}`,
        { headers: getAuthHeaders() }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur info inter-site:', error);
      return { success: false, error: error.message };
    }
  },

  // Propager la réduction vers les sites distants
  propagerReduction: async (familleUuid, reductionInscription, reductionMensuel) => {
    try {
      const response = await fetch(
        `${API_URL}/intersite/familles/${familleUuid}/propager-reduction`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify({
            reduction_inscription: reductionInscription,
            reduction_mensuel: reductionMensuel
          })
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur propagation réduction:', error);
      return { success: false, error: error.message };
    }
  },

  // Ce que le Hub porte face à ce que chaque site applique réellement.
  verifierCoherenceInterSite: async () => {
    try {
      const response = await fetch(`${API_URL}/intersite/familles/coherence`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur vérification de cohérence inter-sites:', error);
      return { success: false, error: error.message };
    }
  },

  // Les propagations qui n'ont pas pu aboutir et restent à reprendre.
  getPropagationsInterSiteEnAttente: async () => {
    try {
      const response = await fetch(`${API_URL}/intersite/familles/propagations-en-attente`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lecture des propagations en attente:', error);
      return { success: false, error: error.message };
    }
  },

  // Reprise bloquée par un accord : elle devient une demande locale, sur le site
  // qui a signé cet accord et qui seul peut le recomposer.
  demanderApplicationPropagationInterSite: async (propagationId, motif) => {
    try {
      const response = await fetch(
        `${API_URL}/intersite/familles/propagations/${propagationId}/demander`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ motif: motif || undefined })
        }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur demande d\'application de propagation:', error);
      return { success: false, error: error.message };
    }
  },

  rejouerPropagationInterSite: async (propagationId) => {
    try {
      const response = await fetch(
        `${API_URL}/intersite/familles/propagations/${propagationId}/rejouer`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur reprise de propagation:', error);
      return { success: false, error: error.message };
    }
  },


  genererLivret: async (familleId, anneeScolaireId) => {
  try {
  const response = await fetch(`${API_URL}/familles/${familleId}/livret`, {
  method: 'POST',
  headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ annee_scolaire_id: anneeScolaireId })
      });
      if (!response.ok) throw new Error('Erreur lors de la génération du livret');
      return await response.blob();
    } catch (error) {
      console.error('Erreur génération livret:', error);
      throw error;
    }
  },




  // Archiver une famille
archiverFamille: async (familleId, data) => {
  try {
    const response = await fetch(`${API_URL}/familles/${familleId}/archiver`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur archivage famille:', error);
    return { success: false, error: error.message };
  }
},

// Lister les familles archivées
getArchives: async (includeRestored = false) => {
  try {
    const response = await fetch(
      `${API_URL}/familles/archives/liste?include_restored=${includeRestored}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur liste archives:', error);
    return { success: false, error: error.message };
  }
},

// Détail d'une archive
getArchiveDetail: async (archiveId) => {
  try {
    const response = await fetch(
      `${API_URL}/familles/archives/${archiveId}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur détail archive:', error);
    return { success: false, error: error.message };
  }
},

// Restaurer une famille archivée
restaurerFamille: async (archiveId, data) => {
  try {
    const response = await fetch(`${API_URL}/familles/archives/${archiveId}/restaurer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur restauration famille:', error);
    return { success: false, error: error.message };
  }
},



// Désactiver une famille inter-site
  desactiverInterSite: async (familleUuid, data) => {
    try {
      const response = await fetch(`${API_URL}/intersite/familles/${familleUuid}/desactiver`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur désactivation inter-site:', error);
      return { success: false, error: error.message };
    }
  },


  // Re-lier une famille au Hub (après désactivation)
  relierAuHub: async (familleLocaleId) => {
    try {
      const response = await fetch(`${API_URL}/intersite/familles/relier-hub`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ famille_locale_id: familleLocaleId })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur re-liaison Hub:', error);
      return { success: false, error: error.message };
    }
  },


},




portefeuille: {
  // Récupérer les statistiques (✅ DÉJÀ OK)
  getStats: async (periode = 'mois', anneeScolaireId = null) => {
    try {
      const params = new URLSearchParams({ periode });
      if (anneeScolaireId) params.append('annee_scolaire_id', anneeScolaireId);

      const response = await fetch(`${API_URL}/portefeuille/stats?${params}`, {
        headers: getAuthHeaders()  // ✅ OK
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération stats portefeuille:', error);
      return { success: false, error: error.message };
    }
  },

  // Récupérer les transactions (✅ DÉJÀ OK)
  getTransactions: async (params = {}) => {
    try {
      const query = new URLSearchParams(params);
      const response = await fetch(`${API_URL}/portefeuille/transactions?${query}`, {
        headers: getAuthHeaders()  // ✅ OK
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération transactions:', error);
      return { success: false, error: error.message };
    }
  },

  // Ajouter une dépense (✅ CORRIGÉ)
  ajouterDepense: async (depenseData) => {
    try {
      const formData = new FormData();
      formData.append('description', depenseData.description || '');
      formData.append('montant', String(parseFloat(depenseData.montant || 0)));
      formData.append('justificatif', depenseData.justificatif || '');
      if (depenseData.annee_scolaire_id) formData.append('annee_scolaire_id', depenseData.annee_scolaire_id);
      if (depenseData.justificatif_fichier) formData.append('justificatif_fichier', depenseData.justificatif_fichier);

      const response = await fetch(`${API_URL}/portefeuille/depense`, {
        method: 'POST',
        headers: getFormDataHeaders(),
        body: formData
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur ajout depense:', error);
      return {
        success: false,
        message: error.message || 'Erreur lors de l\'ajout de la depense',
        error: error.message,
        code: error.code
      };
    }
  },

  demanderDotation: async (demandeData) => {
    try {
      const formData = new FormData();
      formData.append('montant', String(parseFloat(demandeData.montant || 0)));
      formData.append('usage_dotation', demandeData.usage_dotation || 'budget_depenses');
      formData.append('motif', demandeData.motif || '');
      formData.append('justificatif', demandeData.justificatif || '');
      if (demandeData.employes_avances) {
        formData.append('employes_avances', JSON.stringify(demandeData.employes_avances));
      }
      if (demandeData.beneficiaire_id) formData.append('beneficiaire_id', demandeData.beneficiaire_id);
      if (demandeData.annee_scolaire_id) formData.append('annee_scolaire_id', demandeData.annee_scolaire_id);
      if (demandeData.objet_dotation) formData.append('objet_dotation', demandeData.objet_dotation);
      if (demandeData.mois) formData.append('mois', demandeData.mois);
      if (demandeData.annee) formData.append('annee', demandeData.annee);
      if (demandeData.mois_scolaire_id) formData.append('mois_scolaire_id', demandeData.mois_scolaire_id);
      if (demandeData.justificatif_fichier) formData.append('justificatif_fichier', demandeData.justificatif_fichier);

      const response = await fetch(`${API_URL}/portefeuille/demandes-dotation`, {
        method: 'POST',
        headers: getFormDataHeaders(),
        body: formData
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur demande dotation:', error);
      return {
        success: false,
        message: error.message || 'Erreur lors de la demande de dotation',
        error: error.message,
        code: error.code
      };
    }
  },

  enregistrerEncaissement: async (encaissementData) => {
    try {
      const dataToSend = {
        eleve_id: encaissementData.eleve_id,
        montant: parseFloat(encaissementData.montant),
        description: encaissementData.description,
        paiement_id: encaissementData.paiement_id || null,
        annee_scolaire_id: encaissementData.annee_scolaire_id
      };

      console.log('Données encaissement envoyées:', dataToSend);

      const response = await fetch(`${API_URL}/portefeuille/encaissement`, {
        method: 'POST',
        headers: getAuthHeaders(),  // ✅ SIMPLIFIÉ
        body: JSON.stringify(dataToSend)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur enregistrement encaissement:', error);
      return {
        success: false,
        message: error.message || 'Erreur lors de l\'enregistrement de l\'encaissement',
        error: error.message,
        code: error.code
      };
    }
  },

  /**
   * L'état mensuel en PDF.
   *
   * Renvoie la réponse brute — le corps est un PDF binaire, pas du JSON.
   */
  exporterEtatMensuelPDF: async ({ anneeScolaireId, mois }) => {
    const params = new URLSearchParams({ annee_scolaire_id: anneeScolaireId, mois });
    return fetch(`${API_URL}/portefeuille/etat-mensuel/pdf?${params}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
  },

  /**
   * Récupère un justificatif de caisse comme blob.
   *
   * Le fichier était atteint par `<a href="/uploads/…">`. Deux défauts : le
   * chemin est racine-relatif, donc résolu contre l'origine du frontend — en
   * développement le proxy servait `index.html`, et le lien « ouvrait une page
   * KLASYS » ; et le montage statique ne vérifie aucun jeton, si bien que la
   * pièce était lisible déconnecté, depuis n'importe quel tenant.
   *
   * Le passage par `/api` corrige les deux. Le jeton voyage dans l'en-tête,
   * donc un lien nu ne suffit plus : on récupère un blob, que la visionneuse
   * affiche.
   *
   * @param source 'transaction' | 'depense-fixe'
   * @param piece  'depense' (la facture du mois) | 'modele' (le bail)
   */
  getJustificatif: async (source, id, piece = 'depense') => {
    try {
      const params = piece === 'modele' ? '?piece=modele' : '';
      const response = await fetch(
        `${API_URL}/portefeuille/justificatifs/${source}/${id}${params}`,
        { headers: getAuthHeaders() }
      );

      if (!response.ok) {
        let message = 'Justificatif introuvable.';
        try {
          const corps = await response.json();
          if (corps?.message) message = corps.message;
        } catch {
          // Le serveur n'a pas répondu en JSON : le message par défaut suffit.
        }
        return { success: false, message };
      }

      const blob = await response.blob();
      return { success: true, blob, url: URL.createObjectURL(blob) };
    } catch (error) {
      console.error('Erreur lecture justificatif:', error);
      return { success: false, message: 'Erreur lors de la lecture du justificatif' };
    }
  },

  /**
   * Les mois qu'une gestionnaire peut consulter.
   *
   * Les mois scolaires de l'année, puis ceux, grégoriens, où elle a des
   * mouvements sans correspondance dans le calendrier.
   */
  getMoisConsultables: async (anneeScolaireId) => {
    try {
      const response = await fetch(
        `${API_URL}/portefeuille/mois-consultables?annee_scolaire_id=${anneeScolaireId}`,
        { headers: getAuthHeaders() }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération des mois consultables:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Synthèse d'un mois : entrées ventilées, dotations à part, sorties, solde.
   *
   * `mois` est un identifiant de mois scolaire, ou 'AAAA-MM' hors calendrier.
   */
  getEtatMensuel: async ({ anneeScolaireId, mois }) => {
    try {
      const params = new URLSearchParams({ annee_scolaire_id: anneeScolaireId, mois });
      const response = await fetch(`${API_URL}/portefeuille/etat-mensuel?${params}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error("Erreur récupération de l'état mensuel:", error);
      return { success: false, error: error.message };
    }
  },

  // Récupérer les analyses (✅ DÉJÀ OK)
  getAnalyses: async (periode = 'mois', anneeScolaireId = null) => {
    try {
      const params = new URLSearchParams({ periode });
      if (anneeScolaireId) params.append('annee_scolaire_id', anneeScolaireId);

      const response = await fetch(`${API_URL}/portefeuille/analyses?${params}`, {
        headers: getAuthHeaders()  // ✅ OK
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération analyses:', error);
      return { success: false, error: error.message };
    }
  },

  // Supprimer une transaction (✅ DÉJÀ OK)
  supprimerTransaction: async (id) => {
    try {
      const response = await fetch(`${API_URL}/portefeuille/transaction/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()  // ✅ OK
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur suppression transaction:', error);
      return {
        success: false,
        message: 'Erreur lors de la suppression de la transaction',
        error: error.message
      };
    }
  },



  // Vérifier le statut de clôture
  getStatutCloture: async (date, annee_scolaire_id) => {
    try {
      const params = new URLSearchParams();
      const dateParam = normalizeDateParam(date);
      if (dateParam) params.append('date', dateParam);
      if (annee_scolaire_id) params.append('annee_scolaire_id', annee_scolaire_id);

      const response = await fetch(`${API_URL}/portefeuille/cloture/statut?${params}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur vérification statut clôture:', error);
      return { success: false, error: error.message };
    }
  },

  // Obtenir le récapitulatif avant clôture
  getRecapitulatifCloture: async (date, annee_scolaire_id) => {
    try {
      const params = new URLSearchParams();
      const dateParam = normalizeDateParam(date);
      if (dateParam) params.append('date', dateParam);
      if (annee_scolaire_id) params.append('annee_scolaire_id', annee_scolaire_id);

      const response = await fetch(`${API_URL}/portefeuille/cloture/recapitulatif?${params}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récapitulatif clôture:', error);
      return { success: false, error: error.message };
    }
  },

  // Ouvrir la caisse du jour
  ouvrirCaisse: async (date, annee_scolaire_id, solde_ouverture = 0, note_ouverture = '') => {
    try {
      const response = await fetch(`${API_URL}/portefeuille/cloture/ouvrir`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ date, annee_scolaire_id, solde_ouverture: 0, note: note_ouverture })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur ouverture caisse:', error);
      return {
        success: false,
        message: error.message || 'Erreur lors de l\'ouverture de la caisse',
        error: error.message,
        code: error.code
      };
    }
  },

  // Clôturer la caisse
  cloturerCaisse: async (date, note, annee_scolaire_id, montant_declare, motif_ecart = '') => {
    try {
      const response = await fetch(`${API_URL}/portefeuille/cloture/cloturer`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ date, note, annee_scolaire_id, montant_declare, motif_ecart })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur clôture caisse:', error);
      return {
        success: false,
        message: error.message || 'Erreur lors de la clôture de la caisse',
        error: error.message,
        code: error.code
      };
    }
  },

  // Rouvrir une caisse clôturée (admin)
  rouvrirCaisse: async (date, annee_scolaire_id) => {
    try {
      const response = await fetch(`${API_URL}/portefeuille/cloture/rouvrir`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ date, annee_scolaire_id })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur réouverture caisse:', error);
      return {
        success: false,
        message: error.message || 'Erreur lors de la réouverture de la caisse',
        error: error.message,
        code: error.code
      };
    }
  },

  demanderOuvertureExceptionnelle: async ({ date, annee_scolaire_id, motif }) => {
    try {
      const response = await fetch(`${API_URL}/portefeuille/cloture/demande-ouverture-exceptionnelle`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ date, annee_scolaire_id, motif })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur demande ouverture exceptionnelle caisse:', error);
      return {
        success: false,
        message: error.message || "Erreur lors de la demande d'ouverture exceptionnelle",
        error: error.message,
        code: error.code
      };
    }
  },

  // ========================================================================
  // MÉTHODES AVEC FILTRES AVANCÉS (modifier les existantes si nécessaire)
  // ========================================================================

  // Récupérer les transactions avec filtres avancés
  getTransactionsFiltrees: async (params = {}) => {
    try {
      const query = new URLSearchParams();

      // Paramètres existants
      if (params.periode) query.append('periode', params.periode);
      if (params.type) query.append('type', params.type);
      if (params.annee_scolaire_id) query.append('annee_scolaire_id', params.annee_scolaire_id);
      if (params.page) query.append('page', params.page);
      if (params.limit) query.append('limit', params.limit);

      // NOUVEAUX paramètres de filtrage
      if (params.section_id) query.append('section_id', params.section_id);
      if (params.classe_id) query.append('classe_id', params.classe_id);
      if (params.search_eleve) query.append('search_eleve', params.search_eleve);
      if (params.mode_paiement) query.append('mode_paiement', params.mode_paiement);
      if (params.date_debut) query.append('date_debut', params.date_debut);
      if (params.date_fin) query.append('date_fin', params.date_fin);
      if (params.depense_scope) query.append('depense_scope', params.depense_scope);

      const response = await fetch(`${API_URL}/portefeuille/transactions?${query}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération transactions filtrées:', error);
      return { success: false, error: error.message };
    }
  },

  // Stats avec filtres avancés
  getStatsFiltrees: async (params = {}) => {
    try {
      const query = new URLSearchParams();

      if (params.periode) query.append('periode', params.periode);
      if (params.annee_scolaire_id) query.append('annee_scolaire_id', params.annee_scolaire_id);
      if (params.section_id) query.append('section_id', params.section_id);
      if (params.classe_id) query.append('classe_id', params.classe_id);
      if (params.date_debut) query.append('date_debut', params.date_debut);
      if (params.date_fin) query.append('date_fin', params.date_fin);

      const response = await fetch(`${API_URL}/portefeuille/stats?${query}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération stats filtrées:', error);
      return { success: false, error: error.message };
    }
  },


    getStatsDetaillees: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/portefeuille/stats-detaillees?${queryString}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },


  getEncaissements: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/portefeuille/encaissements?${queryString}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },


// 🆕 Récupérer les salaires
getSalaires: async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const response = await fetch(`${API_URL}/portefeuille/salaires?${queryString}`, {
    headers: getAuthHeaders()
  });
  return handleResponse(response);
},



},


// INTER SITE ENDPOINT


intersite: {

  // ========================================
  // STATUT & CONFIGURATION
  // ========================================

  // Ce que la clôture d'une liaison va changer, avant de la demander
  analyserClotureChildLink: async (id) => {
    try {
      const response = await fetch(`${API_URL}/intersite/child-links/${id}/analyse-cloture`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur analyserClotureChildLink:', error);
      return { success: false, error: error.message };
    }
  },

  // Terminer définitivement une liaison enfant à charge
  terminateChildLink: async (id, raison) => {
    try {
      const response = await fetch(`${API_URL}/intersite/child-links/${id}/terminate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ raison })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur terminateChildLink:', error);
      return { success: false, error: error.message };
    }
  },

  // Contrôle de cohérence des transferts et des enfants à charge (lecture seule)
  coherenceTransferts: async () => {
    try {
      const response = await fetch(`${API_URL}/intersite/coherence`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur coherenceTransferts:', error);
      return { success: false, error: error.message };
    }
  },

  // Vérifier si le tenant fait partie d'un groupe inter-sites
  getStatus: async () => {
    try {
      const response = await fetch(`${API_URL}/intersite/status`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur getStatus intersite:', error);
      return { success: false, error: error.message };
    }
  },

  // Détails du groupe + tous les sites
  getGroup: async () => {
    try {
      const response = await fetch(`${API_URL}/intersite/group`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur getGroup:', error);
      return { success: false, error: error.message };
    }
  },

  // Sites frères du tenant actuel
  getSiblings: async () => {
    try {
      const response = await fetch(`${API_URL}/intersite/siblings`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur getSiblings:', error);
      return { success: false, error: error.message };
    }
  },

  // ========================================
  // TRANSFERTS DE PERSONNEL
  // ========================================

  // Créer une demande de transfert
  createTransfer: async (data) => {
    try {
      const response = await fetch(`${API_URL}/intersite/transfers`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur createTransfer:', error);
      return { success: false, error: error.message };
    }
  },

  // Lister les demandes de transfert
  getTransfers: async (status = null) => {
    try {
      const url = status
        ? `${API_URL}/intersite/transfers?status=${status}`
        : `${API_URL}/intersite/transfers`;
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur getTransfers:', error);
      return { success: false, error: error.message };
    }
  },

  // Détail d'une demande
  getTransferDetail: async (transferId) => {
    try {
      const response = await fetch(`${API_URL}/intersite/transfers/${transferId}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur getTransferDetail:', error);
      return { success: false, error: error.message };
    }
  },

  // Approuver le départ (site source)
  approveDeparture: async (transferId) => {
    try {
      const response = await fetch(`${API_URL}/intersite/transfers/${transferId}/approve-departure`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur approveDeparture:', error);
      return { success: false, error: error.message };
    }
  },

  // Approuver l'accueil (site cible)
  approveArrival: async (transferId) => {
    try {
      const response = await fetch(`${API_URL}/intersite/transfers/${transferId}/approve-arrival`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur approveArrival:', error);
      return { success: false, error: error.message };
    }
  },

  // Exécuter le transfert
  executeTransfer: async (transferId) => {
    try {
      const response = await fetch(`${API_URL}/intersite/transfers/${transferId}/execute`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur executeTransfer:', error);
      return { success: false, error: error.message };
    }
  },

  // Rejeter un transfert
  rejectTransfer: async (transferId, reason) => {
    try {
      const response = await fetch(`${API_URL}/intersite/transfers/${transferId}/reject`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur rejectTransfer:', error);
      return { success: false, error: error.message };
    }
  },

  // ========================================
  // LIAISONS ENFANTS À CHARGE INTER-SITES
  // ========================================

  // Créer une liaison parent ↔ enfant cross-site
  createChildLink: async (data) => {
    try {
      const response = await fetch(`${API_URL}/intersite/child-links`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur createChildLink:', error);
      return { success: false, error: error.message };
    }
  },

  // Toutes les liaisons enfants du site actuel (parent ou enfant)
  getAllChildLinks: async () => {
    try {
      const response = await fetch(`${API_URL}/intersite/child-links`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur getAllChildLinks:', error);
      return { success: false, error: error.message };
    }
  },

  // Liaisons d'un parent (employé du site actuel)
  getChildLinksForParent: async (employeId) => {
    try {
      const response = await fetch(`${API_URL}/intersite/child-links/parent/${employeId}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur getChildLinksForParent:', error);
      return { success: false, error: error.message };
    }
  },

  // Retenues inter-sites actives pour un employé
  getActiveDeductions: async (employeId) => {
    try {
      const response = await fetch(`${API_URL}/intersite/child-links/deductions/${employeId}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur getActiveDeductions:', error);
      return { success: false, error: error.message };
    }
  },

  // ========================================
  // TRANSACTIONS COMPTABLES
  // ========================================

  // Crédits en attente pour ce site
  getPendingCredits: async () => {
    try {
      const response = await fetch(`${API_URL}/intersite/transactions/pending`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur getPendingCredits:', error);
      return { success: false, error: error.message };
    }
  },

  // Historique des transactions
  getTransactionHistory: async (mois = null, annee = null) => {
    try {
      let url = `${API_URL}/intersite/transactions/history`;
      const params = new URLSearchParams();
      if (mois) params.append('mois', mois);
      if (annee) params.append('annee', annee);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur getTransactionHistory:', error);
      return { success: false, error: error.message };
    }
  },

  // Appliquer un crédit manuellement
  applyCreditManually: async (transactionId) => {
    try {
      const response = await fetch(`${API_URL}/intersite/transactions/${transactionId}/apply-credit`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur applyCreditManually:', error);
      return { success: false, error: error.message };
    }
  },

  // ========================================
  // ENTITÉS & AUDIT
  // ========================================

  // Rechercher des entités dans le Hub
  searchEntities: async (type = null, query = '') => {
    try {
      const params = new URLSearchParams();
      if (type) params.append('type', type);
      if (query) params.append('q', query);

      const response = await fetch(`${API_URL}/intersite/entities/search?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur searchEntities:', error);
      return { success: false, error: error.message };
    }
  },

  // Rechercher des entités sur un site distant (élèves cross-tenant)
  searchRemoteEntities: async (targetSite, type = 'eleve', query = '') => {
    try {
      const params = new URLSearchParams({ target_site: targetSite, type, q: query });
      const response = await fetch(`${API_URL}/intersite/entities/search-remote?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur searchRemoteEntities:', error);
      return { success: false, error: error.message };
    }
  },

  // Journal d'audit
  getAuditLog: async (limit = 50) => {
    try {
      const response = await fetch(`${API_URL}/intersite/audit?limit=${limit}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur getAuditLog:', error);
      return { success: false, error: error.message };
    }
  },

  // ========================================
  // ADMIN - GROUPES
  // ========================================

  createGroup: async (data) => {
    try {
      const response = await fetch(`${API_URL}/intersite/groups`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur createGroup:', error);
      return { success: false, error: error.message };
    }
  },

  addSiteToGroup: async (groupId, data) => {
    try {
      const response = await fetch(`${API_URL}/intersite/groups/${groupId}/sites`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ [API] Erreur addSiteToGroup:', error);
      return { success: false, error: error.message };
    }
  },


  // Récupérer les enfants inter-sites d'un employé
    getEnfantsInterSite: async (employeId) => {
      try {
        const response = await fetch(
          `${API_URL}/intersite/child-links/deductions/${employeId}`,
          { headers: getAuthHeaders() }
        );
        return handleResponse(response);
      } catch (error) {
        console.error('❌ Erreur getEnfantsInterSite:', error);
        return { success: false, data: [], error: error.message };
      }
    },


    reactivateChildLink: async (linkId) => {
    try {
      const response = await fetch(`${API_URL}/intersite/child-links/${linkId}/reactivate`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur reactivateChildLink:', error);
      return { success: false, error: error.message };
    }
  },




// Transferts d'élèves
  previewEleveTransfer: async (eleveId) => {
    try {
      const response = await fetch(`${API_URL}/intersite/eleve-transfers/discovery`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ eleve_id: eleveId })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur previewEleveTransfer:', error);
      return { success: false, error: error.message };
    }
  },

  createEleveTransfer: async (data) => {
    try {
      const response = await fetch(`${API_URL}/intersite/eleve-transfers`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur createEleveTransfer:', error);
      return { success: false, error: error.message };
    }
  },

  getEleveTransfers: async () => {
    try {
      const response = await fetch(`${API_URL}/intersite/eleve-transfers`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur getEleveTransfers:', error);
      return { success: false, error: error.message };
    }
  },

  getEleveTransferDetail: async (uuid) => {
    try {
      const response = await fetch(`${API_URL}/intersite/eleve-transfers/${uuid}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur getEleveTransferDetail:', error);
      return { success: false, error: error.message };
    }
  },

  approveEleveTransferSource: async (uuid) => {
    try {
      const response = await fetch(`${API_URL}/intersite/eleve-transfers/${uuid}/approve-source`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur approveSource:', error);
      return { success: false, error: error.message };
    }
  },

  approveEleveTransferCible: async (uuid, classeId) => {
    try {
      const response = await fetch(`${API_URL}/intersite/eleve-transfers/${uuid}/approve-cible`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ classe_id: classeId })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur approveCible:', error);
      return { success: false, error: error.message };
    }
  },

  executeEleveTransfer: async (uuid) => {
    try {
      const response = await fetch(`${API_URL}/intersite/eleve-transfers/${uuid}/execute`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur executeEleveTransfer:', error);
      return { success: false, error: error.message };
    }
  },

  rejectEleveTransfer: async (uuid, reason) => {
    try {
      const response = await fetch(`${API_URL}/intersite/eleve-transfers/${uuid}/reject`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur rejectEleveTransfer:', error);
      return { success: false, error: error.message };
    }
  },

  getClassesSiteCible: async (uuid) => {
    try {
      const response = await fetch(`${API_URL}/intersite/eleve-transfers/${uuid}/classes-cible`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('❌ Erreur getClassesSiteCible:', error);
      return { success: false, error: error.message };
    }
  },



},

// ============================================================================
// SECTIONS
// ============================================================================
sections: {
  // Récupérer toutes les sections
  getAll: async () => {
    try {
      const response = await fetch(`${API_URL}/sections`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération sections:', error);
      return { success: false, error: error.message };
    }
  },

  // Récupérer les classes d'une section
  getClasses: async (sectionId) => {
    try {
      const response = await fetch(`${API_URL}/sections/${sectionId}/classes`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération classes de la section:', error);
      return { success: false, error: error.message };
    }
  }
},

// ============================================================================
// DÉPENSES FIXES RÉCURRENTES
// ============================================================================
depensesFixes: {
  // Récupérer toutes les dépenses fixes
  getAll: async (annee_scolaire_id) => {
    try {
      const params = new URLSearchParams();
      if (annee_scolaire_id) params.append('annee_scolaire_id', annee_scolaire_id);

      const response = await fetch(`${API_URL}/depenses-fixes?${params}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération dépenses fixes:', error);
      return { success: false, error: error.message };
    }
  },

  // Récupérer les dépenses en attente d'application mensuelle
  getPendingMonthly: async (annee_scolaire_id) => {
    try {
      const params = new URLSearchParams({ annee_scolaire_id });

      const response = await fetch(`${API_URL}/depenses-fixes/pending-monthly?${params}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération dépenses en attente:', error);
      return { success: false, error: error.message };
    }
  },

  // Créer une dépense fixe
  create: async (depenseData) => {
    try {
      const formData = new FormData();
      formData.append('nom', depenseData.nom || '');
      formData.append('montant', String(parseFloat(depenseData.montant || 0)));
      formData.append('justificatif', depenseData.justificatif || '');
      formData.append('statut', depenseData.statut || 'actif');
      if (depenseData.annee_scolaire_id) formData.append('annee_scolaire_id', depenseData.annee_scolaire_id);
      if (depenseData.justificatif_fichier) formData.append('justificatif_fichier', depenseData.justificatif_fichier);

      const response = await fetch(`${API_URL}/depenses-fixes`, {
        method: 'POST',
        headers: getFormDataHeaders(),
        body: formData
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur creation depense fixe:', error);
      return {
        success: false,
        message: 'Erreur lors de la creation de la depense fixe',
        error: error.message
      };
    }
  },

  update: async (id, depenseData) => {
    try {
      const formData = new FormData();
      if (depenseData.nom !== undefined) formData.append('nom', depenseData.nom || '');
      if (depenseData.montant !== undefined) formData.append('montant', String(parseFloat(depenseData.montant || 0)));
      if (depenseData.justificatif !== undefined) formData.append('justificatif', depenseData.justificatif || '');
      if (depenseData.statut !== undefined) formData.append('statut', depenseData.statut || 'actif');
      if (depenseData.annee_scolaire_id) formData.append('annee_scolaire_id', depenseData.annee_scolaire_id);
      if (depenseData.justificatif_fichier) formData.append('justificatif_fichier', depenseData.justificatif_fichier);

      const response = await fetch(`${API_URL}/depenses-fixes/${id}`, {
        method: 'PUT',
        headers: getFormDataHeaders(),
        body: formData
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur modification depense fixe:', error);
      return {
        success: false,
        message: 'Erreur lors de la modification de la depense fixe',
        error: error.message
      };
    }
  },

  desactiver: async (id) => {
    try {
      const response = await fetch(`${API_URL}/depenses-fixes/${id}/desactiver`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur désactivation dépense fixe:', error);
      return {
        success: false,
        message: 'Erreur lors de la désactivation',
        error: error.message
      };
    }
  },

  // Supprimer une dépense fixe
  delete: async (id) => {
    try {
      const response = await fetch(`${API_URL}/depenses-fixes/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur suppression dépense fixe:', error);
      return {
        success: false,
        message: 'Erreur lors de la suppression',
        error: error.message
      };
    }
  },

  // Appliquer les dépenses mensuelles.
  //
  // `justificatif_fichier` est la facture DU MOIS — celle qui prouve le
  // paiement, par opposition au bail que porte le modèle. Sans fichier, on
  // garde l'envoi JSON : le serveur accepte les deux formes.
  appliquerMensuelles: async (depenses, annee_scolaire_id, mois_consulte = null, justificatif_fichier = null) => {
    try {
      let response;

      if (justificatif_fichier) {
        const formData = new FormData();
        formData.append('depenses', JSON.stringify(depenses));
        if (annee_scolaire_id) formData.append('annee_scolaire_id', annee_scolaire_id);
        if (mois_consulte) formData.append('mois_consulte', mois_consulte);
        formData.append('justificatif_fichier', justificatif_fichier);

        response = await fetch(`${API_URL}/depenses-fixes/appliquer-mensuelles`, {
          method: 'POST',
          headers: getFormDataHeaders(),
          body: formData
        });
      } else {
        response = await fetch(`${API_URL}/depenses-fixes/appliquer-mensuelles`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ depenses, annee_scolaire_id, mois_consulte })
        });
      }

      return handleResponse(response);
    } catch (error) {
      console.error('Erreur application dépenses mensuelles:', error);
      return {
        success: false,
        message: 'Erreur lors de l\'application des dépenses',
        error: error.message
      };
    }
  },

  // Réglages : le justificatif est-il exigé ?
  getParametres: async (annee_scolaire_id) => {
    try {
      const params = new URLSearchParams();
      if (annee_scolaire_id) params.append('annee_scolaire_id', annee_scolaire_id);

      const response = await fetch(`${API_URL}/depenses-fixes/parametres?${params}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lecture réglages dépenses:', error);
      return { success: false, error: error.message };
    }
  },

  setParametres: async (annee_scolaire_id, valeurs) => {
    try {
      const response = await fetch(`${API_URL}/depenses-fixes/parametres`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ annee_scolaire_id, ...valeurs })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur enregistrement réglages dépenses:', error);
      return { success: false, error: error.message };
    }
  },

  // Annuler les dépenses du mois
  annulerMois: async (annee_scolaire_id) => {
    try {
      const response = await fetch(`${API_URL}/depenses-fixes/annuler-mois`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ annee_scolaire_id })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur annulation dépenses mois:', error);
      return {
        success: false,
        message: 'Erreur lors de l\'annulation',
        error: error.message
      };
    }
  }
},



caisseCentrale: {
  // ========================================================================
  // RÉCUPÉRATION DES DONNÉES
  // ========================================================================

  // La remise de la personne connectée pour un jour (aujourd'hui par défaut).
  // Ouverte sans la dérogation caisse centrale : c'est ce que lit le Portefeuille.
  getMaCaisse: async (params = {}) => {
    try {
      const query = new URLSearchParams();
      if (params.date) query.append('date', params.date);
      if (params.annee_scolaire_id) query.append('annee_scolaire_id', params.annee_scolaire_id);
      const response = await fetch(`${API_URL}/caisse-centrale/ma-caisse?${query}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur lecture de ma caisse centrale:', error);
      return { success: false, data: null, error: error.message };
    }
  },

  // Récupérer toutes les caisses avec filtres
  getAll: async (params = {}) => {
    try {
      const query = new URLSearchParams();

      if (params.periode) query.append('periode', params.periode);
      if (params.statut) query.append('statut', params.statut);
      if (params.annee_scolaire_id) query.append('annee_scolaire_id', params.annee_scolaire_id);
      if (params.search_gestionnaire) query.append('search_gestionnaire', params.search_gestionnaire);
      if (params.page) query.append('page', params.page);
      if (params.limit) query.append('limit', params.limit);

      const response = await fetch(`${API_URL}/caisse-centrale?${query}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération caisses centrales:', error);
      return { success: false, error: error.message };
    }
  },

  // Récupérer les statistiques globales
  getStats: async (periode = 'mois', annee_scolaire_id = null) => {
    try {
      const params = new URLSearchParams({ periode });
      if (annee_scolaire_id) params.append('annee_scolaire_id', annee_scolaire_id);

      const response = await fetch(`${API_URL}/caisse-centrale/stats?${params}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération stats caisse centrale:', error);
      return { success: false, error: error.message };
    }
  },

  // Recuperer le bilan financier mensuel consolide
  // Les mois consultables du bilan : ceux du calendrier scolaire, puis ceux
  // qui portent des mouvements sans y figurer. Le sélecteur croisait
  // auparavant 12 mois et 5 années — 60 combinaisons dont presque aucune ne
  // rendait quoi que ce soit.
  getMoisBilan: async (annee_scolaire_id) => {
    try {
      const response = await fetch(
        `${API_URL}/caisse-centrale/mois-bilan?annee_scolaire_id=${annee_scolaire_id}`,
        { headers: getAuthHeaders() }
      );
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération des mois du bilan:', error);
      return { success: false, error: error.message };
    }
  },

  // `mois` est une RÉFÉRENCE : l'identifiant d'un mois scolaire, ou `AAAA-MM`
  // pour un mois hors calendrier. Plus un numéro grégorien.
  getBilanMensuel: async (params = {}) => {
    try {
      const query = new URLSearchParams();
      if (params.mois) query.append('mois', params.mois);
      if (params.annee_scolaire_id) query.append('annee_scolaire_id', params.annee_scolaire_id);

      const response = await fetch(`${API_URL}/caisse-centrale/bilan-mensuel?${query}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur recuperation bilan mensuel:', error);
      return { success: false, error: error.message };
    }
  },

  // Les caisses du jour : ouvertes, remises, et celles que personne n'a
  // ouvertes. Le registre des remises ne montrait que les secondes.
  getCaissesDuJour: async (params = {}) => {
    try {
      const query = new URLSearchParams();
      if (params.annee_scolaire_id) query.append('annee_scolaire_id', params.annee_scolaire_id);

      const response = await fetch(`${API_URL}/caisse-centrale/caisses-du-jour?${query}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur recuperation des caisses du jour:', error);
      return { success: false, error: error.message };
    }
  },

  // Recuperer le registre reel des mouvements centraux
  getMouvements: async (params = {}) => {
    try {
      const query = new URLSearchParams();
      if (params.annee_scolaire_id) query.append('annee_scolaire_id', params.annee_scolaire_id);
      if (params.type) query.append('type', params.type);
      if (params.page) query.append('page', params.page);
      if (params.limit) query.append('limit', params.limit);

      const response = await fetch(`${API_URL}/caisse-centrale/mouvements?${query}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur recuperation mouvements caisse centrale:', error);
      return { success: false, error: error.message };
    }
  },

  // Ajouter un mouvement manuel dans la caisse centrale
  getDemandesDotation: async (params = {}) => {
    try {
      const query = new URLSearchParams();
      if (params.annee_scolaire_id) query.append('annee_scolaire_id', params.annee_scolaire_id);
      if (params.statut) query.append('statut', params.statut);
      if (params.page) query.append('page', params.page);
      if (params.limit) query.append('limit', params.limit);

      const response = await fetch(`${API_URL}/caisse-centrale/demandes-dotation?${query}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur recuperation demandes dotation:', error);
      return { success: false, error: error.message };
    }
  },

  deciderDemandeDotation: async (id, data) => {
    try {
      const response = await fetch(`${API_URL}/caisse-centrale/demandes-dotation/${id}/decision`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur decision demande dotation:', error);
      return { success: false, error: error.message };
    }
  },

  createMouvement: async (data) => {
    try {
      const response = await fetch(`${API_URL}/caisse-centrale/mouvements`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur creation mouvement caisse centrale:', error);
      return { success: false, error: error.message };
    }
  },

  // Récupérer les détails d'une caisse spécifique
  getById: async (id) => {
    try {
      const response = await fetch(`${API_URL}/caisse-centrale/${id}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération détails caisse:', error);
      return { success: false, error: error.message };
    }
  },

  // Récupérer les observations d'une caisse
  getObservations: async (caisseId) => {
    try {
      const response = await fetch(`${API_URL}/caisse-centrale/${caisseId}/observations`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération observations:', error);
      return { success: false, error: error.message };
    }
  },

  // ========================================================================
  // VALIDATION
  // ========================================================================

  // Valider une transaction spécifique
  // `transactionId` : un identifiant ou une liste (sélection, en un seul appel).
  // `autoValidation` : { auto_validation, justification, code_totp } quand la
  // personne concernée valide elle-même comme super validateur.
  validerTransaction: async (caisseId, transactionId, autoValidation = {}) => {
    try {
      const cible = Array.isArray(transactionId)
        ? { transaction_ids: transactionId }
        : { transaction_id: transactionId };
      const response = await fetch(`${API_URL}/caisse-centrale/${caisseId}/valider-transaction`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...cible, ...autoValidation })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur validation transaction:', error);
      return {
        success: false,
        message: 'Erreur lors de la validation de la transaction',
        error: error.message
      };
    }
  },

  // Valider toute une caisse
  validerCaisse: async (caisseId, noteValidation = null, autoValidation = {}) => {
    try {
      const response = await fetch(`${API_URL}/caisse-centrale/${caisseId}/valider-tout`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ note_validation: noteValidation, ...autoValidation })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur validation caisse:', error);
      return {
        success: false,
        message: 'Erreur lors de la validation de la caisse',
        error: error.message
      };
    }
  },

  // ========================================================================
  // OBSERVATIONS
  // ========================================================================

  // Ajouter une observation
  ajouterObservation: async (caisseId, observationData) => {
    try {
      const dataToSend = {
        transaction_id: observationData.transaction_id || null,
        type: observationData.type, // 'observation' ou 'justificatif'
        message: observationData.message
      };

      console.log('Données observation envoyées:', dataToSend);

      const response = await fetch(`${API_URL}/caisse-centrale/${caisseId}/observation`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(dataToSend)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur ajout observation:', error);
      return {
        success: false,
        message: 'Erreur lors de l\'ajout de l\'observation',
        error: error.message
      };
    }
  },

  // Répondre à une observation
  repondreObservation: async (observationId, reponse) => {
    try {
      const response = await fetch(`${API_URL}/caisse-centrale/observation/${observationId}/repondre`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reponse })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur réponse observation:', error);
      return {
        success: false,
        message: 'Erreur lors de la réponse à l\'observation',
        error: error.message
      };
    }
  },

  // Supprimer une observation
  supprimerObservation: async (observationId) => {
    try {
      const response = await fetch(`${API_URL}/caisse-centrale/observation/${observationId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur suppression observation:', error);
      return {
        success: false,
        message: 'Erreur lors de la suppression de l\'observation',
        error: error.message
      };
    }
  },

  // ========================================================================
  // CLÔTURE MENSUELLE
  // ========================================================================

  // Vérifier si la clôture mensuelle est possible
  verifierClotureMensuelle: async (mois, annee, annee_scolaire_id) => {
    try {
      const params = new URLSearchParams();
      if (mois) params.append('mois', mois);
      if (annee) params.append('annee', annee);
      if (annee_scolaire_id) params.append('annee_scolaire_id', annee_scolaire_id);

      const response = await fetch(`${API_URL}/caisse-centrale/cloture-mensuelle/verifier?${params}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur vérification clôture mensuelle:', error);
      return { success: false, error: error.message };
    }
  },

  // Obtenir le récapitulatif du mois
  getRecapitulatifMensuel: async (mois, annee, annee_scolaire_id) => {
    try {
      const params = new URLSearchParams();
      if (mois) params.append('mois', mois);
      if (annee) params.append('annee', annee);
      if (annee_scolaire_id) params.append('annee_scolaire_id', annee_scolaire_id);

      const response = await fetch(`${API_URL}/caisse-centrale/cloture-mensuelle/recapitulatif?${params}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récapitulatif mensuel:', error);
      return { success: false, error: error.message };
    }
  },

  // Clôturer le mois
  cloturerMois: async (mois, annee, annee_scolaire_id, note_cloture = null) => {
    try {
      const response = await fetch(`${API_URL}/caisse-centrale/cloture-mensuelle/cloturer`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ mois, annee, annee_scolaire_id, note_cloture })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur clôture mois:', error);
      return {
        success: false,
        message: 'Erreur lors de la clôture du mois',
        error: error.message
      };
    }
  },

  // Récupérer l'historique des clôtures mensuelles
  getHistoriqueClotures: async (annee_scolaire_id, page = 1, limit = 12) => {
    try {
      const params = new URLSearchParams();
      if (annee_scolaire_id) params.append('annee_scolaire_id', annee_scolaire_id);
      if (page) params.append('page', page);
      if (limit) params.append('limit', limit);

      const response = await fetch(`${API_URL}/caisse-centrale/cloture-mensuelle/historique?${params}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur historique clôtures:', error);
      return { success: false, error: error.message };
    }
  },

  // Récupérer les détails d'une clôture mensuelle
  getClotureMensuelle: async (clotureId) => {
    try {
      const response = await fetch(`${API_URL}/caisse-centrale/cloture-mensuelle/${clotureId}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur détails clôture mensuelle:', error);
      return { success: false, error: error.message };
    }
  }
},

// ============================================================================
// CLÔTURE DE CAISSE (À AJOUTER DANS portefeuille)
// ============================================================================


// ============================================================================
// SERVICES POUR LES EXAMENS NATIONAUX
// ============================================================================
examens: {

  // ========== CONFIGURATION ==========

  /**
   * Récupérer la configuration des examens
   */
  getConfig: async () => {
    try {
      const response = await fetch(`${API_URL}/examens/config`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération config examens:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Mettre à jour la configuration d'un examen
   */
  updateConfig: async (code, configData) => {
    try {
      const response = await fetch(`${API_URL}/examens/config/${code}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(configData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur mise à jour config:', error);
      return { success: false, error: error.message };
    }
  },

  // ========== DOCUMENTS REQUIS ==========

  /**
   * Récupérer les documents requis pour un type d'examen
   */
  getDocumentsRequis: async (examenType) => {
    try {
      const response = await fetch(`${API_URL}/examens/documents-requis/${examenType}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération documents requis:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Constater la réception d'une pièce, sans fichier.
   *
   * La question au guichet est « l'ai-je reçue ? », pas « l'ai-je numérisée ? ».
   * Joindre un fichier reste possible ensuite, sur la même pièce.
   */
  marquerReceptionPiece: async ({ eleve_id = null, candidat_libre_id = null, document_requis_id, recue = true }) => {
    try {
      const response = await fetch(`${API_URL}/examens/documents/reception`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ eleve_id, candidat_libre_id, document_requis_id, recue })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur marquage de la pièce:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Liste des candidats en PDF.
   *
   * Renvoie la réponse brute — pas `handleResponse` : le corps est un PDF
   * binaire, pas du JSON. L'appelant en fait un blob et déclenche le
   * téléchargement, comme les autres exports du projet.
   */
  exporterListePDF: async ({
    examenType = null,
    candidatsLibres = false,
    filtreStatut = null,
    filtreClasse = null,
    filtreRecherche = null
  } = {}) => {
    const params = new URLSearchParams();
    if (examenType) params.append('examen_type', examenType);
    if (candidatsLibres) params.append('candidats_libres', '1');
    if (filtreStatut) params.append('filtre_statut', filtreStatut);
    if (filtreClasse) params.append('filtre_classe', filtreClasse);
    if (filtreRecherche) params.append('filtre_recherche', filtreRecherche);

    return fetch(`${API_URL}/examens/export/pdf?${params.toString()}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
  },

  /**
   * Recopier les pièces exigées d'une campagne vers la campagne courante.
   *
   * Le paramétrage étant annualisé, une nouvelle campagne démarre sans aucune
   * pièce : sans cette recopie il faudrait tout ressaisir chaque année.
   */
  copierDocumentsRequis: async (anneeSource) => {
    try {
      const response = await fetch(`${API_URL}/examens/documents-requis/copier`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ annee_source: anneeSource })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur recopie des pièces exigées:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Ajouter un document requis
   */
  addDocumentRequis: async (documentData) => {
    try {
      const response = await fetch(`${API_URL}/examens/documents-requis`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(documentData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur ajout document requis:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Modifier un document requis
   */
  updateDocumentRequis: async (id, documentData) => {
    try {
      const response = await fetch(`${API_URL}/examens/documents-requis/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(documentData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur modification document requis:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Supprimer un document requis
   */
  deleteDocumentRequis: async (id) => {
    try {
      const response = await fetch(`${API_URL}/examens/documents-requis/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur suppression document requis:', error);
      return { success: false, error: error.message };
    }
  },

  // ========== ÉLÈVES PAR EXAMEN ==========

  /**
   * Récupérer les élèves de CM2 (CEPE)
   */
  getElevesCEPE: async () => {
    try {
      const response = await fetch(`${API_URL}/examens/eleves/cepe`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération élèves CEPE:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Récupérer les élèves de 3ème (BEPC)
   */
  getElevesBEPC: async () => {
    try {
      const response = await fetch(`${API_URL}/examens/eleves/bepc`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération élèves BEPC:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Récupérer les élèves de Terminale (BAC)
   */
  getElevesBAC: async () => {
    try {
      const response = await fetch(`${API_URL}/examens/eleves/bac`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération élèves BAC:', error);
      return { success: false, error: error.message };
    }
  },

  // ========== INSCRIPTIONS ==========

  /**
   * Inscrire un élève à un examen
   */
  inscrireEleve: async (eleveId, examenType) => {
    try {
      const response = await fetch(`${API_URL}/examens/inscriptions`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          eleve_id: eleveId,
          examen_type: examenType
        })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur inscription élève:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Récupérer les détails d'une inscription
   */
  getDetailsInscription: async (eleveId, examenType) => {
    try {
      const response = await fetch(`${API_URL}/examens/inscriptions/eleve/${eleveId}/${examenType}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération détails inscription:', error);
      return { success: false, error: error.message };
    }
  },

  // ========== UPLOAD DOCUMENTS ==========

  /**
   * Upload un document pour un élève
   */
  uploadDocument: async (eleveId, documentRequisId, file) => {
    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('eleve_id', eleveId);
      formData.append('document_requis_id', documentRequisId);

      const response = await fetch(`${API_URL}/examens/documents/upload`, {
        method: 'POST',
        headers: getFormDataHeaders(),
        body: formData
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur upload document:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Supprimer un document
   */
  deleteDocument: async (documentId) => {
    try {
      const response = await fetch(`${API_URL}/examens/documents/${documentId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur suppression document:', error);
      return { success: false, error: error.message };
    }
  },

  // ========== PAIEMENTS ==========

  /**
   * Enregistrer un paiement de frais d'examen
   */
  enregistrerPaiement: async (paiementData) => {
    try {
      const response = await fetch(`${API_URL}/examens/paiements`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(paiementData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur enregistrement paiement:', error);
      return { success: false, error: error.message };
    }
  },


  // ========== CANDIDATS LIBRES ==========

/**
 * Inscrire un candidat libre
 */
inscrireCandidatLibre: async (candidatData) => {
  try {
    const response = await fetch(`${API_URL}/examens/candidats-libres`, { // ✅ SANS /api
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(candidatData)
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur inscription candidat libre:', error);
    return { success: false, error: error.message };
  }
},

/**
 * Récupérer TOUS les candidats libres (tous examens)
 */
getAllCandidatsLibres: async () => {
  try {
    const response = await fetch(`${API_URL}/examens/candidats-libres`, { // ✅ SANS /api
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur récupération tous candidats libres:', error);
    return { success: false, error: error.message };
  }
},

/**
 * Récupérer les candidats libres par examen
 */
getCandidatsLibres: async (examenType) => {
  try {
    const response = await fetch(`${API_URL}/examens/candidats-libres/${examenType}`, { // ✅ SANS /api
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur récupération candidats libres:', error);
    return { success: false, error: error.message };
  }
},

/**
 * Récupérer les détails complets d'un candidat libre
 */
getDetailsCandidatLibre: async (id) => {
  try {
    const response = await fetch(`${API_URL}/examens/candidats-libres/details/${id}`, { // ✅ SANS /api
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur récupération détails candidat libre:', error);
    return { success: false, error: error.message };
  }
},

/**
 * Modifier un candidat libre
 */
modifierCandidatLibre: async (id, candidatData) => {
  try {
    const response = await fetch(`${API_URL}/examens/candidats-libres/${id}`, { // ✅ SANS /api
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(candidatData)
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur modification candidat libre:', error);
    return { success: false, error: error.message };
  }
},

/**
 * Supprimer un candidat libre
 */
supprimerCandidatLibre: async (id) => {
  try {
    const response = await fetch(`${API_URL}/examens/candidats-libres/${id}`, { // ✅ SANS /api
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur suppression candidat libre:', error);
    return { success: false, error: error.message };
  }
},

/**
 * Upload document pour candidat libre
 */
uploadDocumentCandidatLibre: async (candidatLibreId, documentRequisId, file) => {
  try {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('document_requis_id', documentRequisId);

    const response = await fetch(`${API_URL}/examens/candidats-libres/${candidatLibreId}/documents`, { // ✅ SANS /api
      method: 'POST',
      headers: getFormDataHeaders(),
      body: formData
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Erreur upload document candidat libre:', error);
    return { success: false, error: error.message };
  }
},






  // ========== STATISTIQUES ==========

  /**
   * Récupérer les statistiques du dashboard
   */
  getStatisticsDashboard: async () => {
    try {
      const response = await fetch(`${API_URL}/examens/statistiques/dashboard`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération statistiques:', error);
      return { success: false, error: error.message };
    }
  },

  // ========== RÉSULTATS ==========

  /**
   * Résultats des élèves d'un examen : { disponible, peut_saisir, resultats, synthese }.
   * L'année est celle de l'en-tête X-Annee-Scolaire-Id, comme la liste.
   */
  getResultats: async (examenType) => {
    try {
      const response = await fetch(`${API_URL}/examens/resultats/${examenType}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération résultats:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Enregistrer le résultat d'un élève. Seuls les champs présents changent :
   * { examen_type, eleve_id, resultat?, mention?, matricule_national?, numero_table? },
   * null efface. Renvoie { resultat, synthese }.
   */
  enregistrerResultat: async (payload) => {
    try {
      const response = await fetch(`${API_URL}/examens/resultats`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur enregistrement résultat:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Import de la liste officielle, premier temps : rapprocher les lignes lues
   * dans le navigateur de nos candidats. N'écrit rien.
   * { examen_type, type_liste: 'admis' | 'complete', lignes: [...] }
   */
  analyserImportResultats: async (payload) => {
    try {
      const response = await fetch(`${API_URL}/examens/resultats/import/analyser`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur rapprochement import résultats:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Import de la liste officielle, second temps : écrire les résultats retenus.
   * { examen_type, type_liste, nom_fichier, format, lignes_fichier, liste_complete, resultats: [...] }
   */
  confirmerImportResultats: async (payload) => {
    try {
      const response = await fetch(`${API_URL}/examens/resultats/import/confirmer`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur enregistrement import résultats:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Résultats et devenir d'une année d'examen (la plus récente qui a des
   * résultats si `anneeExamenId` est vide) : { disponible, annees, devenir }.
   */
  getDevenirResultats: async (anneeExamenId = null) => {
    try {
      const params = anneeExamenId ? `?annee_examen_id=${encodeURIComponent(anneeExamenId)}` : '';
      const response = await fetch(`${API_URL}/examens/resultats/devenir${params}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur devenir des candidats:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Réglages des examens pour l'année affichée : dernier examen de l'école,
   * et classes de chaque examen ({ disponible, annee, examen_final, examens }).
   */
  getReglagesExamens: async () => {
    try {
      const response = await fetch(`${API_URL}/examens/reglages`, { headers: getAuthHeaders() });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur réglages des examens:', error);
      return { success: false, error: error.message };
    }
  },

  /** Les classes d'un examen pour l'année affichée ; `classeIds` null revient à la règle du nom. */
  enregistrerClassesExamen: async (examenType, classeIds) => {
    try {
      const response = await fetch(`${API_URL}/examens/reglages/classes/${examenType}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ classe_ids: classeIds })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur classes de l\'examen:', error);
      return { success: false, error: error.message };
    }
  },

  /** Les classes réglées l'année précédente, retrouvées par leur nom dans l'année affichée. */
  getClassesAnneePrecedente: async (examenType) => {
    try {
      const response = await fetch(`${API_URL}/examens/reglages/classes/${examenType}/annee-precedente`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur classes de l\'année précédente:', error);
      return { success: false, error: error.message };
    }
  },

  /** Le dernier examen de l'école (CEPE, BEPC ou BAC). */
  enregistrerExamenFinal: async (examenType) => {
    try {
      const response = await fetch(`${API_URL}/examens/reglages/examen-final`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ examen_type: examenType })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur dernier examen:', error);
      return { success: false, error: error.message };
    }
  },

  // ========== UTILITAIRES ==========

  /**
   * Calculer le statut global d'un dossier
   */
  calculateStatutDossier: (documentsRequisCount, documentsFournisCount, statutPaiement) => {
    const documentComplet = documentsFournisCount >= documentsRequisCount;
    const paiementComplet = statutPaiement === 'paye';

    if (documentComplet && paiementComplet) {
      return 'complete';
    } else if (documentComplet || paiementComplet) {
      return 'pending';
    } else {
      return 'incomplete';
    }
  },

  /**
   * Formater le statut pour l'affichage
   */
  formatStatut: (statut) => {
    const statusMap = {
      'incomplet': { label: 'Incomplet', color: 'red' },
      'en_attente': { label: 'En attente', color: 'orange' },
      'complet': { label: 'Complet', color: 'green' },
      'valide': { label: 'Validé', color: 'blue' },
      'rejete': { label: 'Rejeté', color: 'red' }
    };
    return statusMap[statut] || { label: 'Inconnu', color: 'gray' };
  },

  /**
   * Formater le statut de paiement
   */
  formatStatutPaiement: (statut) => {
    const statusMap = {
      'non_paye': { label: 'Non payé', color: 'red' },
      'partiel': { label: 'Partiel', color: 'orange' },
      'paye': { label: 'Payé', color: 'green' }
    };
    return statusMap[statut] || { label: 'Inconnu', color: 'gray' };
  }
}


};


api.suiviPedagogique = {
  getMe: async () => {
    try {
      const response = await fetch(`${API_URL}/suivi-pedagogique/me`, {
        headers: getAuthHeaders(),
        credentials: 'include',
        cache: 'no-store'
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur chargement contexte suivi pédagogique:', error);
      return { success: false, message: error.message, data: null };
    }
  },

  getEnseignants: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') params.append(key, value);
      });
      const query = params.toString();
      const response = await fetch(`${API_URL}/suivi-pedagogique/enseignant/options${query ? `?${query}` : ''}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
        cache: 'no-store'
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur chargement enseignants du suivi:', error);
      return { success: false, message: error.message, data: [] };
    }
  },

  getAffectations: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      const normalizedFilters = typeof filters === 'object' ? filters : { annee_scolaire_id: filters };
      Object.entries(normalizedFilters || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') params.append(key, value);
      });
      const query = params.toString();
      const response = await fetch(`${API_URL}/suivi-pedagogique/enseignant/affectations${query ? `?${query}` : ''}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
        cache: 'no-store'
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur chargement affectations enseignant:', error);
      return { success: false, message: error.message, data: [] };
    }
  },

  getMesProgrammes: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      const normalizedFilters = typeof filters === 'object' ? filters : { annee_scolaire_id: filters };
      Object.entries(normalizedFilters || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') params.append(key, value);
      });
      const query = params.toString();
      const response = await fetch(`${API_URL}/suivi-pedagogique/enseignant/programmes${query ? `?${query}` : ''}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
        cache: 'no-store'
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur chargement programmes enseignant:', error);
      return { success: false, message: error.message, data: [] };
    }
  },

  getPlanning: async ({ date, annee_scolaire_id, enseignant_id } = {}) => {
    try {
      const params = new URLSearchParams();
      if (date) params.append('date', date);
      if (annee_scolaire_id) params.append('annee_scolaire_id', annee_scolaire_id);
      if (enseignant_id) params.append('enseignant_id', enseignant_id);
      const response = await fetch(`${API_URL}/suivi-pedagogique/enseignant/planning?${params.toString()}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
        cache: 'no-store'
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur chargement planning enseignant:', error);
      return { success: false, message: error.message, data: [] };
    }
  },

  prepareCours: async ({ emploi_temps_id, date_cours }) => {
    try {
      const params = new URLSearchParams();
      params.append('emploi_temps_id', emploi_temps_id);
      if (date_cours) params.append('date_cours', date_cours);
      const response = await fetch(`${API_URL}/suivi-pedagogique/cours/preparer?${params.toString()}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
        cache: 'no-store'
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur préparation cours:', error);
      return { success: false, message: error.message, data: null };
    }
  },

  saveProgramme: async (payload) => {
    try {
      const response = await fetch(`${API_URL}/suivi-pedagogique/programmes`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur sauvegarde programme:', error);
      return { success: false, message: error.message };
    }
  },

  submitProgramme: async (programmeId) => {
    try {
      const response = await fetch(`${API_URL}/suivi-pedagogique/programmes/${programmeId}/soumettre`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur soumission programme:', error);
      return { success: false, message: error.message };
    }
  },

  saveCours: async (payload) => {
    try {
      const response = await fetch(`${API_URL}/suivi-pedagogique/cours`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur sauvegarde cours:', error);
      return { success: false, message: error.message };
    }
  },

  saveAppel: async (payload) => {
    try {
      const response = await fetch(`${API_URL}/suivi-pedagogique/appels`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur sauvegarde appel:', error);
      return { success: false, message: error.message };
    }
  },

  getDashboardDirection: async ({ date, annee_scolaire_id } = {}) => {
    try {
      const params = new URLSearchParams();
      if (date) params.append('date', date);
      if (annee_scolaire_id) params.append('annee_scolaire_id', annee_scolaire_id);
      const response = await fetch(`${API_URL}/suivi-pedagogique/direction/dashboard?${params.toString()}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
        cache: 'no-store'
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur dashboard suivi enseignants:', error);
      return { success: false, message: error.message, data: null };
    }
  },

  getProgrammesDirection: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') params.append(key, value);
      });
      const query = params.toString();
      const response = await fetch(`${API_URL}/suivi-pedagogique/direction/programmes${query ? `?${query}` : ''}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
        cache: 'no-store'
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur programmes direction:', error);
      return { success: false, message: error.message, data: [] };
    }
  },

  validerProgramme: async (programmeId, commentaire = '') => {
    try {
      const response = await fetch(`${API_URL}/suivi-pedagogique/direction/programmes/${programmeId}/valider`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ commentaire })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur validation programme:', error);
      return { success: false, message: error.message };
    }
  },

  rejeterProgramme: async (programmeId, commentaire = '') => {
    try {
      const response = await fetch(`${API_URL}/suivi-pedagogique/direction/programmes/${programmeId}/rejeter`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ commentaire })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur rejet programme:', error);
      return { success: false, message: error.message };
    }
  },

  getAppelsDirection: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') params.append(key, value);
      });
      const query = params.toString();
      const response = await fetch(`${API_URL}/suivi-pedagogique/direction/appels${query ? `?${query}` : ''}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
        cache: 'no-store'
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur appels direction:', error);
      return { success: false, message: error.message, data: { items: [] } };
    }
  }
};

api.reinscription = {
  getContext: async () => {
    try {
      const response = await fetch(`${API_URL}/reinscription/context`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur contexte réinscription:', error);
      return { success: false, message: error.message, data: null };
    }
  },

  searchCandidates: async ({ annee_id, target_annee_id, q = '' }) => {
    try {
      const params = new URLSearchParams();
      if (annee_id) params.append('annee_id', annee_id);
      if (target_annee_id) params.append('target_annee_id', target_annee_id);
      if (q) params.append('q', q);

      const response = await fetch(`${API_URL}/reinscription/candidats?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur recherche candidats réinscription:', error);
      return { success: false, message: error.message, data: [] };
    }
  },

  getCandidateDetails: async (eleveId, annee_id, target_annee_id) => {
    try {
      const params = new URLSearchParams();
      if (annee_id) params.append('annee_id', annee_id);
      if (target_annee_id) params.append('target_annee_id', target_annee_id);

      const response = await fetch(`${API_URL}/reinscription/candidats/${eleveId}?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur détail candidat réinscription:', error);
      return { success: false, message: error.message, data: null };
    }
  },

  searchFamilies: async ({ annee_id, target_annee_id, q = '' }) => {
    try {
      const params = new URLSearchParams();
      if (annee_id) params.append('annee_id', annee_id);
      if (target_annee_id) params.append('target_annee_id', target_annee_id);
      if (q) params.append('q', q);

      const response = await fetch(`${API_URL}/reinscription/familles?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur recherche familles réinscription:', error);
      return { success: false, message: error.message, data: [] };
    }
  },

  getFamilyDetails: async (familleId, annee_id, target_annee_id) => {
    try {
      const params = new URLSearchParams();
      if (annee_id) params.append('annee_id', annee_id);
      if (target_annee_id) params.append('target_annee_id', target_annee_id);

      const response = await fetch(`${API_URL}/reinscription/familles/${familleId}?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur détail famille réinscription:', error);
      return { success: false, message: error.message, data: null };
    }
  },

  create: async (data) => {
    try {
      const response = await fetch(`${API_URL}/reinscription`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur création réinscription:', error);
      return { success: false, message: error.message };
    }
  },

  getHistorique: async (annee_scolaire_id) => {
    try {
      const params = new URLSearchParams();
      if (annee_scolaire_id) params.append('annee_scolaire_id', annee_scolaire_id);

      const response = await fetch(`${API_URL}/reinscription/historique?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Erreur historique réinscription:', error);
      return { success: false, message: error.message, data: [] };
    }
  },

  getEligibleStudents: async () => ({ success: true, data: [] }),
  reinscrire: async (data) => api.reinscription.create(data),
  getStats: async () => api.reinscription.getContext(),
  getStatus: async () => ({ success: true, data: null }),
  getPendingApprovals: async () => ({ success: true, data: [] }),
  approve: async () => ({ success: false, message: 'Le nouveau flux valide directement la réinscription.' }),
  getYearStats: async () => api.reinscription.getContext()
};


// ============================================================================
// 🆕 AJOUTER ICI - FONCTIONS UTILITAIRES POUR RÉDUCTIONS
// ============================================================================

/**
 * Calcule le montant après réduction et le pourcentage
 * @param {number} fraisOriginal - Montant original
 * @param {number} reduction - Montant de réduction à soustraire
 * @returns {Object} Détails du calcul
 */
export const calculerMontantsAvecReduction = (fraisOriginal, reduction) => {
  const montantReduit = Math.max(0, fraisOriginal - reduction);
  const pourcentageReduction = fraisOriginal > 0
    ? ((reduction / fraisOriginal) * 100).toFixed(2)
    : 0;

  return {
    montant_original: fraisOriginal,
    reduction: reduction,
    montant_final: montantReduit,
    pourcentage: parseFloat(pourcentageReduction)
  };
};

/**
 * Calcule l'économie totale sur l'année scolaire
 * @param {number} reductionInscription - Réduction sur inscription
 * @param {number} reductionMensuel - Réduction mensuelle
 * @param {number} nombreMois - Nombre de mois (défaut: 9)
 * @returns {Object} Détails de l'économie annuelle
 */
export const calculerEconomieAnnuelle = (reductionInscription, reductionMensuel, nombreMois = 9) => {
  const economieInscription = reductionInscription || 0;
  const economieMensuelle = (reductionMensuel || 0) * nombreMois;
  const economieTotal = economieInscription + economieMensuelle;

  return {
    economie_inscription: economieInscription,
    economie_mensuelle: economieMensuelle,
    economie_totale: economieTotal,
    nombre_mois: nombreMois
  };
};

/**
 * Formate un montant en FCFA avec séparateurs de milliers
 * @param {number} montant - Montant à formater
 * @returns {string} Montant formaté
 */
export const formaterMontant = (montant) => {
  return new Intl.NumberFormat('fr-FR').format(montant);
};
